import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowDown, ArrowUp, Camera, ChevronDown, ChevronRight, Copy, Download, Eye, EyeOff, FlipHorizontal, GripVertical, Image, Languages, ListOrdered, Loader2, MessageSquare, Plus, Save, Search, Smartphone, Sparkles, Tablet, Tag, Type, Trash2, Undo2, Upload, Video, X } from 'lucide-react';
import { getAppScreens, saveAppScreens, listAppScreensSets, createAppScreensSet, deleteAppScreensSet, createScreensUploadJob, createScreensTranslateJob, redesignAppScreenAI, suggestAppScreensAITemplate, triggerJob, uploadStorageFile, listJobs, getJob, type AppScreenIssue, type AppScreensSetSummary, type Job } from '../api/client';
import {
  APP_STORE_LOCALIZATIONS,
  DEFAULT_APP_STORE_LOCALE,
  getLocaleFlag,
} from '../constants/appStoreLocalizations';
import ColorSwatchPicker from './ColorSwatchPicker';
import RichTextEditor from './RichTextEditor';
import AppPageLayout from './AppPageLayout';
import ConfirmDialog from './ConfirmDialog';
import ModalLayout from './ModalLayout';
import VideoClipTrimmer from './VideoClipTrimmer';
import { useApplication } from '../context/ApplicationContext';
import { useError } from '../context/ErrorContext';
import { useIsPro } from '../hooks/useIsPro';
import { panelPath } from '../config/paths';
import { resolveStorageUrl } from '../utils/storageUrl';
import {
  collectImageRefsFromScreens,
  getCachedScreenImage,
  loadScreenImage,
  preloadScreenImages,
} from '../utils/screenImageCache';
import ProTag from './ProTag';
import PageToolbar from './PageToolbar';
import AppScreensAIChatPanel from './AppScreensAIChatPanel';
import LocaleSelectMenu from './LocaleSelectMenu';
import PlatformSelectCards from './PlatformSelectCards';
import SourceTargetLocalePicker from './SourceTargetLocalePicker';
import { SkeletonBar } from './Skeleton';
import OnboardingTour, { type OnboardingStep } from './onboarding/OnboardingTour';
import EmptyState from './onboarding/EmptyState';

/**
 * App Store ekran boyutları — Apple'ın kabul ettiği piksel çözünürlükleri.
 * Kaynak: https://www.mobileaction.co/guide/app-screenshot-sizes-and-guidelines-for-the-app-store/
 * UI önizleme ve export aynı boyut (WYSIWYG).
 */
const SCREEN_SIZE_PRESETS = [
  // iPhone (portrait) — 19.5:9 (6.7", 6.5", 6.1") ve 16:9 (5.5")
  { id: 'iphone-6.7', group: 'iphone', diagonal: '6.7"', label: 'iPhone 6.7" (1290×2796)', width: 1290, height: 2796, exportWidth: 1290, exportHeight: 2796 },
  { id: 'iphone-6.5', group: 'iphone', diagonal: '6.5"', label: 'iPhone 6.5" (1242×2688)', width: 1242, height: 2688, exportWidth: 1242, exportHeight: 2688 },
  { id: 'iphone-6.1', group: 'iphone', diagonal: '6.1"', label: 'iPhone 6.1" (1179×2556)', width: 1179, height: 2556, exportWidth: 1179, exportHeight: 2556 },
  { id: 'iphone-5.5', group: 'iphone', diagonal: '5.5"', label: 'iPhone 5.5" (1242×2208)', width: 1242, height: 2208, exportWidth: 1242, exportHeight: 2208 },
  // iPad (portrait) — 4:3
  { id: 'ipad-12.9', group: 'ipad', diagonal: '12.9"', label: 'iPad 12.9" (2048×2732)', width: 2048, height: 2732, exportWidth: 2048, exportHeight: 2732 },
  { id: 'ipad-11', group: 'ipad', diagonal: '11"', label: 'iPad 11" (1668×2388)', width: 1668, height: 2388, exportWidth: 1668, exportHeight: 2388 },
  { id: 'ipad-10.5', group: 'ipad', diagonal: '10.5"', label: 'iPad 10.5" (1668×2224)', width: 1668, height: 2224, exportWidth: 1668, exportHeight: 2224 },
  { id: 'ipad-9.7', group: 'ipad', diagonal: '9.7"', label: 'iPad 9.7" (1536×2048)', width: 1536, height: 2048, exportWidth: 1536, exportHeight: 2048 },
] as const;

type ScreenSizeId = (typeof SCREEN_SIZE_PRESETS)[number]['id'];

/** BuildMate proje ikonu — watermark'ta logo olarak kullanılır. */
const BUILD_MATE_LOGO_URL = `${process.env.PUBLIC_URL || ''}/logo192.png`;

type ScreenBackground =
  | { type: 'color'; color: string }
  | {
      type: 'image';
      imageUrl: string;
      imageFit: 'cover' | 'contain' | 'fill';
      imagePosition: string;
    };

const FONT_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Rubik', label: 'Rubik' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
];

/** Ekran yüksekliğine göre metin boyutu: UI'da % olarak gösterilir (1.2 = %1.2). DB'de oran olarak saklanır (0.012); backend /100 kullanmaz. */
const REF_HEIGHT_FOR_LEGACY_PX = 932;

/** Backend (render.go) ile aynı: font boyutu çarpanı; UI ve export görsel uyumu. */
const FONT_SCALE = 1.18;

/** Content-fit (non-full-width) sentinel; 0 backend'de full width sayılır. */
const TEXT_WIDTH_CONTENT_FIT = 1;

function isTextFullWidth(item: Extract<LayoutItem, { type: 'text' }>): boolean {
  return (item.widthPercent ?? 100) >= 100;
}

/** Metin boyutu punto (pt). Tek alan: textSize. Eski veride textSizePt veya textSizePercent varsa ondan türetilir. */
function getTextSize(item: Extract<LayoutItem, { type: 'text' }>): number {
  const n = (item as { textSize?: number }).textSize;
  if (typeof n === 'number' && n > 0) return n;
  const pt = (item as { textSizePt?: number }).textSizePt;
  if (typeof pt === 'number' && pt > 0) return pt;
  const pct = (item as { textSizePercent?: number }).textSizePercent;
  if (typeof pct === 'number' && pct > 0) {
    const ratio = pct < 1 ? pct : pct / 100;
    const estimatedPx = REF_HEIGHT_FOR_LEGACY_PX * ratio * FONT_SCALE;
    return Math.max(6, Math.min(200, Math.round(estimatedPx)));
  }
  return 14;
}

/** Ondalık float gürültüsünü keser (örn. 0.013999... → 0.014). */
function roundRatio(ratio: number, decimals = 6): number {
  const f = 10 ** decimals;
  return Math.round(ratio * f) / f;
}

/** Verilen yükseklik (px) için çizim/export font boyutu (px). textSize pt olarak pt*height/ref. */
function getTextSizePxForHeight(item: Extract<LayoutItem, { type: 'text' }>, heightPx: number): number {
  const pt = getTextSize(item);
  const px = Math.round((pt * heightPx) / REF_HEIGHT_FOR_LEGACY_PX);
  return Math.max(8, Math.min(200, px));
}

export type TextPaddingPercents = { top: number; right: number; bottom: number; left: number };

/** Arka plan padding: UI'da % (0–100) gösterilir. DB'de oran (0–1) saklanır. */
function toDisplayPercent(raw: number | undefined): number {
  if (raw == null || raw === 0) return 0;
  if (raw > 0 && raw <= 1) return raw * 100;
  return raw;
}
function getTextPaddingPercents(item: Extract<LayoutItem, { type: 'text' }>): TextPaddingPercents {
  const raw = item as {
    textPaddingPercent?: number;
    textPadding?: number;
    textPaddingTopPercent?: number;
    textPaddingRightPercent?: number;
    textPaddingBottomPercent?: number;
    textPaddingLeftPercent?: number;
  };
  const legacy =
    typeof raw.textPaddingPercent === 'number' && raw.textPaddingPercent >= 0
      ? raw.textPaddingPercent < 1 ? raw.textPaddingPercent * 100 : raw.textPaddingPercent
      : typeof raw.textPadding === 'number' && raw.textPadding > 0
        ? (raw.textPadding / REF_HEIGHT_FOR_LEGACY_PX) * 100
        : 0;
  const t = toDisplayPercent(raw.textPaddingTopPercent);
  const r = toDisplayPercent(raw.textPaddingRightPercent);
  const b = toDisplayPercent(raw.textPaddingBottomPercent);
  const l = toDisplayPercent(raw.textPaddingLeftPercent);
  const hasExplicitPadding =
    raw.textPaddingTopPercent !== undefined ||
    raw.textPaddingRightPercent !== undefined ||
    raw.textPaddingBottomPercent !== undefined ||
    raw.textPaddingLeftPercent !== undefined;
  const useLegacy = !hasExplicitPadding;
  const clamp = (v: number) => Math.max(0, Math.min(100, v));
  if (useLegacy) {
    return { top: clamp(legacy), right: clamp(legacy), bottom: clamp(legacy), left: clamp(legacy) };
  }
  return { top: clamp(t), right: clamp(r), bottom: clamp(b), left: clamp(l) };
}

/** Arka plan radius: DB'de oran (0–0.5), UI'da % (0–50). */
function getBackgroundRadiusPercent(item: Extract<LayoutItem, { type: 'text' }>): number {
  const raw = item.backgroundRadiusPercent;
  if (raw == null || raw === 0) return 0;
  const pct = raw > 0 && raw <= 1 ? raw * 100 : raw;
  return Math.max(0, Math.min(50, pct));
}

/** Content-fit min genişlik: DB'de oran (0–1), UI'da ekran genişliği % (0–100). */
function getMinWidthPercent(item: Extract<LayoutItem, { type: 'text' }>): number {
  const raw = item.minWidthPercent;
  if (raw == null || raw === 0) return 0;
  const pct = raw > 0 && raw <= 1 ? raw * 100 : raw;
  return Math.max(0, Math.min(100, pct));
}

type LayoutItem =
  | {
      id: string;
      type: 'text';
      /** Render sırası; büyük değer görsel olarak en üstte. */
      index?: number;
      /** false = gizle (önizleme/export'ta çizilmez). Yok/true = görünür. */
      visible?: boolean;
      /** Tüm metin verisi locale → metin. Primary artık ayrı field değil, translations[primaryLocale]. */
      translations?: Record<string, string>;
      textColor: string; // #RRGGBB veya #RRGGBBAA (opacity)
      /** Metin boyutu punto (pt). Render'da pt*height/ref ile px. */
      textSize?: number;
      /** @deprecated Eski veri; sadece okuma uyumluluğu. Yeni kayıtlar textSize kullanır. */
      textSizePercent?: number;
      /** @deprecated Eski veri; sadece okuma uyumluluğu. Yeni kayıtlar textSize kullanır. */
      textSizePt?: number;
      /** @deprecated Oran kullan: textPaddingTopPercent vb. */
      textPadding?: number;
      /** @deprecated Tek oran; yerine 4 ayrı alan kullanın */
      textPaddingPercent?: number;
      /** Arka plan padding, ekran yüksekliğine oran % (0–100). */
      textPaddingTopPercent?: number;
      textPaddingRightPercent?: number;
      textPaddingBottomPercent?: number;
      textPaddingLeftPercent?: number;
      backgroundColor: string; // transparent | #RRGGBB | #RRGGBBAA
      /**
       * Arka plan köşe yuvarlaklığı: min(kutuW, kutuH) oranına göre (0–1).
       * UI'da % (0–50) gösterilir; 50 ≈ tam kapsül.
       */
      backgroundRadiusPercent?: number;
      /**
       * Content-fit min kutu genişliği: ekran genişliğine oran (0–1).
       * UI'da % (0–100). Tam genişlikte yok sayılır.
       */
      minWidthPercent?: number;
      /**
       * Metin kutusunun izin verilen azami yüksekliği: ekran yüksekliğine % (0–100).
       * Yok/0 = tanımsız; bu durumda bütçe komşu öğelerin tasarım kutularından türetilir.
       */
      maxHeightPercent?: number;
      font: string;
      positionTopPercent?: number;
      positionLeftPercent?: number;
      /**
       * Metin kutusu genişliği %.
       * >=100 (veya yok) = tam genişlik ortalı; (0,100) = içeriğe göre kutu (serbest konum).
       * Not: 0 backend'de unset kabul edilir (full width); content-fit için 1 kullanın.
       */
      widthPercent?: number;
    }
  | {
      id: string;
      type: 'image';
      /** Render sırası; büyük değer görsel olarak en üstte. */
      index?: number;
      /** false = gizle (önizleme/export'ta çizilmez). Yok/true = görünür. */
      visible?: boolean;
      imageUrl?: string;
      imageFit?: 'cover' | 'contain' | 'fill';
      /** Gösterim şekli: oval, kare, yıldız vb. */
      imageShape?: 'circle' | 'oval' | 'square' | 'rounded' | 'star' | 'diamond';
      /** Çerçeve: 0 = yok, >0 = kalınlık (px) */
      frameThickness?: number;
      frameColor?: string;
      /** Yatay ayna (sol↔sağ). */
      flipHorizontal?: boolean;
      positionTopPercent?: number;
      positionLeftPercent?: number;
      /** Ekrana oranlı boyut (0–100) */
      widthPercent?: number;
      heightPercent?: number;
    }
  | {
      id: string;
      type: 'device';
      /** Render sırası; büyük değer görsel olarak en üstte. */
      index?: number;
      /** false = gizle (önizleme/export'ta çizilmez). Yok/true = görünür. */
      visible?: boolean;
      /** Çerçeve rengi (bezel) */
      frameColor?: string;
      /** Cihazın içinde görünen içerik (renk veya görsel) */
      background?: ScreenBackground;
      positionTopPercent?: number;
      positionLeftPercent?: number;
      /** Döndürme açısı (derece). 0 = düz; sürgü sola = negatif, sağa = pozitif. */
      rotationDegrees?: number;
    };

/** Yok veya true = görünür; false = gizli. */
function isLayoutItemVisible(item: LayoutItem): boolean {
  return item.visible !== false;
}

function layoutItemIndex(item: LayoutItem): number {
  return typeof item.index === 'number' && Number.isFinite(item.index) ? item.index : 0;
}

/** Index yoksa/çakışıyorsa dizi sırasına göre atar; unique ise index'e göre sıralar. */
function ensureLayoutItemIndices(items: LayoutItem[]): LayoutItem[] {
  if (items.length === 0) return items;
  const indices = items.map(layoutItemIndex);
  const unique = new Set(indices).size === indices.length;
  if (!unique) {
    return items.map((item, i) => ({ ...item, index: i }));
  }
  return [...items].sort((a, b) => layoutItemIndex(a) - layoutItemIndex(b));
}

function reassignLayoutItemIndices(items: LayoutItem[]): LayoutItem[] {
  return items.map((item, i) => ({ ...item, index: i }));
}

function maxLayoutItemIndex(items: LayoutItem[]): number {
  return items.reduce((max, it) => Math.max(max, layoutItemIndex(it)), -1);
}

function normalizeScreenLayoutIndices(screen: Screen): Screen {
  if (!screen.layoutItems?.length) return screen;
  return { ...screen, layoutItems: ensureLayoutItemIndices(screen.layoutItems) };
}

function normalizeScreensLayoutIndices(screens: Screen[]): Screen[] {
  return screens.map(normalizeScreenLayoutIndices);
}

type VideoClip = {
  url: string;
  /** Inclusive trim start (seconds). Default 0. */
  startSeconds?: number;
  /** Exclusive-ish trim end (seconds). Omit / ≤ start → full source until known. */
  endSeconds?: number;
};

type Screen = {
  id: string;
  /** image = screenshot tasarımı (varsayılan); video = App Preview upload */
  type?: 'image' | 'video';
  layoutItems?: LayoutItem[];
  background?: ScreenBackground;
  /** App Preview klipleri (url + trim); export'ta sırayla birleştirilir. Max 3. */
  videoClips?: VideoClip[];
  /** Legacy; yüklemede videoClips'e migrate edilir. */
  videoUrls?: string[];
  deviceType?: string;
  /** false = gizle (export/upload'ta render edilmez). Yok/true = görünür. */
  visible?: boolean;
};

/** Yok veya true = görünür; false = gizli (export/upload'a dahil edilmez). */
function isScreenVisible(screen: Screen): boolean {
  return screen.visible !== false;
}

const MAX_SCREEN_VIDEO_SOURCES = 3;

function isVideoScreen(screen: Screen): boolean {
  return screen.type === 'video';
}

/** Ordered video clips, capped at max. Migrates legacy videoUrls. */
function getScreenVideoClips(screen: Screen): VideoClip[] {
  const fromClips = (screen.videoClips ?? [])
    .map(c => ({
      url: (c.url ?? '').trim(),
      startSeconds: typeof c.startSeconds === 'number' && c.startSeconds > 0 ? c.startSeconds : 0,
      endSeconds: typeof c.endSeconds === 'number' && c.endSeconds > 0 ? c.endSeconds : undefined,
    }))
    .filter(c => c.url);
  if (fromClips.length > 0) return fromClips.slice(0, MAX_SCREEN_VIDEO_SOURCES);
  return (screen.videoUrls ?? [])
    .map(u => u.trim())
    .filter(Boolean)
    .slice(0, MAX_SCREEN_VIDEO_SOURCES)
    .map(url => ({ url }));
}

function clipTrimDuration(clip: VideoClip, sourceDuration?: number): number {
  const start = Math.max(0, clip.startSeconds ?? 0);
  const end =
    typeof clip.endSeconds === 'number' && clip.endSeconds > start
      ? clip.endSeconds
      : sourceDuration && sourceDuration > start
        ? sourceDuration
        : 0;
  if (end <= start) return 0;
  return end - start;
}

/**
 * Website preview player for multi-clip App Preview screens.
 * User play starts at clip 1 (trim start); advances automatically through the rest; stops after the last clip.
 */
function SequentialVideoPreview({
  clips,
  className,
  onClick,
  onClipIndexChange,
}: {
  clips: VideoClip[];
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLVideoElement>) => void;
  onClipIndexChange?: (index: number) => void;
}) {
  const sources = React.useMemo(
    () =>
      clips
        .map(c => ({
          url: resolveStorageUrl(c.url),
          start: Math.max(0, c.startSeconds ?? 0),
          end: typeof c.endSeconds === 'number' && c.endSeconds > (c.startSeconds ?? 0) ? c.endSeconds : undefined,
        }))
        .filter(c => c.url),
    [clips]
  );
  const sourcesKey = sources.map(s => `${s.url}|${s.start}|${s.end ?? ''}`).join('\0');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  /** Play next clip as soon as its src is applied (auto-advance). */
  const autoAdvanceRef = useRef(false);
  /** True after the user starts a run until the last clip ends (pause/resume keeps place). */
  const inSequenceRef = useRef(false);

  useEffect(() => {
    setIndex(0);
    indexRef.current = 0;
    autoAdvanceRef.current = false;
    inSequenceRef.current = false;
  }, [sourcesKey]);

  useEffect(() => {
    indexRef.current = index;
    onClipIndexChange?.(index);
  }, [index, onClipIndexChange]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !autoAdvanceRef.current) return;
    autoAdvanceRef.current = false;
    const start = sources[index]?.start ?? 0;
    const play = () => {
      try {
        el.currentTime = start;
      } catch {
        /* ignore */
      }
      el.play().catch(() => {});
    };
    if (el.readyState >= 2) play();
    else el.addEventListener('canplay', play, { once: true });
  }, [index, sourcesKey, sources]);

  if (sources.length === 0) return null;

  const current = sources[index];

  return (
    <>
      {sources[index + 1]?.url ? (
        <video
          key={`preload-${sources[index + 1].url}`}
          src={sources[index + 1].url}
          preload="auto"
          className="hidden"
          aria-hidden
          tabIndex={-1}
        />
      ) : null}
      <video
        ref={videoRef}
        key={sourcesKey}
        src={current.url}
        controls
        playsInline
        preload="auto"
        className={className}
        onClick={onClick}
        onLoadedMetadata={() => {
          const el = videoRef.current;
          if (!el) return;
          const start = current.start ?? 0;
          if (el.currentTime < start - 0.05 || el.currentTime > start + 0.25) {
            try {
              el.currentTime = start;
            } catch {
              /* ignore */
            }
          }
        }}
        onPlay={() => {
          // Auto-advance play() calls land here with inSequenceRef already true.
          if (inSequenceRef.current) return;
          inSequenceRef.current = true;
          if (indexRef.current !== 0) {
            videoRef.current?.pause();
            autoAdvanceRef.current = true;
            indexRef.current = 0;
            setIndex(0);
            return;
          }
          const el = videoRef.current;
          const start = sources[0]?.start ?? 0;
          if (el && Math.abs(el.currentTime - start) > 0.25) {
            try {
              el.currentTime = start;
            } catch {
              /* ignore */
            }
          }
        }}
        onTimeUpdate={() => {
          const el = videoRef.current;
          if (!el || !inSequenceRef.current) return;
          const end = sources[indexRef.current]?.end;
          if (end != null && el.currentTime >= end - 0.04) {
            el.pause();
            const i = indexRef.current;
            if (i + 1 < sources.length) {
              autoAdvanceRef.current = true;
              const next = i + 1;
              indexRef.current = next;
              setIndex(next);
              return;
            }
            inSequenceRef.current = false;
            autoAdvanceRef.current = false;
            indexRef.current = 0;
            setIndex(0);
            requestAnimationFrame(() => {
              const v = videoRef.current;
              if (!v) return;
              v.pause();
              try {
                v.currentTime = sources[0]?.start ?? 0;
              } catch {
                /* ignore */
              }
            });
          }
        }}
        onEnded={() => {
          const i = indexRef.current;
          if (i + 1 < sources.length) {
            autoAdvanceRef.current = true;
            const next = i + 1;
            indexRef.current = next;
            setIndex(next);
            return;
          }
          inSequenceRef.current = false;
          autoAdvanceRef.current = false;
          indexRef.current = 0;
          setIndex(0);
          requestAnimationFrame(() => {
            const el = videoRef.current;
            if (!el) return;
            el.pause();
            try {
              el.currentTime = sources[0]?.start ?? 0;
            } catch {
              /* ignore */
            }
          });
        }}
      />
    </>
  );
}

/** Sidebar preview that loops only the selected trim window. */
function TrimmedClipPreview({
  src,
  startSeconds,
  endSeconds,
}: {
  src: string;
  startSeconds: number;
  endSeconds?: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const start = Math.max(0, startSeconds);
  const endRef = useRef(endSeconds);
  endRef.current = endSeconds;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    try {
      el.currentTime = start;
    } catch {
      /* ignore */
    }
  }, [src, start]);

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      playsInline
      preload="metadata"
      className="w-full h-full object-contain"
      onLoadedMetadata={() => {
        const el = videoRef.current;
        if (!el) return;
        try {
          el.currentTime = start;
        } catch {
          /* ignore */
        }
      }}
      onPlay={() => {
        const el = videoRef.current;
        if (!el) return;
        if (Math.abs(el.currentTime - start) > 0.3) {
          try {
            el.currentTime = start;
          } catch {
            /* ignore */
          }
        }
      }}
      onTimeUpdate={() => {
        const el = videoRef.current;
        if (!el) return;
        const end = endRef.current;
        if (end != null && el.currentTime >= end - 0.05) {
          try {
            el.currentTime = start;
          } catch {
            /* ignore */
          }
          if (!el.paused) el.play().catch(() => {});
        }
      }}
    />
  );
}

/** Canvas-area preview for a video screen (empty state or sequential multi-clip player). */
function VideoScreenCanvasPreview({ clips }: { clips: VideoClip[] }) {
  const { t } = useTranslation();
  const [clipIndex, setClipIndex] = useState(0);

  if (clips.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-4 h-full w-full">
        <Video className="h-10 w-10 text-slate-500 shrink-0" />
        <p className="text-xs text-slate-400 text-center px-3 line-clamp-3">{t('appScreen.uploadVideo')}</p>
        <span className="absolute top-3 left-3 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white pointer-events-none">
          {t('appScreen.screenTypeVideo')}
        </span>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full min-h-0">
      <SequentialVideoPreview
        clips={clips}
        className="h-full w-full object-contain"
        onClipIndexChange={setClipIndex}
      />
      <span className="absolute top-3 left-3 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white pointer-events-none">
        {t('appScreen.screenTypeVideo')}
      </span>
      {clips.length > 1 ? (
        <span className="absolute top-3 right-3 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white pointer-events-none tabular-nums">
          {clipIndex + 1} / {clips.length}
        </span>
      ) : null}
    </div>
  );
}

/** Seçilen locale için metin item'ında gösterilecek metni döner (sadece translations). */
function getTextForLocale(item: Extract<LayoutItem, { type: 'text' }>, locale: string): string {
  if (!locale) return '';
  return item.translations?.[locale] ?? '';
}

const DEFAULT_TEXT_LAYOUT: Omit<Extract<LayoutItem, { type: 'text' }>, 'id'> = {
  type: 'text',
  translations: {},
  textColor: '#000000',
  textSize: 14,
  textPaddingTopPercent: 0,
  textPaddingRightPercent: 0,
  textPaddingBottomPercent: 0,
  textPaddingLeftPercent: 0,
  backgroundColor: 'transparent',
  backgroundRadiusPercent: 0,
  minWidthPercent: 0,
  font: 'system',
  positionTopPercent: 50,
  positionLeftPercent: 50,
  widthPercent: 100,
};

const DEFAULT_IMAGE_LAYOUT: Omit<Extract<LayoutItem, { type: 'image' }>, 'id'> = {
  type: 'image',
  imageUrl: '',
  imageFit: 'contain',
  imageShape: 'square',
  frameThickness: 0,
  frameColor: '#000000',
  positionTopPercent: 50,
  positionLeftPercent: 50,
  widthPercent: 40,
  heightPercent: 30,
};

/** iPhone referans oranı (genişlik:yükseklik); device her zaman bu oranda, genişlik %90. */
const DEVICE_ASPECT_WIDTH = 430;
const DEVICE_ASPECT_HEIGHT = 932;
/** iPhone gövde köşe yuvarlaklığı (min kenarın oranı); iç köşeler bununla eş merkezli. */
const DEVICE_FRAME_RADIUS_PCT = 0.16;
/** Apple "continuous corner" hissi için süperelips üssü (daire = 2). */
const DEVICE_CORNER_EXPONENT = 5;
/** Cihaz bezel kalınlığı (Go render_device.go ile aynı). */
const DEVICE_BEZEL_PCT = 0.0225;
const DEVICE_BEZEL_MIN = 5;
const DEVICE_BEZEL_MAX = 22;
/** Çerçeve rengi ile ekran görseli arasındaki ince siyah şerit (gerçek ekran kenarı). */
const DEVICE_SCREEN_EDGE_PCT = 0.0045;
const DEVICE_SCREEN_EDGE_MIN = 1;
const DEVICE_SCREEN_EDGE_COLOR = '#000000';

const DEFAULT_DEVICE_FRAME_COLOR = '#374151';
const DEFAULT_DEVICE_LAYOUT: Omit<Extract<LayoutItem, { type: 'device' }>, 'id'> = {
  type: 'device',
  frameColor: DEFAULT_DEVICE_FRAME_COLOR,
  positionTopPercent: 50,
  positionLeftPercent: 50,
  rotationDegrees: 0,
};

/** App Store App Preview cihaz tipleri (fastlane deliver dosya adı). */
const VIDEO_DEVICE_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'IPHONE_67', labelKey: 'appScreen.videoDeviceIphone67' },
  { value: 'IPHONE_65', labelKey: 'appScreen.videoDeviceIphone65' },
  { value: 'IPHONE_61', labelKey: 'appScreen.videoDeviceIphone61' },
  { value: 'IPHONE_55', labelKey: 'appScreen.videoDeviceIphone55' },
  { value: 'IPAD_PRO_3GEN_129', labelKey: 'appScreen.videoDeviceIpad129' },
  { value: 'IPAD_PRO_3GEN_11', labelKey: 'appScreen.videoDeviceIpad11' },
  { value: 'IPAD_105', labelKey: 'appScreen.videoDeviceIpad105' },
  { value: 'IPAD_97', labelKey: 'appScreen.videoDeviceIpad97' },
];

function previewDeviceTypeFromScreenSizeId(id: ScreenSizeId): string {
  switch (id) {
    case 'iphone-6.7':
      return 'IPHONE_67';
    case 'iphone-6.5':
      return 'IPHONE_65';
    case 'iphone-6.1':
      return 'IPHONE_61';
    case 'iphone-5.5':
      return 'IPHONE_55';
    case 'ipad-12.9':
      return 'IPAD_PRO_3GEN_129';
    case 'ipad-11':
      return 'IPAD_PRO_3GEN_11';
    case 'ipad-10.5':
      return 'IPAD_105';
    case 'ipad-9.7':
      return 'IPAD_97';
    default:
      return 'IPHONE_65';
  }
}

/** Hazır görseller: arama için label ve tags kullanılır */
const PRESET_IMAGES: { id: string; url: string; label: string; tags: string[] }[] = [
  { id: 'p1', url: 'https://picsum.photos/seed/nature1/200/200', label: 'Doğa 1', tags: ['doğa', 'nature', 'orman', 'forest'] },
  { id: 'p2', url: 'https://picsum.photos/seed/nature2/200/200', label: 'Doğa 2', tags: ['doğa', 'nature', 'dağ', 'mountain'] },
  { id: 'p3', url: 'https://picsum.photos/seed/tech1/200/200', label: 'Teknoloji', tags: ['teknoloji', 'tech', 'bilgisayar'] },
  { id: 'p4', url: 'https://picsum.photos/seed/tech2/200/200', label: 'Şehir', tags: ['şehir', 'city', 'bina', 'building'] },
  { id: 'p5', url: 'https://picsum.photos/seed/food1/200/200', label: 'Yemek', tags: ['yemek', 'food', 'restoran'] },
  { id: 'p6', url: 'https://picsum.photos/seed/people1/200/200', label: 'İnsan', tags: ['insan', 'people', 'portre'] },
  { id: 'p7', url: 'https://picsum.photos/seed/abstract1/200/200', label: 'Soyut', tags: ['soyut', 'abstract', 'renk'] },
  { id: 'p8', url: 'https://picsum.photos/seed/abstract2/200/200', label: 'Arka plan', tags: ['arka plan', 'background', 'pattern'] },
  { id: 'p9', url: 'https://picsum.photos/seed/business1/200/200', label: 'İş', tags: ['iş', 'business', 'ofis', 'office'] },
  { id: 'p10', url: 'https://picsum.photos/seed/sport1/200/200', label: 'Spor', tags: ['spor', 'sport', 'aktif'] },
];

const IMAGE_FIT_OPTIONS: { value: 'cover' | 'contain' | 'fill'; labelKey: string }[] = [
  { value: 'cover', labelKey: 'appScreen.fitCover' },
  { value: 'contain', labelKey: 'appScreen.fitContain' },
  { value: 'fill', labelKey: 'appScreen.fitFill' },
];

/** Görsel gösterim şekilleri */
const IMAGE_SHAPE_OPTIONS: { value: Extract<LayoutItem, { type: 'image' }>['imageShape']; labelKey: string }[] = [
  { value: 'square', labelKey: 'appScreen.shapeSquare' },
  { value: 'rounded', labelKey: 'appScreen.shapeRounded' },
  { value: 'circle', labelKey: 'appScreen.shapeCircle' },
  { value: 'oval', labelKey: 'appScreen.shapeOval' },
  { value: 'diamond', labelKey: 'appScreen.shapeDiamond' },
  { value: 'star', labelKey: 'appScreen.shapeStar' },
];

const IMAGE_POSITION_OPTIONS = [
  'center', 'top', 'bottom', 'left', 'right',
  'top left', 'top right', 'bottom left', 'bottom right',
];

const MAX_SCREEN_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_SCREEN_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SCREEN_VIDEO_BYTES = 50 * 1024 * 1024;
/** Apple App Preview max duration for the final (concatenated / trimmed) clip. */
const MAX_SCREEN_VIDEO_SECONDS = 30;
/** Max length of an uploaded source before trim. */
const MAX_SCREEN_VIDEO_SOURCE_SECONDS = 180;
const MIN_SCREEN_VIDEO_TRIM_SECONDS = 1;
const ALLOWED_SCREEN_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v'];

function validateScreenImageFile(file: File, t: (key: string) => string): void {
  if (file.size > MAX_SCREEN_IMAGE_BYTES) {
    throw new Error(t('appScreen.imageTooLarge'));
  }
  if (!ALLOWED_SCREEN_IMAGE_TYPES.includes(file.type)) {
    throw new Error(t('appScreen.imageInvalidFormat'));
  }
}

function validateScreenVideoFileSync(file: File, t: (key: string) => string): void {
  if (file.size > MAX_SCREEN_VIDEO_BYTES) {
    throw new Error(t('appScreen.videoTooLarge'));
  }
  const okType = ALLOWED_SCREEN_VIDEO_TYPES.includes(file.type) || /\.(mp4|mov|m4v)$/i.test(file.name);
  if (!okType) {
    throw new Error(t('appScreen.videoInvalidFormat'));
  }
}

/** Reads duration via browser metadata; rejects if missing. */
function readVideoDurationSecondsFromSrc(src: string, revokeObjectUrl?: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
      if (revokeObjectUrl) URL.revokeObjectURL(revokeObjectUrl);
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      cleanup();
      if (!Number.isFinite(d) || d <= 0) {
        reject(new Error('unreadable'));
        return;
      }
      resolve(d);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('unreadable'));
    };
    video.src = src;
  });
}

function readVideoDurationSeconds(file: File): Promise<number> {
  const url = URL.createObjectURL(file);
  return readVideoDurationSecondsFromSrc(url, url);
}

async function readStoredVideoDurationSeconds(storagePath: string): Promise<number> {
  const url = resolveStorageUrl(storagePath);
  if (!url) throw new Error('unreadable');
  return readVideoDurationSecondsFromSrc(url);
}

async function estimateClipTrimSeconds(clip: VideoClip): Promise<number> {
  const start = Math.max(0, clip.startSeconds ?? 0);
  if (typeof clip.endSeconds === 'number' && clip.endSeconds > start) {
    return clip.endSeconds - start;
  }
  try {
    const dur = await readStoredVideoDurationSeconds(clip.url);
    return Math.max(0, dur - start);
  } catch {
    return 0;
  }
}

/** UI-first validation: size/format/source duration before upload.
 *  existingClips: clips already on the screen; new file default trim must leave total ≤ 30s. */
async function validateScreenVideoFile(
  file: File,
  t: (key: string) => string,
  existingClips: VideoClip[] = []
): Promise<{ duration: number; defaultEnd: number }> {
  validateScreenVideoFileSync(file, t);
  let duration: number;
  try {
    duration = await readVideoDurationSeconds(file);
  } catch {
    throw new Error(t('appScreen.videoDurationUnreadable'));
  }
  if (duration > MAX_SCREEN_VIDEO_SOURCE_SECONDS) {
    throw new Error(t('appScreen.videoSourceTooLong'));
  }
  let existingTotal = 0;
  for (const clip of existingClips) {
    existingTotal += await estimateClipTrimSeconds(clip);
  }
  const remaining = MAX_SCREEN_VIDEO_SECONDS - existingTotal;
  if (remaining < MIN_SCREEN_VIDEO_TRIM_SECONDS - 0.05) {
    throw new Error(t('appScreen.videoTotalTooLong'));
  }
  const defaultEnd = Math.min(duration, remaining);
  if (defaultEnd < MIN_SCREEN_VIDEO_TRIM_SECONDS - 0.05) {
    throw new Error(t('appScreen.videoTotalTooLong'));
  }
  return { duration, defaultEnd };
}

function parsePositionPercentInput(raw: string): number | null {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

const DEFAULT_BG_COLOR = '#ffffff';

/** Canvas çizimi için öğe sınırları (tıklama hit-test) */
type ItemBound = { id: string; left: number; top: number; width: number; height: number };

/** Konum metnini backend ile uyumlu normalize eder (küçük harf, tek boşluk). */
function normalizeBackgroundPosition(position: string): string {
  const s = position.toLowerCase().trim();
  if (!s) return 'center';
  const map: Record<string, string> = {
    'top left': 'top left',
    'left top': 'top left',
    'top right': 'top right',
    'right top': 'top right',
    'bottom left': 'bottom left',
    'left bottom': 'bottom left',
    'bottom right': 'bottom right',
    'right bottom': 'bottom right',
  };
  return map[s] ?? s;
}

/** cover modunda scaled görsel içinde viewport penceresinin offset'ini döndürür. */
function coverOffset(
  scaledW: number,
  scaledH: number,
  viewW: number,
  viewH: number,
  position: string
): { offX: number; offY: number } {
  const px = (scaledW - viewW) / 2;
  const py = (scaledH - viewH) / 2;
  const pos = normalizeBackgroundPosition(position);
  switch (pos) {
    case 'top left':
      return { offX: 0, offY: 0 };
    case 'left':
      return { offX: 0, offY: py };
    case 'top':
      return { offX: px, offY: 0 };
    case 'top right':
      return { offX: scaledW - viewW, offY: 0 };
    case 'right':
      return { offX: scaledW - viewW, offY: py };
    case 'center':
      return { offX: px, offY: py };
    case 'bottom left':
      return { offX: 0, offY: scaledH - viewH };
    case 'bottom':
      return { offX: px, offY: scaledH - viewH };
    case 'bottom right':
      return { offX: scaledW - viewW, offY: scaledH - viewH };
    default:
      return { offX: px, offY: py };
  }
}

/** contain modunda letterbox konumunu (dx, dy) döndürür; backend rectPositionedIn ile uyumlu. */
function containPosition(
  boundsW: number,
  boundsH: number,
  contentW: number,
  contentH: number,
  position: string
): { dx: number; dy: number } {
  const pos = normalizeBackgroundPosition(position);
  let cw = contentW;
  let ch = contentH;
  if (cw > boundsW) cw = boundsW;
  if (ch > boundsH) ch = boundsH;
  let dx: number;
  let dy: number;
  switch (pos) {
    case 'top left':
    case 'left':
      dx = 0;
      dy = pos === 'left' ? (boundsH - ch) / 2 : 0;
      break;
    case 'top':
      dx = (boundsW - cw) / 2;
      dy = 0;
      break;
    case 'top right':
    case 'right':
      dx = boundsW - cw;
      dy = pos === 'right' ? (boundsH - ch) / 2 : 0;
      break;
    case 'center':
      dx = (boundsW - cw) / 2;
      dy = (boundsH - ch) / 2;
      break;
    case 'bottom left':
      dx = 0;
      dy = boundsH - ch;
      break;
    case 'bottom':
      dx = (boundsW - cw) / 2;
      dy = boundsH - ch;
      break;
    case 'bottom right':
      dx = boundsW - cw;
      dy = boundsH - ch;
      break;
    default:
      dx = (boundsW - cw) / 2;
      dy = (boundsH - ch) / 2;
  }
  dx = Math.max(0, Math.min(boundsW - cw, dx));
  dy = Math.max(0, Math.min(boundsH - ch, dy));
  return { dx, dy };
}

/**
 * Görseli verilen dikdörtgene fit (cover/contain/fill) ve position'a göre çizer.
 * Ekran arka planı ve cihaz içi arka plan için ortak kullanım.
 */
function drawImageFitInRect(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  fit: 'cover' | 'contain' | 'fill',
  position: string
): void {
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  if (sw <= 0 || sh <= 0) return;

  if (fit === 'fill') {
    ctx.drawImage(img, 0, 0, sw, sh, x, y, w, h);
    return;
  }

  if (fit === 'contain') {
    const scale = Math.min(w / sw, h / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    const { dx, dy } = containPosition(w, h, dw, dh, position);
    ctx.drawImage(img, 0, 0, sw, sh, x + dx, y + dy, dw, dh);
    return;
  }

  // cover: oran korunur, alan dolar, position'a göre kırpılır
  const scale = Math.max(w / sw, h / sh);
  const scaledW = sw * scale;
  const scaledH = sh * scale;
  const { offX, offY } = coverOffset(scaledW, scaledH, w, h, position);
  ctx.drawImage(
    img,
    offX / scale,
    offY / scale,
    w / scale,
    h / scale,
    x,
    y,
    w,
    h
  );
}

function drawScreenBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  background: Screen['background'],
  getImage: (url: string) => HTMLImageElement | null
) {
  if (!background || background.type === 'color') {
    ctx.fillStyle = background?.type === 'color' ? background.color : DEFAULT_BG_COLOR;
    ctx.fillRect(0, 0, w, h);
    return;
  }
  if (background.type === 'image' && background.imageUrl) {
    const img = getImage(background.imageUrl);
    if (!img || !img.naturalWidth) {
      ctx.fillStyle = DEFAULT_BG_COLOR;
      ctx.fillRect(0, 0, w, h);
      return;
    }
    const fit = background.imageFit ?? 'cover';
    const position = background.imagePosition ?? 'center';
    drawImageFitInRect(ctx, img, 0, 0, w, h, fit, position);
    return;
  }
  ctx.fillStyle = DEFAULT_BG_COLOR;
  ctx.fillRect(0, 0, w, h);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (!Number.isFinite(maxWidth) || maxWidth <= 0) return [text || ''];
  const raw = (text || '').replace(/\r\n/g, '\n');
  const paragraphs = raw.split('\n');
  const out: string[] = [];
  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) {
      out.push(' ');
      continue;
    }
    const words = trimmed.split(/\s+/).filter(Boolean);
    let line = '';
    for (const word of words) {
      const test = line ? line + ' ' + word : word;
      const m = ctx.measureText(test);
      if (m.width > maxWidth && line) {
        out.push(line);
        line = word;
        while (ctx.measureText(line).width > maxWidth && line.length > 1) {
          let lo = 0;
          let hi = line.length;
          while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (ctx.measureText(line.slice(0, mid)).width <= maxWidth) lo = mid;
            else hi = mid;
          }
          // maxWidth tek bir karakterden bile dar olabilir. Bu durumda lo=0 kalır;
          // en az bir karakter tüketilmezse döngü sonsuza kadar boş satır ekler.
          const splitAt = Math.max(1, lo);
          out.push(line.slice(0, splitAt));
          line = line.slice(splitAt);
        }
      } else {
        line = test;
      }
    }
    while (line && ctx.measureText(line).width > maxWidth && line.length > 1) {
      let lo = 0;
      let hi = line.length;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (ctx.measureText(line.slice(0, mid)).width <= maxWidth) lo = mid;
        else hi = mid;
      }
      const splitAt = Math.max(1, lo);
      out.push(line.slice(0, splitAt));
      line = line.slice(splitAt);
    }
    if (line) out.push(line);
  }
  return out.length ? out : [''];
}

/** Metin HTML etiketleri içeriyorsa true (canvas'ta string yerine overlay ile render edilecek). */
function isHtmlContent(text: string): boolean {
  if (!text || typeof text !== 'string') return false;
  return /<[a-zA-Z][^>]*>/.test(text);
}

/** HTML etiketlerini kaldırarak ölçüm için düz metin (box boyutu tahmini). */
function stripHtmlForMeasure(text: string): string {
  return (text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || ' ';
}

/** HTML içerikteki satır sayısını tahmin eder (</p>, <br> vb.) — kutu yüksekliği için. */
function estimateHtmlLineCount(html: string): number {
  if (!html || !isHtmlContent(html)) return 1;
  const closedP = (html.match(/<\/p>/gi) || []).length;
  const br = (html.match(/<br\s*\/?>/gi) || []).length;
  if (closedP > 0) return Math.max(1, closedP + br);
  return Math.max(1, 1 + br);
}

/** Offscreen DOM ile HTML metnin doğal (bold dahil) boyutunu ölçer. */
let htmlMeasureHost: HTMLDivElement | null = null;
function measureHtmlContentSize(
  html: string,
  fontSize: number,
  fontFamily: string,
  maxWidthPx: number | null
): { width: number; height: number } {
  if (typeof document === 'undefined') {
    return { width: Math.max(1, fontSize * 4), height: Math.max(1, Math.round(fontSize * 1.2)) };
  }
  if (!htmlMeasureHost) {
    htmlMeasureHost = document.createElement('div');
    htmlMeasureHost.setAttribute('aria-hidden', 'true');
    htmlMeasureHost.className = 'app-screen-html-overlay';
    htmlMeasureHost.style.cssText =
      'position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;box-sizing:border-box;margin:0;';
    document.body.appendChild(htmlMeasureHost);
  }
  const el = htmlMeasureHost;
  el.style.fontSize = `${fontSize}px`;
  el.style.fontFamily = fontFamily;
  el.style.lineHeight = '1.2';
  el.style.whiteSpace = 'pre-line';
  el.style.width = 'max-content';
  el.style.maxWidth = maxWidthPx != null && maxWidthPx > 0 ? `${maxWidthPx}px` : 'none';
  el.style.overflowWrap = maxWidthPx != null ? 'break-word' : 'normal';
  el.style.wordBreak = maxWidthPx != null ? 'break-word' : 'normal';
  el.innerHTML = html || ' ';
  const width = Math.ceil(el.offsetWidth);
  const height = Math.ceil(el.offsetHeight);
  el.innerHTML = '';
  return { width: Math.max(1, width), height: Math.max(1, height) };
}

/**
 * Çeviri uzadığında metin kutusu aşağı doğru büyür ve altındaki öğenin üstüne biner.
 * buildDesignRects + textItemVerticalLimit bunu engeller; mantık api tarafındaki
 * render_text_budget.go ile birebir aynıdır (önizleme = export).
 *
 * İskelet primary (kaynak) dilden çıkar: çeviri, tasarımın bıraktığı boşluğu sonuna kadar
 * kullanabilir ama komşusunun alanına giremez. Kasıtlı bindirmeyi (cihaz üstündeki rozet)
 * taşmadan ayırmak için öğenin "çapası" — üst kenar + bir satır — kullanılır.
 */

/** Çakışmayı çözerken font boyutu taban boyutun en fazla bu oranına kadar küçültülür. */
const TEXT_FIT_MIN_SCALE = 0.5;
/** Engel kutusuyla bırakılan asgari boşluk (ekran yüksekliğine oran). */
const TEXT_OBSTACLE_GAP_RATIO = 0.004;

type DesignRect = { left: number; top: number; right: number; bottom: number; fromPrimary: boolean };

/** "pt-BR" → "pt"; ayırıcı olarak - ve _ kabul edilir. */
function localeBaseLanguage(locale: string): string {
  const s = (locale ?? '').trim().toLowerCase();
  const i = s.search(/[-_]/);
  return i > 0 ? s.slice(0, i) : s;
}

/**
 * locale için metin; tam eşleşme yoksa taban dile ("en-US" → "en") ve aynı taban dili
 * paylaşan ilk locale'e düşer. Yalnızca REFERANS ölçümü için — çizilen metin tam eşleşmedir.
 */
function localeTextWithFallback(item: Extract<LayoutItem, { type: 'text' }>, locale: string): string {
  if (!locale) return '';
  const tr = item.translations ?? {};
  if (tr[locale]?.trim()) return tr[locale];
  const base = localeBaseLanguage(locale);
  if (!base) return '';
  if (tr[base]?.trim()) return tr[base];
  for (const [loc, text] of Object.entries(tr)) {
    if (localeBaseLanguage(loc) === base && text?.trim()) return text;
  }
  return '';
}

/** Öğenin tasarım kutusu: metin için primary dildeki taban boyutlu kutu, görsel/cihaz için geometrik kutu. */
function layoutItemDesignRect(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: LayoutItem,
  renderLocale: string,
  primaryLocale: string
): DesignRect | null {
  if (item.type === 'text') {
    // Primary yalnızca render dilinden FARKLI bir dilse bağımsız referanstır. Tek dilli
    // önizlemede "primary metin" öğenin kendi metnidir; taşmanın kendisi olabilir.
    let text = '';
    let fromPrimary = false;
    if (primaryLocale && localeBaseLanguage(primaryLocale) !== localeBaseLanguage(renderLocale)) {
      const t = localeTextWithFallback(item, primaryLocale);
      if (t.trim()) {
        text = t;
        fromPrimary = true;
      }
    }
    if (!text.trim()) text = getTextForLocale(item, renderLocale);
    if (!text.trim()) return null;
    const m = measureTextItem(ctx, w, h, item, text, getTextSizePxForHeight(item, h));
    return { left: m.boxLeft, top: m.boxTop, right: m.boxLeft + m.boxW, bottom: m.boxTop + m.boxH, fromPrimary };
  }
  if (item.type === 'image') {
    const iw = (w * (item.widthPercent ?? 40)) / 100;
    const ih = (h * (item.heightPercent ?? 30)) / 100;
    const cx = (w * (item.positionLeftPercent ?? 50)) / 100;
    const cy = (h * (item.positionTopPercent ?? 50)) / 100;
    return { left: cx - iw / 2, top: cy - ih / 2, right: cx + iw / 2, bottom: cy + ih / 2, fromPrimary: true };
  }
  // device — drawDeviceItem ile aynı geometri (%90 genişlik + aspect, rotasyon AABB'si)
  const devW = w * 0.9;
  const devH = (devW * DEVICE_ASPECT_HEIGHT) / DEVICE_ASPECT_WIDTH;
  const drawW = devH > h ? devW * (h / devH) : devW;
  const drawH = devH > h ? h : devH;
  const cx = (w * (item.positionLeftPercent ?? 50)) / 100;
  const cy = (h * (item.positionTopPercent ?? 50)) / 100;
  const aabb = rotatedRectBounds(cx, cy, drawW, drawH, ((item.rotationDegrees ?? 0) * Math.PI) / 180);
  return { left: aabb.left, top: aabb.top, right: aabb.left + aabb.width, bottom: aabb.top + aabb.height, fromPrimary: true };
}

/** Ekrandaki görünür öğelerin tasarım kutuları (öğe id → kutu). */
function buildDesignRects(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  items: LayoutItem[],
  renderLocale: string,
  primaryLocale: string
): Map<string, DesignRect> {
  const out = new Map<string, DesignRect>();
  for (const item of items) {
    if (!isLayoutItemVisible(item)) continue;
    const r = layoutItemDesignRect(ctx, w, h, item, renderLocale, primaryLocale);
    if (r) out.set(item.id, r);
  }
  return out;
}

/**
 * Metin kutusunun aşamayacağı alt sınır (px, ekranın üstünden).
 * En küçüğü kazanır: maxHeightPercent, çapanın altındaki komşuların üst kenarı, ekran alt kenarı.
 * Sonuç asla tabanın altına inmez — her öğenin en az bir satır ve primary tasarımı kadar hakkı vardır.
 */
function textItemVerticalLimit(
  h: number,
  item: Extract<LayoutItem, { type: 'text' }>,
  m: TextItemMetrics,
  designRects: Map<string, DesignRect> | null
): number {
  // Çapa: üst kenar + bir satır. Bu kadarı her koşulda öğenin hakkıdır ve kasıtlı
  // bindirmelerin başladığı bölgedir.
  const anchorBottom = m.boxTop + m.padT + m.lineHeight + m.padB;
  let floor = anchorBottom;

  let limit = h;
  const maxPct = item.maxHeightPercent ?? 0;
  const explicit = maxPct > 0;
  if (explicit) {
    limit = Math.min(limit, m.boxTop + (h * Math.min(maxPct, 100)) / 100);
  }
  if (!designRects || designRects.size === 0) return Math.max(limit, floor);

  const me = designRects.get(item.id);
  // Açık bütçe yoksa primary tasarım kutusu kadar yer öğenin kendisinindir; açık bütçe
  // varsa tasarımcının sözü geçerlidir ve taban yalnızca "bir satır"dır.
  if (!explicit && me?.fromPrimary && me.bottom > floor) floor = me.bottom;

  let xMin = m.boxLeft;
  let xMax = m.boxLeft + m.boxW;
  if (me) {
    xMin = Math.min(xMin, me.left);
    xMax = Math.max(xMax, me.right);
  }
  const gap = h * TEXT_OBSTACLE_GAP_RATIO;

  designRects.forEach((r, id) => {
    if (id === item.id) return;
    if (r.right <= xMin || r.left >= xMax) return; // yatayda kesişmiyor
    if (r.top < anchorBottom) return; // üstte ya da çapayla bindiriyor (kasıtlı)
    limit = Math.min(limit, r.top - gap);
  });
  return Math.max(limit, floor);
}

/** budgetH'e sığan en büyük font boyutu (taban boyutu aşmaz, TEXT_FIT_MIN_SCALE altına inmez). */
function fitTextItemToHeight(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: Extract<LayoutItem, { type: 'text' }>,
  displayText: string,
  baseFontSize: number,
  budgetH: number
): number {
  const budget = budgetH > 0 ? budgetH : 1;
  const floor = Math.max(8, Math.round(baseFontSize * TEXT_FIT_MIN_SCALE));
  if (floor >= baseFontSize) return baseFontSize;
  let lo = floor;
  let hi = baseFontSize;
  let best = floor;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (measureTextItem(ctx, w, h, item, displayText, mid).boxH <= budget) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/**
 * Bir metin öğesinin ölçüm sonucu. Ölçüm ve çizim aynı yapıyı paylaşır; böylece
 * "ölçtüğümüz yükseklik" ile "çizdiğimiz yükseklik" ayrışamaz (ayrışırsa kutu
 * komşusunun üstüne taşar). Go tarafındaki textItemLayout ile birebir aynı mantık.
 */
type TextItemMetrics = {
  fontSize: number;
  lineHeight: number;
  fontFamily: string;
  fullWidth: boolean;
  wrapWidth: number;
  padT: number;
  padR: number;
  padB: number;
  padL: number;
  plainLines: string[];
  textBlockWidth: number;
  textBlockHeight: number;
  boxLeft: number;
  boxTop: number;
  boxW: number;
  boxH: number;
};

/** measureTextItem item'ı verilen font boyutunda ölçer ve kutusunu hesaplar (çizim yapmaz). */
function measureTextItem(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: Extract<LayoutItem, { type: 'text' }>,
  displayText: string,
  fontSize: number
): TextItemMetrics {
  const top = item.positionTopPercent ?? 50;
  const left = item.positionLeftPercent ?? 50;
  const p = getTextPaddingPercents(item);
  const padT = (h * p.top) / 100;
  const padR = (w * p.right) / 100;
  const padB = (h * p.bottom) / 100;
  const padL = (w * p.left) / 100;
  const fontFamily = item.font === 'system' ? 'sans-serif' : item.font;
  const fullWidth = isTextFullWidth(item);
  const cx = (w * left) / 100;
  // Content-fit: ekran kenarına kadar uzasın (merkez kısıtı yok — bold vb. alta kaymasın).
  const screenWrap = Math.max(1, w - padL - padR - 2);
  const wrapWidth = screenWrap;
  const lineHeight = fontSize * 1.2;

  let textBlockWidth = 0;
  let textBlockHeight = 0;
  let plainLines: string[] = [];

  if (isHtmlContent(displayText)) {
    let measured = measureHtmlContentSize(displayText, fontSize, fontFamily, fullWidth ? wrapWidth : null);
    if (!fullWidth && measured.width > wrapWidth) {
      measured = measureHtmlContentSize(displayText, fontSize, fontFamily, wrapWidth);
    }
    textBlockWidth = measured.width;
    textBlockHeight = measured.height;
  } else {
    ctx.font = `${fontSize}px ${fontFamily}`;
    const raw = (displayText || ' ').replace(/\r\n/g, '\n');
    const naturalParas = raw.split('\n').map(s => s.trim() || ' ');
    let naturalW = 0;
    for (const para of naturalParas) {
      naturalW = Math.max(naturalW, ctx.measureText(para).width);
    }
    if (fullWidth || naturalW > wrapWidth) {
      plainLines = wrapText(ctx, displayText || ' ', wrapWidth);
      for (const line of plainLines) {
        textBlockWidth = Math.max(textBlockWidth, ctx.measureText(line).width);
      }
    } else {
      plainLines = naturalParas;
      textBlockWidth = naturalW;
    }
    textBlockHeight = plainLines.length * lineHeight;
  }

  const boxW = fullWidth
    ? w
    : Math.max(textBlockWidth + padL + padR, (w * getMinWidthPercent(item)) / 100);
  const boxH = textBlockHeight + padT + padB;
  const boxLeft = fullWidth ? 0 : cx - boxW / 2;
  /* Üstten sabit: boşluk/satır eklenince kutu sadece aşağı uzar, metin aşağı kaymaz */
  const boxTop = (h * top) / 100;

  return {
    fontSize,
    lineHeight,
    fontFamily,
    fullWidth,
    wrapWidth,
    padT,
    padR,
    padB,
    padL,
    plainLines,
    textBlockWidth,
    textBlockHeight,
    boxLeft,
    boxTop,
    boxW,
    boxH,
  };
}

function drawTextItem(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: Extract<LayoutItem, { type: 'text' }>,
  bounds: ItemBound[],
  textOverride?: string,
  limitY?: number
): void {
  const displayText = textOverride ?? '';
  const baseFontSize = getTextSizePxForHeight(item, h);
  let m = measureTextItem(ctx, w, h, item, displayText, baseFontSize);
  if (limitY != null && m.boxTop + m.boxH > limitY) {
    const fitted = fitTextItemToHeight(ctx, w, h, item, displayText, baseFontSize, limitY - m.boxTop);
    if (fitted < baseFontSize) m = measureTextItem(ctx, w, h, item, displayText, fitted);
  }
  const { fontSize, lineHeight, fontFamily, fullWidth, wrapWidth, padT, padR, padB, padL } = m;
  const { plainLines, textBlockWidth, boxLeft, boxTop, boxW, boxH } = m;
  const contentAreaW = boxW - padL - padR;
  const contentLeft = boxLeft + padL;
  const contentTop = boxTop + padT;
  const centerInBox = !fullWidth && boxW > textBlockWidth + padL + padR + 0.5;

  if (item.backgroundColor !== 'transparent' && item.backgroundColor) {
    const radiusPct = getBackgroundRadiusPercent(item) / 100;
    ctx.fillStyle = item.backgroundColor;
    if (radiusPct > 0) {
      fillRoundRect(ctx, boxLeft, boxTop, boxW, boxH, radiusPct);
    } else {
      ctx.fillRect(boxLeft, boxTop, boxW, boxH);
    }
  }

  const clampLeft = Math.max(0, boxLeft);
  const clampTop = Math.max(0, boxTop);
  const clampW = Math.min(w - clampLeft, boxW);
  const clampH = Math.min(h - clampTop, boxH);
  bounds.push({
    id: item.id,
    left: clampLeft,
    top: clampTop,
    width: Math.max(0, clampW),
    height: Math.max(0, clampH),
  });

  if (isHtmlContent(displayText)) {
    return;
  }

  ctx.fillStyle = item.textColor || '#000000';
  ctx.textBaseline = 'top';
  ctx.font = `${fontSize}px ${fontFamily}`;
  const drawLines = plainLines.length > 0 ? plainLines : wrapText(ctx, displayText || ' ', wrapWidth);
  drawLines.forEach((line, i) => {
    const lineW = ctx.measureText(line).width;
    const x = fullWidth || centerInBox ? contentLeft + (contentAreaW - lineW) / 2 : contentLeft;
    ctx.fillText(line, x, contentTop + i * lineHeight);
  });
}

function drawImageItem(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: Extract<LayoutItem, { type: 'image' }>,
  bounds: ItemBound[],
  getImage: (url: string) => HTMLImageElement | null
): void {
  if (!item.imageUrl) return;
  const img = getImage(item.imageUrl);
  const top = item.positionTopPercent ?? 50;
  const left = item.positionLeftPercent ?? 50;
  const wPct = item.widthPercent ?? 40;
  const hPct = item.heightPercent ?? 30;
  const iw = (w * wPct) / 100;
  const ih = (h * hPct) / 100;
  const cx = (w * left) / 100;
  const cy = (h * top) / 100;
  const leftPx = cx - iw / 2;
  const topPx = cy - ih / 2;
  const frameThickness = item.frameThickness ?? 0;
  const frameColor = item.frameColor ?? '#000000';
  const shape = item.imageShape ?? 'square';

  const outerLeft = Math.max(0, leftPx);
  const outerTop = Math.max(0, topPx);
  const outerW = Math.min(w - outerLeft, iw + frameThickness * 2);
  const outerH = Math.min(h - outerTop, ih + frameThickness * 2);
  bounds.push({ id: item.id, left: outerLeft, top: outerTop, width: outerW, height: outerH });

  if (frameThickness > 0) {
    ctx.strokeStyle = frameColor;
    ctx.lineWidth = frameThickness;
    ctx.strokeRect(outerLeft, outerTop, outerW, outerH);
  }
  const innerLeft = outerLeft + frameThickness;
  const innerTop = outerTop + frameThickness;
  const innerW = Math.max(0, outerW - frameThickness * 2);
  const innerH = Math.max(0, outerH - frameThickness * 2);
  if (innerW <= 0 || innerH <= 0) return;

  if (img && img.naturalWidth) {
    const fit = item.imageFit ?? 'contain';
    const sw = img.naturalWidth;
    const sh = img.naturalHeight;
    let dw = innerW,
      dh = innerH,
      dx = innerLeft,
      dy = innerTop;
    if (fit === 'contain') {
      const scale = Math.min(innerW / sw, innerH / sh);
      dw = sw * scale;
      dh = sh * scale;
      dx = innerLeft + (innerW - dw) / 2;
      dy = innerTop + (innerH - dh) / 2;
    } else if (fit === 'cover') {
      const scale = Math.max(innerW / sw, innerH / sh);
      dw = sw * scale;
      dh = sh * scale;
      dx = innerLeft + (innerW - dw) / 2;
      dy = innerTop + (innerH - dh) / 2;
    }
    ctx.save();
    if (shape === 'circle') {
      ctx.beginPath();
      ctx.ellipse(innerLeft + innerW / 2, innerTop + innerH / 2, innerW / 2, innerH / 2, 0, 0, Math.PI * 2);
      ctx.clip();
    } else if (shape === 'oval') {
      ctx.beginPath();
      ctx.ellipse(innerLeft + innerW / 2, innerTop + innerH / 2, innerW / 2, innerH / 2, 0, 0, Math.PI * 2);
      ctx.clip();
    } else if (shape === 'rounded') {
      const r = Math.min(innerW, innerH) / 6;
      ctx.beginPath();
      ctx.roundRect(innerLeft, innerTop, innerW, innerH, r);
      ctx.clip();
    } else if (shape === 'diamond') {
      ctx.beginPath();
      ctx.moveTo(innerLeft + innerW / 2, innerTop);
      ctx.lineTo(innerLeft + innerW, innerTop + innerH / 2);
      ctx.lineTo(innerLeft + innerW / 2, innerTop + innerH);
      ctx.lineTo(innerLeft, innerTop + innerH / 2);
      ctx.closePath();
      ctx.clip();
    } else if (shape === 'star') {
      const pts: [number, number][] = [
        [0.5, 0], [0.61, 0.35], [0.98, 0.35], [0.68, 0.57], [0.79, 0.91],
        [0.5, 0.7], [0.21, 0.91], [0.32, 0.57], [0.02, 0.35], [0.39, 0.35],
      ];
      ctx.beginPath();
      ctx.moveTo(innerLeft + pts[0][0] * innerW, innerTop + pts[0][1] * innerH);
      for (let i = 1; i < pts.length; i++)
        ctx.lineTo(innerLeft + pts[i][0] * innerW, innerTop + pts[i][1] * innerH);
      ctx.closePath();
      ctx.clip();
    }
    if (item.flipHorizontal) {
      ctx.translate(dx + dw, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, sw, sh, 0, 0, dw, dh);
    } else {
      ctx.drawImage(img, 0, 0, sw, sh, dx, dy, dw, dh);
    }
    ctx.restore();
  } else {
    ctx.fillStyle = '#e5e7eb';
    ctx.fillRect(innerLeft, innerTop, innerW, innerH);
  }
}

/** Yuvarlatılmış dikdörtgen yarıçapını çerçeve içinde tutacak şekilde hesaplar. */
function roundRectRadius(w: number, h: number, radiusPct: number): number {
  const r = Math.min(w, h) * radiusPct;
  return Math.min(r, w / 2, h / 2);
}

/** Yuvarlatılmış dikdörtgen doldurur (iPhone çerçevesi / iç ekran). */
function fillRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radiusPct: number
): void {
  const r = roundRectRadius(w, h, radiusPct);
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}

/** Cihaz gövdesinin dış köşe yarıçapı (Go: roundRectRadius + deviceFrameRadiusPct). */
function deviceOuterRadius(w: number, h: number): number {
  return roundRectRadius(w, h, DEVICE_FRAME_RADIUS_PCT);
}

/** Dış köşeyle eş merkezli iç köşe yarıçapı (Go: concentricRadius). */
function deviceConcentricRadius(outerRadius: number, inset: number, w: number, h: number): number {
  return Math.max(2, Math.min(outerRadius - inset, w / 2, h / 2));
}

/** Siyah ekran kenarı kalınlığı (Go: deviceScreenEdge). */
function deviceScreenEdge(minDim: number): number {
  return Math.max(DEVICE_SCREEN_EDGE_MIN, Math.round(minDim * DEVICE_SCREEN_EDGE_PCT));
}

/** Squircle (süperelips köşeli) dikdörtgen yolu; fill/clip için hazır path bırakır. */
function traceSquircleRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  if (radius <= 0) {
    ctx.rect(x, y, w, h);
    return;
  }
  const e = 2 / DEVICE_CORNER_EXPONENT;
  const segments = 24;
  const arc = (cx: number, cy: number, dirX: number, dirY: number, reverse: boolean) => {
    for (let i = 0; i <= segments; i++) {
      const k = reverse ? segments - i : i;
      const t = (k / segments) * (Math.PI / 2);
      ctx.lineTo(cx + dirX * radius * Math.pow(Math.cos(t), e), cy + dirY * radius * Math.pow(Math.sin(t), e));
    }
  };
  ctx.moveTo(x, y + radius);
  arc(x + radius, y + radius, -1, -1, false);
  ctx.lineTo(x + w - radius, y);
  arc(x + w - radius, y + radius, 1, -1, true);
  ctx.lineTo(x + w, y + h - radius);
  arc(x + w - radius, y + h - radius, 1, 1, false);
  ctx.lineTo(x + radius, y + h);
  arc(x + radius, y + h - radius, -1, 1, true);
  ctx.closePath();
}

/** Squircle dikdörtgeni doldurur (cihaz gövdesi / ekran kenarı). */
function fillSquircleRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fillStyle: string
): void {
  traceSquircleRect(ctx, x, y, w, h, r);
  ctx.fillStyle = fillStyle;
  ctx.fill();
}

/** Döndürülmüş dikdörtgenin sınır kutusu (AABB) köşe koordinatlarını döndürülmüş hâliyle hesaplar. */
function rotatedRectBounds(cx: number, cy: number, drawW: number, drawH: number, rotationRad: number): { left: number; top: number; width: number; height: number } {
  if (rotationRad === 0) {
    return { left: cx - drawW / 2, top: cy - drawH / 2, width: drawW, height: drawH };
  }
  const c = Math.cos(rotationRad);
  const s = Math.sin(rotationRad);
  const hw = drawW / 2;
  const hh = drawH / 2;
  const x0 = cx + (-hw) * c - (-hh) * s;
  const y0 = cy + (-hw) * s + (-hh) * c;
  const x1 = cx + hw * c - (-hh) * s;
  const y1 = cy + hw * s + (-hh) * c;
  const x2 = cx + hw * c - hh * s;
  const y2 = cy + hw * s + hh * c;
  const x3 = cx + (-hw) * c - hh * s;
  const y3 = cy + (-hw) * s + hh * c;
  const minX = Math.min(x0, x1, x2, x3);
  const maxX = Math.max(x0, x1, x2, x3);
  const minY = Math.min(y0, y1, y2, y3);
  const maxY = Math.max(y0, y1, y2, y3);
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
}

function drawDeviceItem(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  item: Extract<LayoutItem, { type: 'device' }>,
  bounds: ItemBound[],
  getImage: (url: string) => HTMLImageElement | null
): void {
  const top = item.positionTopPercent ?? 50;
  const left = item.positionLeftPercent ?? 50;
  const rotationDeg = item.rotationDegrees ?? 0;
  const rotationRad = (rotationDeg * Math.PI) / 180;
  const frameColor = item.frameColor ?? DEFAULT_DEVICE_FRAME_COLOR;
  const devW = w * 0.9;
  const devH = (devW * DEVICE_ASPECT_HEIGHT) / DEVICE_ASPECT_WIDTH;
  let drawW: number;
  let drawH: number;
  if (devH > h) {
    const scale = h / devH;
    drawW = devW * scale;
    drawH = h;
  } else {
    drawW = devW;
    drawH = devH;
  }
  const cx = (w * left) / 100;
  const cy = (h * top) / 100;
  const aabb = rotatedRectBounds(cx, cy, drawW, drawH, rotationRad);
  bounds.push({
    id: item.id,
    left: Math.max(0, Math.round(aabb.left)),
    top: Math.max(0, Math.round(aabb.top)),
    width: Math.min(w, Math.round(aabb.width)),
    height: Math.min(h, Math.round(aabb.height)),
  });
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotationRad);
  ctx.translate(-drawW / 2, -drawH / 2);
  const outerLeft = 0;
  const outerTop = 0;
  const outerW = drawW;
  const outerH = drawH;
  const minDim = Math.min(outerW, outerH);
  const outerRadius = deviceOuterRadius(outerW, outerH);
  fillSquircleRect(ctx, outerLeft, outerTop, outerW, outerH, outerRadius, frameColor);
  let bezel = Math.floor(minDim * DEVICE_BEZEL_PCT);
  if (bezel < DEVICE_BEZEL_MIN) bezel = DEVICE_BEZEL_MIN;
  if (bezel > DEVICE_BEZEL_MAX) bezel = DEVICE_BEZEL_MAX;
  if (outerW < 2 * bezel + 8 || outerH < 2 * bezel + 8) {
    bezel = Math.min(bezel, Math.floor((minDim - 8) / 2));
    if (bezel < 4) bezel = 4;
  }
  let edge = deviceScreenEdge(minDim);
  if (outerW < 2 * (bezel + edge) + 8 || outerH < 2 * (bezel + edge) + 8) edge = DEVICE_SCREEN_EDGE_MIN;
  // Çerçeve ile ekran arasındaki ince siyah şerit; şeffaf (contain) alanlarda da altta siyah kalır.
  const edgeLeft = outerLeft + bezel;
  const edgeTop = outerTop + bezel;
  const edgeW = Math.max(0, outerW - bezel * 2);
  const edgeH = Math.max(0, outerH - bezel * 2);
  const edgeRadius = deviceConcentricRadius(outerRadius, bezel, edgeW, edgeH);
  fillSquircleRect(ctx, edgeLeft, edgeTop, edgeW, edgeH, edgeRadius, DEVICE_SCREEN_EDGE_COLOR);
  const innerLeft = edgeLeft + edge;
  const innerTop = edgeTop + edge;
  const innerW = Math.max(0, edgeW - edge * 2);
  const innerH = Math.max(0, edgeH - edge * 2);
  const innerRadius = deviceConcentricRadius(outerRadius, bezel + edge, innerW, innerH);
  ctx.save();
  traceSquircleRect(ctx, innerLeft, innerTop, innerW, innerH, innerRadius);
  ctx.clip();
  if (item.background?.type === 'color') {
    ctx.fillStyle = item.background.color;
    ctx.fillRect(innerLeft, innerTop, innerW, innerH);
  } else if (item.background?.type === 'image' && item.background.imageUrl) {
    const img = getImage(item.background.imageUrl);
    if (img?.naturalWidth) {
      const fit = item.background.imageFit ?? 'cover';
      const position = item.background.imagePosition ?? 'center';
      drawImageFitInRect(ctx, img, innerLeft, innerTop, innerW, innerH, fit, position);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(innerLeft, innerTop, innerW, innerH);
    }
  } else {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(innerLeft, innerTop, innerW, innerH);
  }
  ctx.restore();
  ctx.restore();
}

function drawScreenToCanvas(
  ctx: CanvasRenderingContext2D,
  screen: Screen,
  width: number,
  height: number,
  getImage: (url: string) => HTMLImageElement | null,
  localeOptions?: { primaryLocale: string; editingLocale: string }
): ItemBound[] {
  const bounds: ItemBound[] = [];
  drawScreenBackground(ctx, width, height, screen.background, getImage);
  const primary = localeOptions?.primaryLocale ?? '';
  const editing = localeOptions?.editingLocale ?? primary;
  const items = ensureLayoutItemIndices(screen.layoutItems ?? []);
  // Tasarım iskeleti: her öğenin primary dildeki kutusu. Metin öğeleri çeviride büyürken
  // bu iskelete çarpmayacak şekilde küçültülür.
  const designRects = buildDesignRects(ctx, width, height, items, editing, primary);
  for (const item of items) {
    if (!isLayoutItemVisible(item)) continue;
    if (item.type === 'text') {
      const textToDraw = getTextForLocale(item, editing);
      const measured = measureTextItem(ctx, width, height, item, textToDraw, getTextSizePxForHeight(item, height));
      const limitY = textItemVerticalLimit(height, item, measured, designRects);
      drawTextItem(ctx, width, height, item, bounds, textToDraw, limitY);
    } else if (item.type === 'image') {
      drawImageItem(ctx, width, height, item, bounds, getImage);
    } else if (item.type === 'device') {
      drawDeviceItem(ctx, width, height, item, bounds, getImage);
    }
  }
  return bounds;
}

/** Sağ alt köşeye BuildMate watermark çizer (proje ikonu + mavi metin). Free plan önizleme ve export için. Oran önizleme ile export'ta aynı kalacak şekilde width/height'a göre hesaplanır. */
function drawWatermarkOnCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  logoImage?: HTMLImageElement | null
): void {
  if (width < 50 || height < 50) return;
  const minDim = Math.min(width, height);
  const padX = Math.max(8, width * 0.032);
  const padY = Math.max(8, height * 0.032);
  const logoSize = Math.round(Math.max(28, Math.min(100, minDim * 0.045)));
  const textSize = Math.round(Math.max(16, Math.min(72, height * 0.026)));
  const spacing = Math.round(Math.max(8, minDim * 0.012));
  const innerPad = Math.round(Math.max(6, minDim * 0.008));

  ctx.font = `600 ${textSize}px system-ui, -apple-system, sans-serif`;
  const text = 'BuildMate';
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const textHeight = textSize * 1.25;
  const boxH = Math.max(textHeight + innerPad * 2, logoSize + innerPad * 2);
  const boxW = innerPad + logoSize + spacing + textWidth + innerPad;

  const boxMinX = width - padX - boxW;
  const boxMinY = height - padY - boxH;
  const boxMaxX = width - padX;

  const logoLeft = boxMinX + innerPad;
  const logoTop = boxMinY + (boxH - logoSize) / 2;

  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.82)';
  ctx.fillRect(boxMinX, boxMinY, boxW, boxH);
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = Math.max(1, minDim / 800);
  ctx.strokeRect(boxMinX, boxMinY, boxW, boxH);

  if (logoImage?.complete && logoImage.naturalWidth > 0) {
    ctx.drawImage(logoImage, logoLeft, logoTop, logoSize, logoSize);
  } else {
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillRect(logoLeft, logoTop, logoSize, logoSize);
    ctx.fillStyle = '#111';
    ctx.font = `700 ${Math.round(logoSize * 0.72)}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('B', logoLeft + logoSize / 2, logoTop + logoSize / 2);
  }

  ctx.fillStyle = '#fff';
  ctx.font = `700 ${textSize}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText(text, boxMaxX - innerPad, boxMinY + boxH / 2 + textSize * 0.38);
  ctx.restore();
}

const MAX_UNDO_HISTORY = 50;

type ScreensSaveBaseline = {
  screens: Screen[];
  localization: string;
  exportWidth: number;
  exportHeight: number;
};

function serializeScreensBaseline(b: ScreensSaveBaseline): string {
  return JSON.stringify(b);
}

function suggestNextScreensName(existing: AppScreensSetSummary[]): string {
  if (existing.length === 0) return 'v1.0';
  const used = new Set(existing.map(s => s.name.trim().toLowerCase()));
  if (!used.has('v1.0')) return 'v1.0';
  for (let i = 1; i < 1000; i++) {
    const patch = `v1.${i}`;
    if (!used.has(patch.toLowerCase())) return patch;
  }
  return `Set ${existing.length + 1}`;
}

function deepCloneScreens(s: Screen[]): Screen[] {
  return JSON.parse(JSON.stringify(s));
}

const AppScreensPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { showError } = useError();
  const { isPro, goToUpgrade } = useIsPro();
  const {
    app: contextApp,
    loading: contextAppLoading,
    error: contextError,
    versions,
    versionsLoading,
  } = useApplication();
  // ApplicationProvider already loads GET /applications/:id; reuse it.
  const app = appId && contextApp?.id === appId ? contextApp : null;
  const appNotMobile = Boolean(app && app.type !== 'mobil_app');
  const loadError = contextError;
  const screensIdFromQuery = searchParams.get('set')?.trim() || '';
  const [screenSets, setScreenSets] = useState<AppScreensSetSummary[]>([]);
  const [screenSetsLoading, setScreenSetsLoading] = useState(false);
  const [selectedScreensId, setSelectedScreensId] = useState<string>('');
  const screensId = selectedScreensId || screensIdFromQuery;
  const currentScreenSet = screenSets.find(s => s.id === screensId) ?? null;
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  const versionMenuRef = useRef<HTMLDivElement>(null);
  const [newVersionModalOpen, setNewVersionModalOpen] = useState(false);
  const [newVersionNumber, setNewVersionNumber] = useState('');
  const [newVersionCopyScreens, setNewVersionCopyScreens] = useState(true);
  const [newVersionSubmitting, setNewVersionSubmitting] = useState(false);
  const [deletingScreensId, setDeletingScreensId] = useState<string | null>(null);
  const [deleteScreensConfirm, setDeleteScreensConfirm] = useState<{ id: string; name: string } | null>(null);
  const [pendingVersionSwitchId, setPendingVersionSwitchId] = useState<string | null>(null);
  const [localization, setLocalization] = useState(DEFAULT_APP_STORE_LOCALE);
  /** Seçilen locale'e göre metinleri göster/düzenle (primary veya hedef dillerden biri). */
  const [editingLocale, setEditingLocale] = useState(DEFAULT_APP_STORE_LOCALE);
  const [selectedScreenSizeId, setSelectedScreenSizeId] = useState<ScreenSizeId>(SCREEN_SIZE_PRESETS[0].id);
  const [sizeMenuOpen, setSizeMenuOpen] = useState(false);
  const sizeMenuRef = useRef<HTMLDivElement>(null);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [screenOrderModalOpen, setScreenOrderModalOpen] = useState(false);
  const [screenOrderDraft, setScreenOrderDraft] = useState<Screen[]>([]);
  const [screenOrderDragIndex, setScreenOrderDragIndex] = useState<number | null>(null);
  /** Undo için ekran state geçmişi (her adımda bir önceki screens snapshot). */
  const [screensHistory, setScreensHistory] = useState<Screen[][]>([]);
  const screensRef = useRef<Screen[]>(screens);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const [selectedLayoutItemId, setSelectedLayoutItemId] = useState<string | null>(null);

  const currentScreenSize = SCREEN_SIZE_PRESETS.find(p => p.id === selectedScreenSizeId) ?? SCREEN_SIZE_PRESETS[0];
  const [backgroundOpen, setBackgroundOpen] = useState(false);
  const [deviceInnerBackgroundTarget, setDeviceInnerBackgroundTarget] = useState<{ screenId: string; itemId: string } | null>(null);
  const [expandedLayoutItemId, setExpandedLayoutItemId] = useState<string | null>(null);
  /** Metin boyutu input'unda yazarken anlık clamp/fallback karışmasın diye lokal draft. */
  const [textSizeDraft, setTextSizeDraft] = useState<{ itemId: string; value: string } | null>(null);
  const [presetImageSearch, setPresetImageSearch] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const layoutImageInputRef = useRef<HTMLInputElement>(null);
  const screenVideoInputRef = useRef<HTMLInputElement>(null);
  const deviceInnerBackgroundInputRef = useRef<HTMLInputElement>(null);
  const [layoutImageUploadTarget, setLayoutImageUploadTarget] = useState<{ screenId: string; itemId: string } | null>(null);
  const [screenVideoUploadTarget, setScreenVideoUploadTarget] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  /** Son kaydedilen / yüklenen screens + primary locale + export boyutu; Save dirty kontrolü için. */
  const [savedBaseline, setSavedBaseline] = useState<string | null>(null);
  const [screensLoading, setScreensLoading] = useState(false);
  const [targetLocales, setTargetLocales] = useState<string[]>([]);
  const [completedTargetLocales, setCompletedTargetLocales] = useState<string[]>([]);
  const [reviewIssues, setReviewIssues] = useState<AppScreenIssue[]>([]);
  const [aiTranslationModalOpen, setAiTranslationModalOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiTemplateModalOpen, setAiTemplateModalOpen] = useState(false);
  const [aiTemplateContext, setAiTemplateContext] = useState('');
  const [aiTemplateUserPrompt, setAiTemplateUserPrompt] = useState('');
  const [aiTemplateReferenceUrls, setAiTemplateReferenceUrls] = useState('');
  const [aiTemplateReplace, setAiTemplateReplace] = useState(false);
  const [aiTemplateLoading, setAiTemplateLoading] = useState(false);
  const [aiRedesignModalOpen, setAiRedesignModalOpen] = useState(false);
  const [aiRedesignTargetId, setAiRedesignTargetId] = useState<string | null>(null);
  const [aiRedesignOptionalPrompt, setAiRedesignOptionalPrompt] = useState('');
  const [aiRedesignLoading, setAiRedesignLoading] = useState(false);
  const [targetLocalesSaving, setTargetLocalesSaving] = useState(false);
  const [targetLocalesDraft, setTargetLocalesDraft] = useState<string[]>([]);
  /** AI Translation modal: seçilen kaynak dil (primaryLocale). */
  const [sourceLocaleDraft, setSourceLocaleDraft] = useState<string>(DEFAULT_APP_STORE_LOCALE);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadPlatforms, setUploadPlatforms] = useState<('app_store' | 'play_store')[]>([]);
  /** App Store Connect’te ekranların bağlanacağı sürüm (örn. 1.2.0). */
  const [uploadAppStoreVersion, setUploadAppStoreVersion] = useState('');
  /** true: listing yerine Product Page Optimization (A/B) oluşturup treatment'a yükle. */
  const [uploadAsABTest, setUploadAsABTest] = useState(false);
  /** Default true: App Preview yalnızca primary locale. Uncheck = tüm dillere yükle. */
  const [uploadAppPreviewPrimaryOnly, setUploadAppPreviewPrimaryOnly] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastUploadJobId, setLastUploadJobId] = useState<string | null>(null);
  /** Devam eden / son screens_ai_translation ve screens_upload job'ları (metadata AI bar gibi). */
  const [activeTranslationJob, setActiveTranslationJob] = useState<Job | null>(null);
  const [activeUploadJob, setActiveUploadJob] = useState<Job | null>(null);
  const translationJobPrevStatusRef = useRef<Job['status'] | null>(null);
  /** Watermark'ta kullanılan proje logosu; preload edilir. */
  const [watermarkLogoImage, setWatermarkLogoImage] = useState<HTMLImageElement | null>(null);
  /** Önizleme kutusunun gerçek piksel boyutu (export'tan farklı); font/padding orana göre küçültülür. */
  const [previewSizeByScreenId, setPreviewSizeByScreenId] = useState<Record<string, { width: number; height: number }>>({});
  /** Canvas önizleme: her ekran için çizilen öğe sınırları (tıklama hit-test). */
  const [itemBoundsByScreenId, setItemBoundsByScreenId] = useState<Record<string, ItemBound[]>>({});
  const canvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});
  const [imageLoadTrigger, setImageLoadTrigger] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    screensRef.current = screens;
  }, [screens]);

  const pushScreensHistory = useCallback(() => {
    setScreensHistory(prev => [...prev.slice(-(MAX_UNDO_HISTORY - 1)), deepCloneScreens(screensRef.current)]);
  }, []);

  const undoScreens = useCallback(() => {
    setScreensHistory(prev => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      const restored = next.pop()!;
      setScreens(deepCloneScreens(restored));
      return next;
    });
  }, []);

  const canUndo = screensHistory.length > 0;

  const captureSavedBaseline = useCallback(
    (nextScreens: Screen[], nextLocalization: string, exportWidth: number, exportHeight: number) => {
      setSavedBaseline(
        serializeScreensBaseline({
          screens: nextScreens,
          localization: nextLocalization,
          exportWidth,
          exportHeight,
        })
      );
    },
    []
  );

  const hasUnsavedChanges = useMemo(() => {
    if (savedBaseline === null) return false;
    return (
      serializeScreensBaseline({
        screens,
        localization,
        exportWidth: currentScreenSize.exportWidth,
        exportHeight: currentScreenSize.exportHeight,
      }) !== savedBaseline
    );
  }, [savedBaseline, screens, localization, currentScreenSize.exportWidth, currentScreenSize.exportHeight]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.key !== 'z' && e.key !== 'Z') || (!e.ctrlKey && !e.metaKey)) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (!canUndo) return;
      e.preventDefault();
      undoScreens();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canUndo, undoScreens]);

  useEffect(() => {
    const img = document.createElement('img');
    img.onload = () => setWatermarkLogoImage(img);
    img.src = BUILD_MATE_LOGO_URL;
  }, []);

  const getImageForCanvas = useCallback((url: string): HTMLImageElement | null => {
    const cached = getCachedScreenImage(url);
    if (cached) return cached;
    if (url) {
      void loadScreenImage(url)
        .then(() => setImageLoadTrigger(t => t + 1))
        .catch(() => {
          /* broken / invented URLs (e.g. AI placeholders) — leave empty slot */
        });
    }
    return null;
  }, []);

  useEffect(() => {
    const refs = collectImageRefsFromScreens(screens);
    if (refs.length === 0) return;
    let cancelled = false;
    void preloadScreenImages(refs).then(() => {
      if (!cancelled) setImageLoadTrigger(t => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [screens]);

  // Whether we've completed at least one real fetch of the screen sets from the
  // server. Guards auto-create so it can't fire against the initial empty state
  // before the list has loaded (which would create a duplicate default set).
  const screenSetsLoadedRef = useRef(false);

  const refreshScreenSets = useCallback(async () => {
    if (!appId) return [];
    screenSetsLoadedRef.current = false;
    setScreenSetsLoading(true);
    try {
      const list = await listAppScreensSets(appId);
      setScreenSets(list);
      return list;
    } catch (err) {
      setScreenSets([]);
      showError(err instanceof Error ? err : new Error(t('common.loadFailed')));
      return [] as AppScreensSetSummary[];
    } finally {
      screenSetsLoadedRef.current = true;
      setScreenSetsLoading(false);
    }
  }, [appId, showError, t]);

  useEffect(() => {
    if (!appId || !app || app.type !== 'mobil_app') return;
    void refreshScreenSets();
  }, [appId, app, refreshScreenSets]);

  const autoCreatingDefaultSetRef = useRef(false);

  // No screen set exists yet: auto-create one named after the first (oldest) app version.
  useEffect(() => {
    if (!appId || !app || app.type !== 'mobil_app') return;
    if (screenSetsLoading || versionsLoading) return;
    // Only auto-create once the sets have actually been fetched; otherwise the
    // initial empty state would trigger a duplicate default set even when the
    // app already has an active set on the server.
    if (!screenSetsLoadedRef.current) return;
    if (screenSets.length > 0 || versions.length === 0) return;
    if (autoCreatingDefaultSetRef.current) return;
    const defaultName = versions[versions.length - 1]?.version?.trim();
    if (!defaultName) return;
    // Set (not reset on failure) so a create error surfaces once instead of retrying in a loop.
    autoCreatingDefaultSetRef.current = true;
    void (async () => {
      try {
        const created = await createAppScreensSet(appId, { name: defaultName });
        const list = await refreshScreenSets();
        const nextId = created.id || list.find(s => s.name === created.name)?.id || '';
        if (nextId) setSelectedScreensId(nextId);
      } catch (err) {
        showError(err instanceof Error ? err : new Error(t('appScreen.newVersionFailed')));
      }
    })();
  }, [appId, app, screenSets, screenSetsLoading, versions, versionsLoading, refreshScreenSets, showError, t]);

  useEffect(() => {
    if (screenSetsLoading) return;
    if (screenSets.length === 0) {
      setSelectedScreensId('');
      return;
    }
    setSelectedScreensId(prev => {
      if (prev && screenSets.some(s => s.id === prev)) return prev;
      if (screensIdFromQuery && screenSets.some(s => s.id === screensIdFromQuery)) return screensIdFromQuery;
      return screenSets[0].id;
    });
  }, [screenSets, screenSetsLoading, screensIdFromQuery]);

  useEffect(() => {
    if (!screensId) return;
    if (searchParams.get('set') === screensId) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('version');
        next.set('set', screensId);
        return next;
      },
      { replace: true }
    );
  }, [screensId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!appId || !screensId || !app || app.type !== 'mobil_app') return;
    let cancelled = false;
    setScreensLoading(true);
    setSavedBaseline(null);
    getAppScreens(appId, screensId)
      .then(({ screens: loaded, primaryLocale: loadedPrimary, targetLocales: locales, completedTargetLocales: completed, exportWidth: loadedW, exportHeight: loadedH, issues }) => {
        if (cancelled) return;
        const nextScreens = normalizeScreensLayoutIndices(Array.isArray(loaded) ? (loaded as Screen[]) : []);
        setScreens(nextScreens);
        setScreensHistory([]);
        setTargetLocales(Array.isArray(locales) ? locales : []);
        setCompletedTargetLocales(Array.isArray(completed) ? completed : []);
        setReviewIssues(Array.isArray(issues) ? issues : []);
        let nextLocalization = DEFAULT_APP_STORE_LOCALE;
        if (typeof loadedPrimary === 'string' && loadedPrimary) {
          nextLocalization = loadedPrimary;
          setLocalization(loadedPrimary);
          setEditingLocale(loadedPrimary);
        }
        let exportWidth: number = SCREEN_SIZE_PRESETS[0].exportWidth;
        let exportHeight: number = SCREEN_SIZE_PRESETS[0].exportHeight;
        if (typeof loadedW === 'number' && typeof loadedH === 'number' && loadedW > 0 && loadedH > 0) {
          const match = SCREEN_SIZE_PRESETS.find(p => p.exportWidth === loadedW && p.exportHeight === loadedH);
          if (match) {
            setSelectedScreenSizeId(match.id);
            exportWidth = match.exportWidth;
            exportHeight = match.exportHeight;
          }
        }
        captureSavedBaseline(nextScreens, nextLocalization, exportWidth, exportHeight);
      })
      .catch(err => {
        if (cancelled) return;
        showError(err instanceof Error ? err : new Error(t('common.loadFailed')));
      })
      .finally(() => {
        if (!cancelled) setScreensLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appId, screensId, app, showError, t, captureSavedBaseline]);

  const selectVersion = useCallback(
    (id: string) => {
      if (!id || id === screensId) {
        setVersionMenuOpen(false);
        return;
      }
      if (hasUnsavedChanges) {
        setPendingVersionSwitchId(id);
        setVersionMenuOpen(false);
        return;
      }
      setSelectedScreensId(id);
      setVersionMenuOpen(false);
    },
    [screensId, hasUnsavedChanges]
  );

  const confirmVersionSwitch = useCallback(() => {
    if (!pendingVersionSwitchId) return;
    setSelectedScreensId(pendingVersionSwitchId);
    setPendingVersionSwitchId(null);
  }, [pendingVersionSwitchId]);

  const openNewVersionModal = useCallback(() => {
    setNewVersionNumber(suggestNextScreensName(screenSets));
    setNewVersionCopyScreens(Boolean(screensId));
    setNewVersionModalOpen(true);
    setVersionMenuOpen(false);
  }, [screenSets, screensId]);

  const handleCreateScreensVersion = useCallback(async () => {
    if (!appId || !newVersionNumber.trim()) return;
    setNewVersionSubmitting(true);
    try {
      const created = await createAppScreensSet(appId, {
        name: newVersionNumber.trim(),
        copyFromId: newVersionCopyScreens && screensId ? screensId : undefined,
      });
      const list = await refreshScreenSets();
      const nextId = created.id || list.find(s => s.name === created.name)?.id || created.id;
      setSelectedScreensId(nextId);
      setNewVersionModalOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.newVersionFailed')));
    } finally {
      setNewVersionSubmitting(false);
    }
  }, [
    appId,
    screensId,
    newVersionNumber,
    newVersionCopyScreens,
    refreshScreenSets,
    showError,
    t,
  ]);

  const handleDeleteScreensSet = useCallback(
    (id: string, name: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!appId || !id || deletingScreensId) return;
      setDeleteScreensConfirm({ id, name });
      setVersionMenuOpen(false);
    },
    [appId, deletingScreensId]
  );

  const confirmDeleteScreensSet = useCallback(async () => {
    if (!appId || !deleteScreensConfirm || deletingScreensId) return;
    const { id } = deleteScreensConfirm;
    setDeletingScreensId(id);
    try {
      await deleteAppScreensSet(appId, id);
      const list = await refreshScreenSets();
      if (screensId === id) {
        setSelectedScreensId(list[0]?.id ?? '');
        if (list.length === 0) {
          setScreens([]);
          setScreensHistory([]);
          setSavedBaseline(null);
        }
      }
      setDeleteScreensConfirm(null);
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.deleteVersionFailed')));
    } finally {
      setDeletingScreensId(null);
    }
  }, [appId, deleteScreensConfirm, deletingScreensId, screensId, refreshScreenSets, showError, t]);

  const reloadScreensData = useCallback(() => {
    if (!appId || !screensId) return;
    getAppScreens(appId, screensId)
      .then(({ screens: loaded, primaryLocale: loadedPrimary, targetLocales: locales, completedTargetLocales: completed, exportWidth: loadedW, exportHeight: loadedH, issues }) => {
        const nextScreens = normalizeScreensLayoutIndices(Array.isArray(loaded) ? (loaded as Screen[]) : []);
        setScreens(nextScreens);
        setTargetLocales(Array.isArray(locales) ? locales : []);
        setCompletedTargetLocales(Array.isArray(completed) ? completed : []);
        setReviewIssues(Array.isArray(issues) ? issues : []);
        let nextLocalization = localization;
        if (typeof loadedPrimary === 'string' && loadedPrimary) {
          nextLocalization = loadedPrimary;
          setLocalization(loadedPrimary);
        }
        let exportWidth: number = currentScreenSize.exportWidth;
        let exportHeight: number = currentScreenSize.exportHeight;
        if (typeof loadedW === 'number' && typeof loadedH === 'number' && loadedW > 0 && loadedH > 0) {
          const match = SCREEN_SIZE_PRESETS.find(p => p.exportWidth === loadedW && p.exportHeight === loadedH);
          if (match) {
            setSelectedScreenSizeId(match.id);
            exportWidth = match.exportWidth;
            exportHeight = match.exportHeight;
          }
        }
        captureSavedBaseline(nextScreens, nextLocalization, exportWidth, exportHeight);
      })
      .catch(() => {});
  }, [appId, screensId, localization, currentScreenSize.exportWidth, currentScreenSize.exportHeight, captureSavedBaseline]);

  // Sayfa açıldığında bu app için devam eden screens translation / upload job'ları
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    (async () => {
      try {
        const [pending, processing] = await Promise.all([
          listJobs('pending'),
          listJobs('processing'),
        ]);
        if (cancelled) return;
        const all = [...pending, ...processing].filter(j => j.applicationId === appId);
        const pickLatest = (type: string) => {
          const matches = all
            .filter(j => j.type === type)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          return matches[0] ?? null;
        };
        setActiveTranslationJob(pickLatest('screens_ai_translation'));
        setActiveUploadJob(pickLatest('screens_upload'));
      } catch {
        if (!cancelled) {
          setActiveTranslationJob(null);
          setActiveUploadJob(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId]);

  const translationJobId = activeTranslationJob?.id;
  const translationJobStatus = activeTranslationJob?.status;
  useEffect(() => {
    if (!translationJobId || (translationJobStatus !== 'pending' && translationJobStatus !== 'processing')) return;
    const interval = setInterval(() => {
      getJob(translationJobId)
        .then(updated => setActiveTranslationJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(interval);
  }, [translationJobId, translationJobStatus]);

  const uploadJobId = activeUploadJob?.id;
  const uploadJobStatus = activeUploadJob?.status;
  useEffect(() => {
    if (!uploadJobId || (uploadJobStatus !== 'pending' && uploadJobStatus !== 'processing')) return;
    const interval = setInterval(() => {
      getJob(uploadJobId)
        .then(updated => setActiveUploadJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(interval);
  }, [uploadJobId, uploadJobStatus]);

  // Çeviri job tamamlanınca veya fail olunca ekran verisini yenile (locale bazında yazılan translations)
  useEffect(() => {
    const prev = translationJobPrevStatusRef.current;
    const curr = activeTranslationJob?.status ?? null;
    translationJobPrevStatusRef.current = curr;
    if (
      activeTranslationJob?.type === 'screens_ai_translation' &&
      (curr === 'completed' || curr === 'failed') &&
      (prev === 'pending' || prev === 'processing')
    ) {
      reloadScreensData();
    }
  }, [activeTranslationJob?.status, activeTranslationJob?.type, reloadScreensData]);

  const supportedLocaleValues = React.useMemo(() => APP_STORE_LOCALIZATIONS.map(l => l.value), []);

  const currentEditingLocale = supportedLocaleValues.includes(editingLocale)
    ? editingLocale
    : supportedLocaleValues.includes(localization)
      ? localization
      : supportedLocaleValues[0];

  useEffect(() => {
    if (!versionMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (versionMenuRef.current && !versionMenuRef.current.contains(e.target as Node)) {
        setVersionMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVersionMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [versionMenuOpen]);

  useEffect(() => {
    if (!sizeMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (sizeMenuRef.current && !sizeMenuRef.current.contains(e.target as Node)) {
        setSizeMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSizeMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sizeMenuOpen]);

  const screenSizeGroups = useMemo(
    () =>
      [
        { key: 'iphone' as const, labelKey: 'appScreen.screenSizeGroupIphone', Icon: Smartphone },
        { key: 'ipad' as const, labelKey: 'appScreen.screenSizeGroupIpad', Icon: Tablet },
      ].map(group => ({
        ...group,
        presets: SCREEN_SIZE_PRESETS.filter(p => p.group === group.key),
      })),
    []
  );

  useEffect(() => {
    if (supportedLocaleValues.length > 0 && !supportedLocaleValues.includes(editingLocale)) {
      setEditingLocale(localization && supportedLocaleValues.includes(localization) ? localization : supportedLocaleValues[0]);
    }
  }, [supportedLocaleValues, editingLocale, localization]);

  // Önizleme kutusu boyutlarını ölç; export ile gösterim farklı olduğunda font/padding bu orana göre hesaplanır.
  useEffect(() => {
    const refs = screenPreviewRefs.current;
    const observers: ResizeObserver[] = [];
    Object.entries(refs).forEach(([screenId, el]) => {
      if (!el) return;
      const ro = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setPreviewSizeByScreenId(prev => ({ ...prev, [screenId]: { width, height } }));
          }
        }
      });
      ro.observe(el);
      observers.push(ro);
    });
    return () => observers.forEach(ro => ro.disconnect());
  }, [screens.length, selectedScreenSizeId]);

  // Canvas önizlemeyi çiz: ekran verisi veya boyut değişince yeniden çiz.
  useEffect(() => {
    const next: Record<string, ItemBound[]> = {};
    screens.forEach(screen => {
      if (isVideoScreen(screen)) {
        next[screen.id] = [];
        return;
      }
      const size = previewSizeByScreenId[screen.id];
      const canvas = canvasRefs.current[screen.id];
      if (!canvas || !size || size.width <= 0 || size.height <= 0) return;
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(size.width * dpr);
      const h = Math.round(size.height * dpr);
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${size.width}px`;
      canvas.style.height = `${size.height}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, size.width, size.height);
      const bounds = drawScreenToCanvas(ctx, screen, size.width, size.height, getImageForCanvas, {
        primaryLocale: localization,
        editingLocale,
      });
      if (!isPro) drawWatermarkOnCanvas(ctx, size.width, size.height, watermarkLogoImage);
      next[screen.id] = bounds;
    });
    setItemBoundsByScreenId(prev => ({ ...prev, ...next }));
  }, [screens, previewSizeByScreenId, imageLoadTrigger, getImageForCanvas, isPro, watermarkLogoImage, localization, editingLocale]);

  const handleSave = useCallback(async () => {
    if (!appId || !screensId || !hasUnsavedChanges) return;
    setSaveStatus('loading');
    try {
      const payload = screens.map((s: Screen) => {
        const videoClips = getScreenVideoClips(s);
        const layoutItems = ensureLayoutItemIndices(s.layoutItems ?? []).map((it: LayoutItem) =>
          it.type === 'text'
            ? (() => {
                const p = getTextPaddingPercents(it);
                return {
                  ...it,
                  textSize: getTextSize(it),
                  widthPercent: (it as { widthPercent?: number }).widthPercent ?? 100,
                  textPaddingTopPercent: roundRatio(p.top / 100),
                  textPaddingRightPercent: roundRatio(p.right / 100),
                  textPaddingBottomPercent: roundRatio(p.bottom / 100),
                  textPaddingLeftPercent: roundRatio(p.left / 100),
                  backgroundRadiusPercent: roundRatio(getBackgroundRadiusPercent(it) / 100, 6),
                  minWidthPercent: roundRatio(getMinWidthPercent(it) / 100, 6),
                };
              })()
            : it
        );
        return {
          ...s,
          videoClips: videoClips.length > 0 ? videoClips : undefined,
          videoUrls: videoClips.length > 0 ? videoClips.map(c => c.url) : undefined,
          layoutItems,
        };
      }) as Screen[];
      const saved = await saveAppScreens(appId, screensId, {
        screens: payload,
        primaryLocale: localization,
        exportWidth: currentScreenSize.exportWidth,
        exportHeight: currentScreenSize.exportHeight,
      });
      setReviewIssues(Array.isArray(saved.issues) ? saved.issues : []);
      captureSavedBaseline(screens, localization, currentScreenSize.exportWidth, currentScreenSize.exportHeight);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setSaveStatus('error');
      showError(err instanceof Error ? err : new Error(t('appScreen.saveFailed')));
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [appId, screensId, hasUnsavedChanges, screens, localization, currentScreenSize.exportWidth, currentScreenSize.exportHeight, captureSavedBaseline, showError, t]);

  const openAiTranslationModal = useCallback(() => {
    setSourceLocaleDraft(localization);
    setTargetLocalesDraft(targetLocales.filter(l => l !== localization));
    setAiTranslationModalOpen(true);
  }, [localization, targetLocales]);

  const openAiTemplateModal = useCallback(() => {
    if (!app) return;
    const parts: string[] = [];
    if (app.name?.trim()) parts.push(`${t('appScreen.aiTemplateAppNameLabel')}: ${app.name.trim()}`);
    if (app.summary?.trim()) parts.push(`${t('appScreen.aiTemplateSummaryLabel')}:\n${app.summary.trim()}`);
    if (app.description?.trim()) parts.push(`${t('appScreen.aiTemplateDescriptionLabel')}:\n${app.description.trim()}`);
    setAiTemplateContext(parts.join('\n\n'));
    setAiTemplateUserPrompt('');
    setAiTemplateReferenceUrls('');
    setAiTemplateReplace(false);
    setAiTemplateModalOpen(true);
  }, [app, t]);

  const openAiRedesignModal = useCallback(
    (screenId: string) => {
      if (!isPro) {
        goToUpgrade();
        return;
      }
      setAiRedesignTargetId(screenId);
      setAiRedesignOptionalPrompt('');
      setAiRedesignModalOpen(true);
    },
    [isPro, goToUpgrade]
  );

  const handleAiRedesignConfirm = useCallback(async () => {
    if (!appId || !screensId || !aiRedesignTargetId) return;
    const src = screens.find(s => s.id === aiRedesignTargetId);
    if (!src) return;
    setAiRedesignLoading(true);
    try {
      const { screen: raw } = await redesignAppScreenAI(appId, screensId, {
        primaryLocale: editingLocale,
        preserveScreenId: src.id,
        screen: src,
        userPrompt: aiRedesignOptionalPrompt.trim() || undefined,
      });
      if (raw == null || typeof raw !== 'object') {
        throw new Error(t('appScreen.aiRedesignFailed'));
      }
      const next = normalizeScreenLayoutIndices(raw as Screen);
      pushScreensHistory();
      setScreens(prev => prev.map(s => (s.id === aiRedesignTargetId ? next : s)));
      setAiRedesignModalOpen(false);
      setAiRedesignTargetId(null);
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.aiRedesignFailed')));
    } finally {
      setAiRedesignLoading(false);
    }
  }, [
    appId,
    screensId,
    aiRedesignTargetId,
    screens,
    editingLocale,
    aiRedesignOptionalPrompt,
    pushScreensHistory,
    showError,
    t,
  ]);

  const handleAiTemplateGenerate = useCallback(async () => {
    if (!appId || !screensId) return;
    setAiTemplateLoading(true);
    try {
      const payload: {
        primaryLocale: string;
        contextOverride?: string;
        userPrompt?: string;
        referenceImageUrls?: string[];
      } = {
        primaryLocale: editingLocale,
      };
      const ctx = aiTemplateContext.trim();
      if (ctx) payload.contextOverride = ctx;
      const up = aiTemplateUserPrompt.trim();
      if (up) payload.userPrompt = up;
      const refUrls = aiTemplateReferenceUrls
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(s => /^https?:\/\//i.test(s))
        .slice(0, 6);
      if (refUrls.length > 0) payload.referenceImageUrls = refUrls;
      const { screens: raw } = await suggestAppScreensAITemplate(appId, screensId, payload);
      const list = normalizeScreensLayoutIndices((Array.isArray(raw) ? raw : []) as Screen[]);
      pushScreensHistory();
      if (aiTemplateReplace) setScreens(list);
      else setScreens(prev => [...prev, ...list]);
      setSelectedScreenId(null);
      setSelectedLayoutItemId(null);
      setAiTemplateModalOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.aiTemplateFailed')));
    } finally {
      setAiTemplateLoading(false);
    }
  }, [
    appId,
    screensId,
    editingLocale,
    aiTemplateContext,
    aiTemplateUserPrompt,
    aiTemplateReferenceUrls,
    aiTemplateReplace,
    pushScreensHistory,
    showError,
    t,
  ]);

  const appStoreReady = Boolean(app?.iosBundleId && app?.appStoreSecretId);
  const playStoreReady = Boolean(app?.androidBundleId && app?.playStoreSecretId);
  const openUploadModal = useCallback(() => {
    setUploadPlatforms([]);
    setUploadAppStoreVersion(versions[0]?.version ?? '');
    setUploadAsABTest(false);
    setUploadAppPreviewPrimaryOnly(true);
    setUploadModalOpen(true);
  }, [versions]);
  /** Upload: target translations complete and no Guideline 2.3 errors. */
  const reviewErrors = useMemo(
    () => reviewIssues.filter(i => i.severity === 'error'),
    [reviewIssues]
  );
  const reviewWarnings = useMemo(
    () => reviewIssues.filter(i => i.severity === 'warning'),
    [reviewIssues]
  );
  const canUpload =
    (targetLocales.length === 0 || targetLocales.every(l => completedTargetLocales.includes(l))) &&
    reviewErrors.length === 0;

  const focusReviewIssue = useCallback(
    (issue: AppScreenIssue) => {
      if (issue.screenId) {
        setSelectedScreenId(issue.screenId);
      }
      if (issue.layoutItemId) {
        setSelectedLayoutItemId(issue.layoutItemId);
        setExpandedLayoutItemId(issue.layoutItemId);
      }
    },
    []
  );

  // Review issue'ları artık her save yanıtında (ve ilk yüklemede) backend'den gelir;
  // düzenleme sırasında canlı/anlık hesaplama yapılmaz.

  const toggleUploadPlatform = useCallback((platform: 'app_store' | 'play_store') => {
    setUploadPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  }, []);
  const uploadNeedsAppStoreVersion =
    uploadPlatforms.includes('app_store') && !uploadAsABTest && !uploadAppStoreVersion.trim();

  useEffect(() => {
    if (!uploadModalOpen) return;
    const preferred = versions[0]?.version ?? '';
    if (!preferred) return;
    setUploadAppStoreVersion(prev =>
      prev && versions.some(v => v.version === prev) ? prev : preferred
    );
  }, [uploadModalOpen, versions]);

  const handleUploadConfirm = useCallback(async () => {
    if (!appId || !screensId || uploadPlatforms.length === 0) return;
    if (uploadPlatforms.includes('app_store') && !uploadAsABTest && !uploadAppStoreVersion.trim()) return;
    setUploading(true);
    try {
      const { jobId } = await createScreensUploadJob(appId, screensId, uploadPlatforms, {
        appStoreVersion:
          uploadPlatforms.includes('app_store') && !uploadAsABTest
            ? uploadAppStoreVersion.trim()
            : undefined,
        localizeAppPreviews: uploadPlatforms.includes('app_store')
          ? !uploadAppPreviewPrimaryOnly
          : false,
        uploadAsABTest: uploadPlatforms.includes('app_store') ? uploadAsABTest : false,
      });
      setUploadModalOpen(false);
      setUploadPlatforms([]);
      setUploadAppStoreVersion('');
      setUploadAsABTest(false);
      setUploadAppPreviewPrimaryOnly(true);
      setLastUploadJobId(jobId);
      setSaveStatus('success');
      setTimeout(() => { setSaveStatus('idle'); setLastUploadJobId(null); }, 4000);
      // Tetiklemeyi arka planda async yap; UI bekletilmez
      triggerJob(jobId).catch(er => showError(er));
      getJob(jobId)
        .then(job => setActiveUploadJob(job))
        .catch(() => setActiveUploadJob({
          id: jobId,
          applicationId: appId,
          type: 'screens_upload',
          payload: {},
          status: 'pending',
          progress: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.uploadJobCreateFailed')));
    } finally {
      setUploading(false);
    }
  }, [appId, screensId, uploadPlatforms, uploadAppStoreVersion, uploadAsABTest, uploadAppPreviewPrimaryOnly, showError, t]);

  const saveTargetLocales = useCallback(async () => {
    if (!appId || !screensId) return;
    setTargetLocalesSaving(true);
    try {
      const saved = await saveAppScreens(appId, screensId, {
        primaryLocale: sourceLocaleDraft,
        targetLocales: targetLocalesDraft,
      });
      setReviewIssues(Array.isArray(saved.issues) ? saved.issues : []);
      setLocalization(sourceLocaleDraft);
      setEditingLocale(sourceLocaleDraft);
      setTargetLocales(targetLocalesDraft);
      setSavedBaseline(prev => {
        if (prev === null) return prev;
        try {
          const parsed = JSON.parse(prev) as ScreensSaveBaseline;
          return serializeScreensBaseline({ ...parsed, localization: sourceLocaleDraft });
        } catch {
          return prev;
        }
      });
      setAiTranslationModalOpen(false);
      if (targetLocalesDraft.length > 0) {
        const { jobId } = await createScreensTranslateJob(appId, screensId);
        triggerJob(jobId).catch(er => showError(er));
        getJob(jobId)
          .then(job => setActiveTranslationJob(job))
          .catch(() => setActiveTranslationJob({
            id: jobId,
            applicationId: appId,
            type: 'screens_ai_translation',
            payload: {},
            status: 'pending',
            progress: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));
      }
    } catch (err) {
      showError(err instanceof Error ? err : new Error(t('appScreen.targetLanguagesSaveFailed')));
    } finally {
      setTargetLocalesSaving(false);
    }
  }, [appId, screensId, sourceLocaleDraft, targetLocalesDraft, showError, t]);

  const addScreen = useCallback((type: 'image' | 'video' = 'image') => {
    pushScreensHistory();
    const id = `screen-${Date.now()}`;
    if (type === 'video') {
      setScreens(prev => [
        ...prev,
        {
          id,
          type: 'video',
          deviceType: previewDeviceTypeFromScreenSizeId(selectedScreenSizeId),
        },
      ]);
    } else {
      setScreens(prev => [...prev, { id, type: 'image' }]);
    }
    setSelectedScreenId(id);
    setSelectedLayoutItemId(null);
  }, [pushScreensHistory, selectedScreenSizeId]);

  const updateScreen = useCallback((screenId: string, updates: Partial<Screen>) => {
    pushScreensHistory();
    setScreens(prev => prev.map(s => (s.id === screenId ? { ...s, ...updates } : s)));
  }, [pushScreensHistory]);

  const openScreenOrderModal = useCallback(() => {
    setScreenOrderDraft([...screens]);
    setScreenOrderDragIndex(null);
    setScreenOrderModalOpen(true);
  }, [screens]);

  const moveScreenOrderDraft = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setScreenOrderDraft(prev => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const applyScreenOrder = useCallback(() => {
    const orderChanged =
      screenOrderDraft.length === screens.length &&
      screenOrderDraft.some((screen, index) => screen.id !== screens[index]?.id);
    if (orderChanged) {
      pushScreensHistory();
      setScreens(screenOrderDraft);
    }
    setScreenOrderModalOpen(false);
    setScreenOrderDragIndex(null);
  }, [screenOrderDraft, screens, pushScreensHistory]);

  const closePanel = useCallback(() => {
    setSelectedScreenId(null);
    setSelectedLayoutItemId(null);
  }, []);

  const duplicateScreen = useCallback(() => {
    if (!selectedScreenId) return;
    pushScreensHistory();
    const source = screens.find(s => s.id === selectedScreenId);
    const newId = `screen-${Date.now()}`;
    setScreens(prev => {
      const idx = prev.findIndex(s => s.id === selectedScreenId);
      if (idx < 0) return prev;
      const next = [...prev];
      next.splice(idx + 1, 0, {
        id: newId,
        type: source?.type ?? 'image',
        videoClips: source ? getScreenVideoClips(source).map(c => ({ ...c })) : undefined,
        videoUrls: undefined,
        deviceType: source?.deviceType,
        visible: source?.visible,
        layoutItems: source?.layoutItems?.map((l, i) =>
          l.type === 'text' ? { ...l, id: `layout-${Date.now()}-${i}` } : { ...l, id: `layout-${Date.now()}-${i}` }
        ) ?? undefined,
        background: source?.background ? { ...source.background } : undefined,
      });
      return next;
    });
    setSelectedScreenId(newId);
  }, [selectedScreenId, screens, pushScreensHistory]);

  const deleteScreen = useCallback(() => {
    if (!selectedScreenId) return;
    pushScreensHistory();
    setScreens(prev => prev.filter(s => s.id !== selectedScreenId));
    setSelectedScreenId(null);
    setSelectedLayoutItemId(null);
  }, [selectedScreenId, pushScreensHistory]);

  const updateScreenBackground = useCallback((screenId: string, background: ScreenBackground | undefined) => {
    pushScreensHistory();
    setScreens(prev => prev.map(s => (s.id === screenId ? { ...s, background } : s)));
  }, [pushScreensHistory]);

  const addLayoutItem = useCallback((screenId: string, type: 'text' | 'image' | 'device') => {
    const existing = screens.find(s => s.id === screenId)?.layoutItems ?? [];
    const nextIndex = maxLayoutItemIndex(existing) + 1;
    const newItem: LayoutItem =
      type === 'text'
        ? { id: `layout-${Date.now()}`, ...DEFAULT_TEXT_LAYOUT, index: nextIndex, translations: { [editingLocale]: t('appScreen.textContent') } }
        : type === 'image'
          ? { id: `layout-${Date.now()}`, ...DEFAULT_IMAGE_LAYOUT, index: nextIndex }
          : { id: `layout-${Date.now()}`, ...DEFAULT_DEVICE_LAYOUT, index: nextIndex };
    pushScreensHistory();
    setScreens(prev =>
      prev.map(s => (s.id === screenId ? { ...s, layoutItems: [...(s.layoutItems ?? []), newItem] } : s))
    );
    setSelectedLayoutItemId(newItem.id);
    setExpandedLayoutItemId(newItem.id);
  }, [screens, editingLocale, t, pushScreensHistory]);

  const updateLayoutItem = useCallback(
    (
      screenId: string,
      itemId: string,
      updates:
        | Partial<Extract<LayoutItem, { type: 'text' }>>
        | Partial<Extract<LayoutItem, { type: 'image' }>>
        | Partial<Extract<LayoutItem, { type: 'device' }>>
    ) => {
      pushScreensHistory();
      setScreens(prev =>
        prev.map(s =>
          s.id === screenId
            ? {
                ...s,
                layoutItems: (s.layoutItems ?? []).map(item =>
                  item.id === itemId ? ({ ...item, ...updates } as LayoutItem) : item
                ),
              }
            : s
        )
      );
    },
    [pushScreensHistory]
  );

  /** Seçilen locale için metin günceller (her zaman translations[locale]). Undo için history metin alanı focus'ta push edilir, her tuşta değil. */
  const updateTextForLocale = useCallback(
    (screenId: string, itemId: string, locale: string, value: string) => {
      if (!locale) return;
      setScreens(prev =>
        prev.map(s => {
          if (s.id !== screenId) return s;
          return {
            ...s,
            layoutItems: (s.layoutItems ?? []).map(item => {
              if (item.id !== itemId || item.type !== 'text') return item;
              const nextTranslations = { ...(item.translations ?? {}), [locale]: value };
              return { ...item, translations: nextTranslations };
            }),
          };
        })
      );
    },
    []
  );

  /** Metin alanına focus olduğunda undo için önceki state'i history'ye ekle (tek düzenleme = tek undo adımı). */
  const pushHistoryBeforeTextEdit = useCallback(() => {
    pushScreensHistory();
  }, [pushScreensHistory]);

  const removeLayoutItem = useCallback((screenId: string, itemId: string) => {
    pushScreensHistory();
    setScreens(prev =>
      prev.map(s =>
        s.id === screenId ? { ...s, layoutItems: (s.layoutItems ?? []).filter(l => l.id !== itemId) } : s
      )
    );
  }, [pushScreensHistory]);

  /** Layout item'ı bire bir kopyalar (yeni id); kaynağın hemen altına ekler. */
  const duplicateLayoutItem = useCallback((screenId: string, itemId: string) => {
    const source = screens.find(s => s.id === screenId)?.layoutItems?.find(l => l.id === itemId);
    if (!source) return;
    const newId = `layout-${Date.now()}`;
    const cloned: LayoutItem =
      source.type === 'text'
        ? {
            ...source,
            id: newId,
            translations: source.translations ? { ...source.translations } : undefined,
          }
        : source.type === 'device'
          ? {
              ...source,
              id: newId,
              background: source.background ? { ...source.background } : undefined,
            }
          : { ...source, id: newId };
    pushScreensHistory();
    setScreens(prev =>
      prev.map(s => {
        if (s.id !== screenId || !s.layoutItems?.length) return s;
        const idx = s.layoutItems.findIndex(l => l.id === itemId);
        if (idx < 0) return s;
        const items = [...s.layoutItems];
        items.splice(idx + 1, 0, cloned);
        return { ...s, layoutItems: reassignLayoutItemIndices(items) };
      })
    );
    setSelectedLayoutItemId(newId);
    setExpandedLayoutItemId(newId);
  }, [screens, pushScreensHistory]);

  /** Layout item sırasını değiştirir (sürükle-bırak). fromIndex → toIndex; index alanları yeniden atanır. */
  const reorderLayoutItems = useCallback((screenId: string, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    pushScreensHistory();
    setScreens(prev =>
      prev.map(s => {
        if (s.id !== screenId || !s.layoutItems?.length) return s;
        const items = [...s.layoutItems];
        const [removed] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, removed);
        return { ...s, layoutItems: reassignLayoutItemIndices(items) };
      })
    );
  }, [pushScreensHistory]);

  const [layoutListDrag, setLayoutListDrag] = useState<{ screenId: string; itemId: string } | null>(null);

  const screenPreviewRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const layoutItemPanelRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const didDragRef = useRef(false);
  const [dragState, setDragState] = useState<{ screenId: string; itemId: string; startX: number; startY: number } | null>(null);
  const [resizeState, setResizeState] = useState<{
    screenId: string;
    itemId: string;
    anchorLeftPercent: number;
    anchorTopPercent: number;
  } | null>(null);
  const DRAG_THRESHOLD_PX = 5;

  const handleLayoutItemMouseDown = useCallback(
    (e: React.MouseEvent, screenId: string, itemId: string) => {
      e.stopPropagation();
      didDragRef.current = false;
      setSelectedScreenId(screenId);
      setDragState({ screenId, itemId, startX: e.clientX, startY: e.clientY });
    },
    []
  );

  useEffect(() => {
    if (!dragState) return;
    const { screenId, itemId, startX, startY } = dragState;
    let dragging = false;
    const handleMove = (e: MouseEvent) => {
      if (!dragging && (Math.abs(e.clientX - startX) > DRAG_THRESHOLD_PX || Math.abs(e.clientY - startY) > DRAG_THRESHOLD_PX)) {
        dragging = true;
        didDragRef.current = true;
        pushScreensHistory();
      }
      if (!dragging) return;
      const el = screenPreviewRefs.current[screenId];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      const top = Math.round(((e.clientY - rect.top) / rect.height) * 100);
      const clampedLeft = Math.max(0, Math.min(100, left));
      const clampedTop = Math.max(0, Math.min(100, top));
      setScreens(prev =>
        prev.map(s =>
          s.id === screenId
            ? {
                ...s,
                layoutItems: (s.layoutItems ?? []).map(it =>
                  it.id === itemId
                    ? ({ ...it, positionLeftPercent: clampedLeft, positionTopPercent: clampedTop } as LayoutItem)
                    : it
                ),
              }
            : s
        )
      );
    };
    const handleUp = () => {
      if (!didDragRef.current) {
        setSelectedLayoutItemId(itemId);
        setExpandedLayoutItemId(itemId);
      }
      setDragState(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragState, pushScreensHistory]);

  useEffect(() => {
    if (!resizeState) return;
    const { screenId, itemId, anchorLeftPercent, anchorTopPercent } = resizeState;
    const handleMove = (e: MouseEvent) => {
      const el = screenPreviewRefs.current[screenId];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cursorXPercent = ((e.clientX - rect.left) / rect.width) * 100;
      const cursorYPercent = ((e.clientY - rect.top) / rect.height) * 100;
      let newW = cursorXPercent - anchorLeftPercent;
      let newH = cursorYPercent - anchorTopPercent;
      newW = Math.max(5, Math.min(100 - anchorLeftPercent, newW));
      newH = Math.max(5, Math.min(100 - anchorTopPercent, newH));
      const newLeft = anchorLeftPercent + newW / 2;
      const newTop = anchorTopPercent + newH / 2;
      setScreens(prev =>
        prev.map(s =>
          s.id === screenId
            ? {
                ...s,
                layoutItems: (s.layoutItems ?? []).map(it =>
                  it.id === itemId && it.type === 'image'
                    ? {
                        ...it,
                        widthPercent: Math.round(newW),
                        heightPercent: Math.round(newH),
                        positionLeftPercent: newLeft,
                        positionTopPercent: newTop,
                      }
                    : it
                ),
              }
            : s
        )
      );
    };
    const handleUp = () => setResizeState(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizeState]);

  const uploadScreenImage = useCallback(
    async (file: File) => {
      validateScreenImageFile(file, t);
      setImageUploading(true);
      try {
        const result = await uploadStorageFile('app-screens', file);
        return result.path;
      } finally {
        setImageUploading(false);
      }
    },
    [t]
  );

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, screenId: string) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const path = await uploadScreenImage(file);
        updateScreenBackground(screenId, {
          type: 'image',
          imageUrl: path,
          imageFit: 'cover',
          imagePosition: 'center',
        });
      } catch (err) {
        showError(err instanceof Error ? err : new Error(t('appScreen.saveFailed')));
      }
    },
    [uploadScreenImage, updateScreenBackground, showError, t]
  );

  const handleDeviceInnerImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !deviceInnerBackgroundTarget) return;
      const { screenId, itemId } = deviceInnerBackgroundTarget;
      try {
        const path = await uploadScreenImage(file);
        updateLayoutItem(screenId, itemId, {
          background: {
            type: 'image',
            imageUrl: path,
            imageFit: 'cover',
            imagePosition: 'center',
          },
        });
        setDeviceInnerBackgroundTarget(null);
      } catch (err) {
        showError(err instanceof Error ? err : new Error(t('appScreen.saveFailed')));
      }
    },
    [deviceInnerBackgroundTarget, uploadScreenImage, updateLayoutItem, showError, t]
  );

  const handleLayoutImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !layoutImageUploadTarget) return;
      try {
        const path = await uploadScreenImage(file);
        updateLayoutItem(layoutImageUploadTarget.screenId, layoutImageUploadTarget.itemId, {
          imageUrl: path,
        });
        setLayoutImageUploadTarget(null);
      } catch (err) {
        showError(err instanceof Error ? err : new Error(t('appScreen.saveFailed')));
      }
    },
    [layoutImageUploadTarget, uploadScreenImage, updateLayoutItem, showError, t]
  );

  const uploadScreenVideo = useCallback(
    async (file: File, existingClips: VideoClip[]) => {
      const { defaultEnd } = await validateScreenVideoFile(file, t, existingClips);
      setImageUploading(true);
      try {
        const result = await uploadStorageFile('app-screens', file);
        return { path: result.path, defaultEnd } as const;
      } finally {
        setImageUploading(false);
      }
    },
    [t]
  );

  const handleScreenVideoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !screenVideoUploadTarget) return;
      const screen = screens.find(s => s.id === screenVideoUploadTarget);
      const existing = screen ? getScreenVideoClips(screen) : [];
      if (existing.length >= MAX_SCREEN_VIDEO_SOURCES) {
        showError(new Error(t('appScreen.videoMaxSources')));
        setScreenVideoUploadTarget(null);
        return;
      }
      try {
        const { path, defaultEnd } = await uploadScreenVideo(file, existing);
        const nextClips: VideoClip[] = [
          ...existing,
          { url: path, startSeconds: 0, endSeconds: defaultEnd },
        ];
        updateScreen(screenVideoUploadTarget, {
          videoClips: nextClips,
          videoUrls: nextClips.map(c => c.url),
        });
        setScreenVideoUploadTarget(null);
      } catch (err) {
        showError(err instanceof Error ? err : new Error(t('appScreen.saveFailed')));
      }
    },
    [screenVideoUploadTarget, screens, uploadScreenVideo, updateScreen, showError, t]
  );

  const removeScreenVideoAt = useCallback(
    (screenId: string, index: number) => {
      const screen = screens.find(s => s.id === screenId);
      if (!screen) return;
      const clips = getScreenVideoClips(screen).filter((_, i) => i !== index);
      updateScreen(screenId, {
        videoClips: clips.length > 0 ? clips : undefined,
        videoUrls: clips.length > 0 ? clips.map(c => c.url) : undefined,
      });
    },
    [screens, updateScreen]
  );

  const moveScreenVideo = useCallback(
    (screenId: string, fromIndex: number, toIndex: number) => {
      const screen = screens.find(s => s.id === screenId);
      if (!screen) return;
      const clips = [...getScreenVideoClips(screen)];
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= clips.length || toIndex >= clips.length) return;
      const [moved] = clips.splice(fromIndex, 1);
      clips.splice(toIndex, 0, moved);
      updateScreen(screenId, {
        videoClips: clips,
        videoUrls: clips.map(c => c.url),
      });
    },
    [screens, updateScreen]
  );

  const updateScreenVideoTrim = useCallback(
    (screenId: string, index: number, startSeconds: number, endSeconds: number) => {
      // Skip undo history while dragging the trim handles (high-frequency updates).
      setScreens(prev =>
        prev.map(s => {
          if (s.id !== screenId) return s;
          const clips = getScreenVideoClips(s).map((c, i) =>
            i === index ? { ...c, startSeconds, endSeconds } : c
          );
          return {
            ...s,
            videoClips: clips,
            videoUrls: clips.map(c => c.url),
          };
        })
      );
    },
    []
  );

  const filteredPresetImages = PRESET_IMAGES.filter(
    p =>
      !presetImageSearch.trim() ||
      p.label.toLowerCase().includes(presetImageSearch.toLowerCase()) ||
      p.tags.some(t => t.toLowerCase().includes(presetImageSearch.toLowerCase()))
  );

  const takeScreenshot = useCallback(async () => {
    const targetScreenId = selectedScreenId ?? screens[0]?.id;
    if (!targetScreenId) return;
    const screen = screens.find(s => s.id === targetScreenId);
    if (!screen || isVideoScreen(screen)) return;
    const { exportWidth, exportHeight } = currentScreenSize;
    try {
      await preloadScreenImages(collectImageRefsFromScreens([screen]));
      const canvas = document.createElement('canvas');
      canvas.width = exportWidth;
      canvas.height = exportHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      drawScreenToCanvas(ctx, screen, exportWidth, exportHeight, getImageForCanvas, {
        primaryLocale: localization,
        editingLocale,
      });
      if (!isPro) drawWatermarkOnCanvas(ctx, exportWidth, exportHeight, watermarkLogoImage);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png', 1));
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `screenshot-${currentScreenSize.id}-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export failed; user can retry
    }
  }, [selectedScreenId, screens, currentScreenSize, getImageForCanvas, isPro, watermarkLogoImage, localization, editingLocale]);

  const loading = Boolean(appId) && (contextAppLoading || (!app && !loadError));

  const screensTourSteps: OnboardingStep[] = [
    { target: '[data-tour="screens-version-select"]', title: t('onboarding.appScreens.step1Title'), body: t('onboarding.appScreens.step1Body') },
    { target: '[data-tour="screens-locale-select"]', title: t('onboarding.appScreens.step2Title'), body: t('onboarding.appScreens.step2Body') },
    { target: '[data-tour="screens-size-menu"]', title: t('onboarding.appScreens.step3Title'), body: t('onboarding.appScreens.step3Body') },
    { target: '[data-tour="screens-ai-tools"]', title: t('onboarding.appScreens.step4Title'), body: t('onboarding.appScreens.step4Body') },
    { target: '[data-tour="screens-publish"]', title: t('onboarding.appScreens.step5Title'), body: t('onboarding.appScreens.step5Body') },
    { target: '[data-tour="screens-add"]', title: t('onboarding.appScreens.step6Title'), body: t('onboarding.appScreens.step6Body') },
  ];

  return (
    <AppPageLayout
      appId={appId}
      app={app}
      loading={loading}
      error={loadError}
      pageTitle={
        currentScreenSet
          ? `${t('appScreen.pageTitle')} · ${currentScreenSet.name}`
          : t('appScreen.pageTitle')
      }
    >
      {loading ? null : appId && appNotMobile ? (
        <div className="flex flex-col items-center justify-center min-h-[40vh] px-4">
          <p className="text-gray-600 dark:text-gray-400 text-center mb-4">
            {t('appScreen.mobileOnlyMessage')}
          </p>
          <Link
            to={panelPath(`/applications/${appId}`)}
            className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
          >
            {t('appScreen.backToAppDetail')}
          </Link>
        </div>
      ) : (
    <div className="flex flex-col h-full min-w-0 max-w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 has-[[data-stuck]]:!rounded-t-none" data-app-id={appId ?? undefined}>
      {screensLoading ? (
        <>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-4">
            <SkeletonBar className="h-9 w-48 rounded-lg" />
            <SkeletonBar className="h-9 w-36 rounded-lg" />
            <div className="ml-auto flex gap-2">
              <SkeletonBar className="h-9 w-20 rounded-lg" />
              <SkeletonBar className="h-9 w-20 rounded-lg" />
              <SkeletonBar className="h-9 w-24 rounded-lg" />
            </div>
          </div>
          <div className="flex-1 min-w-0 overflow-x-auto overflow-y-auto p-6">
            <div className="flex items-start gap-4">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className="flex-shrink-0 rounded-2xl border-2 border-gray-200 dark:border-gray-600 overflow-hidden animate-pulse bg-gray-100 dark:bg-gray-700"
                  style={{
                    aspectRatio: `${currentScreenSize.width} / ${currentScreenSize.height}`,
                    minHeight: 'min(82vh, 800px)',
                    minWidth: 280,
                  }}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
      {((activeTranslationJob?.status === 'pending' || activeTranslationJob?.status === 'processing') ||
        (activeUploadJob?.status === 'pending' || activeUploadJob?.status === 'processing')) && (
        <div className="px-4 pt-4 space-y-2">
          {(activeTranslationJob?.status === 'pending' || activeTranslationJob?.status === 'processing') && (
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('appScreen.translationJobInProgress')}
                  </span>
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    %{activeTranslationJob.progress ?? 0}
                  </span>
                </div>
                <Link
                  to={panelPath(`/jobs/${activeTranslationJob.id}`)}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('appScreen.jobDetailLink')}
                </Link>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${activeTranslationJob.progress ?? 0}%` }}
                />
              </div>
            </div>
          )}
          {(activeUploadJob?.status === 'pending' || activeUploadJob?.status === 'processing') && (
            <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Upload className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('appScreen.uploadJobInProgress')}
                  </span>
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    %{activeUploadJob.progress ?? 0}
                  </span>
                </div>
                <Link
                  to={panelPath(`/jobs/${activeUploadJob.id}`)}
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('appScreen.jobDetailLink')}
                </Link>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${activeUploadJob.progress ?? 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {(reviewErrors.length > 0 || reviewWarnings.length > 0) && (
        <div className="px-4 pt-4 space-y-2">
          {reviewErrors.length > 0 && (
            <div className="rounded-xl border border-red-200 dark:border-red-800/60 bg-red-50/80 dark:bg-red-950/30 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-red-200/80 dark:border-red-800/50">
                <h2 className="text-sm font-semibold text-red-900 dark:text-red-100 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('appScreen.reviewErrorsTitle')}
                </h2>
                <p className="mt-0.5 text-xs text-red-800/80 dark:text-red-200/70">
                  {t('appScreen.reviewErrorsHint')}
                </p>
              </div>
              <ul className="divide-y divide-red-200/70 dark:divide-red-800/40">
                {reviewErrors.map(issue => (
                  <li key={issue.id}>
                    <button
                      type="button"
                      onClick={() => focusReviewIssue(issue)}
                      className="w-full text-left px-4 py-2.5 hover:bg-red-100/70 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <p className="text-sm text-red-950 dark:text-red-50">{issue.message}</p>
                      {(issue.guideline || issue.locale) && (
                        <p className="mt-0.5 text-xs font-mono text-red-800/80 dark:text-red-200/70">
                          {[issue.guideline ? `Guideline ${issue.guideline}` : null, issue.locale]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {reviewWarnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-950/30 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-amber-200/80 dark:border-amber-800/50">
                <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('appScreen.reviewWarningsTitle')}
                </h2>
                <p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-200/70">
                  {t('appScreen.reviewWarningsHint')}
                </p>
              </div>
              <ul className="divide-y divide-amber-200/70 dark:divide-amber-800/40">
                {reviewWarnings.map(issue => (
                  <li key={issue.id}>
                    <button
                      type="button"
                      onClick={() => focusReviewIssue(issue)}
                      className="w-full text-left px-4 py-2.5 hover:bg-amber-100/70 dark:hover:bg-amber-900/30 transition-colors"
                    >
                      <p className="text-sm text-amber-950 dark:text-amber-50">{issue.message}</p>
                      {(issue.guideline || issue.locale) && (
                        <p className="mt-0.5 text-xs font-mono text-amber-800/80 dark:text-amber-200/70">
                          {[issue.guideline ? `Guideline ${issue.guideline}` : null, issue.locale]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <OnboardingTour tourId="app-screens" steps={screensTourSteps} />
      <PageToolbar>
        <div className="flex items-center gap-2" ref={versionMenuRef} data-tour="screens-version-select">
          <div className="relative">
            <button
              type="button"
              id="app-screens-version"
              aria-haspopup="listbox"
              aria-expanded={versionMenuOpen}
              disabled={screenSetsLoading}
              onClick={() => setVersionMenuOpen(o => !o)}
              title={t('appScreen.versionSelectTitle')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80 disabled:opacity-50"
            >
              <Tag className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
              <span className="font-medium truncate max-w-[10rem]">
                {screenSetsLoading
                  ? '…'
                  : currentScreenSet
                    ? currentScreenSet.name
                    : t('appScreen.noVersion')}
              </span>
              <ChevronDown className={clsx('h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform', versionMenuOpen && 'rotate-180')} />
            </button>
            {versionMenuOpen && (
              <div
                role="listbox"
                aria-labelledby="app-screens-version"
                className="absolute left-0 top-full z-40 mt-1 w-56 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg overflow-hidden"
              >
                <div className="max-h-56 overflow-y-auto py-1">
                  {screenSets.map(s => (
                    <div
                      key={s.id}
                      className={clsx(
                        'flex items-center gap-0.5 pr-1',
                        s.id === screensId
                          ? 'bg-primary-50 dark:bg-primary-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/80'
                      )}
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected={s.id === screensId}
                        onClick={() => selectVersion(s.id)}
                        className={clsx(
                          'min-w-0 flex-1 px-3 py-1.5 text-left text-sm transition-colors truncate',
                          s.id === screensId
                            ? 'text-primary-700 dark:text-primary-300'
                            : 'text-gray-800 dark:text-gray-100'
                        )}
                      >
                        {s.name}
                      </button>
                      <button
                        type="button"
                        title={t('appScreen.deleteVersion')}
                        aria-label={t('appScreen.deleteVersion')}
                        disabled={deletingScreensId === s.id}
                        onClick={e => void handleDeleteScreensSet(s.id, s.name, e)}
                        className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                      >
                        {deletingScreensId === s.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                  {screenSets.length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{t('appScreen.noVersion')}</p>
                  )}
                </div>
                <div className="border-t border-gray-100 dark:border-gray-700 p-1">
                  <button
                    type="button"
                    onClick={openNewVersionModal}
                    className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('appScreen.newVersion')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div data-tour="screens-locale-select">
          <LocaleSelectMenu
            id="app-screens-locale"
            value={currentEditingLocale}
            onChange={setEditingLocale}
            preferredLocales={[localization, ...targetLocales].filter(Boolean)}
            primaryLocale={localization}
          />
        </div>
        <div className="flex items-center gap-2" ref={sizeMenuRef} data-tour="screens-size-menu">
          <div className="relative">
            <button
              type="button"
              id="app-screens-size"
              onClick={() => {
                setSizeMenuOpen(open => !open);
              }}
              aria-label={t('appScreen.screenSize')}
              aria-expanded={sizeMenuOpen}
              aria-haspopup="listbox"
              title={currentScreenSize.label}
              className="inline-flex items-center gap-1.5 h-8 pl-2 pr-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {currentScreenSize.group === 'ipad' ? (
                <Tablet className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
              ) : (
                <Smartphone className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
              )}
              <span className="tabular-nums tracking-tight">{currentScreenSize.diagonal}</span>
              <span className="text-xs font-normal tabular-nums text-gray-500 dark:text-gray-400">
                {currentScreenSize.width}×{currentScreenSize.height}
              </span>
              <ChevronDown className={clsx('h-3.5 w-3.5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform', sizeMenuOpen && 'rotate-180')} />
            </button>
            {sizeMenuOpen && (
              <div
                role="listbox"
                aria-labelledby="app-screens-size"
                className="absolute left-0 top-full z-40 mt-1 w-64 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg overflow-hidden"
              >
                <div className="py-1">
                  {screenSizeGroups.map((group, groupIndex) => (
                    <div key={group.key}>
                      <div
                        className={clsx(
                          'px-2.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500',
                          groupIndex === 0 ? 'pt-1' : 'pt-2'
                        )}
                      >
                        {t(group.labelKey)}
                      </div>
                      {group.presets.map(preset => {
                        const selected = preset.id === selectedScreenSizeId;
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              setSelectedScreenSizeId(preset.id);
                              setSizeMenuOpen(false);
                            }}
                            className={clsx(
                              'w-full grid grid-cols-[1.25rem_3.25rem_minmax(0,1fr)] items-center gap-2 px-2.5 py-1.5 text-left text-sm transition-colors',
                              selected
                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/80'
                            )}
                          >
                            <group.Icon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 justify-self-center" aria-hidden />
                            <span className="font-medium tabular-nums tracking-tight">{preset.diagonal}</span>
                            <span className="tabular-nums text-xs text-gray-500 dark:text-gray-400">
                              {preset.width}×{preset.height}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 ml-auto min-w-0">
          {isPro && !canUpload && (
            <span
              className="text-xs text-amber-600 dark:text-amber-400"
              title={
                reviewErrors.length > 0
                  ? t('appScreen.uploadDisabledReviewErrors')
                  : t('appScreen.uploadDisabledTargetsIncomplete')
              }
            >
              {reviewErrors.length > 0
                ? t('appScreen.uploadNoteReviewErrors')
                : t('appScreen.uploadNoteOngoingTranslation')}
            </span>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Edit tools */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={openScreenOrderModal}
                disabled={screens.length < 2}
                title={t('appScreen.reorderScreensTitle')}
                className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg border border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 text-sm font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ListOrdered className="h-4 w-4 shrink-0" />
                <span className="hidden xl:inline">{t('appScreen.reorderScreens')}</span>
              </button>
              <button
                type="button"
                onClick={undoScreens}
                disabled={!canUndo}
                title={t('appScreen.undoTitle')}
                className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg border border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80 hover:border-gray-200 dark:hover:border-gray-600 text-sm font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <Undo2 className="h-4 w-4 shrink-0" />
                <span className="hidden xl:inline">{t('appScreen.undo')}</span>
              </button>
            </div>

            <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />

            {/* AI tools — emphasized cluster */}
            <div data-tour="screens-ai-tools" className="flex items-center gap-1 rounded-xl p-1 bg-primary-50 dark:bg-primary-950/50 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70">
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    goToUpgrade();
                    return;
                  }
                  openAiTemplateModal();
                }}
                title={isPro ? t('appScreen.aiTemplateTitle') : t('proFeature.requiresPro')}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                  isPro
                    ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400'
                    : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                )}
              >
                <Sparkles className="h-4 w-4 shrink-0" />
                {t('appScreen.aiTemplate')}
                {!isPro && <ProTag />}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    goToUpgrade();
                    return;
                  }
                  openAiTranslationModal();
                }}
                title={isPro ? t('appScreen.selectTargetLanguagesTitle') : t('proFeature.requiresPro')}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                  isPro
                    ? 'bg-white dark:bg-gray-900/80 text-primary-700 dark:text-primary-300 border border-primary-300/80 dark:border-primary-700 hover:bg-primary-100/80 dark:hover:bg-primary-900/40'
                    : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                )}
              >
                <Languages className="h-4 w-4 shrink-0" />
                {t('appScreen.aiTranslation')}
                {!isPro && <ProTag />}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    goToUpgrade();
                    return;
                  }
                  setAiChatOpen(true);
                }}
                title={isPro ? t('appScreen.aiChatTitle') : t('proFeature.requiresPro')}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                  isPro
                    ? 'bg-white dark:bg-gray-900/80 text-primary-700 dark:text-primary-300 border border-primary-300/80 dark:border-primary-700 hover:bg-primary-100/80 dark:hover:bg-primary-900/40'
                    : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                )}
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">{t('appScreen.aiChat')}</span>
                {!isPro && <ProTag />}
              </button>
            </div>

            <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />

            {/* Publish */}
            <div data-tour="screens-publish" className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={takeScreenshot}
                disabled={screens.length === 0}
                title={t('appScreen.downloadScreenshotTitle')}
                className="inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <Download className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">{t('appScreen.export')}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isPro) {
                    goToUpgrade();
                    return;
                  }
                  if (!canUpload) return;
                  openUploadModal();
                }}
                disabled={isPro && !canUpload}
                title={
                  !isPro
                    ? undefined
                    : reviewErrors.length > 0
                      ? t('appScreen.uploadDisabledReviewErrors')
                      : !canUpload && targetLocales.length > 0
                        ? t('appScreen.uploadDisabledTargetsIncomplete')
                        : t('appScreen.uploadToStoresTitle')
                }
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none',
                  isPro && canUpload
                    ? 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                    : 'border border-gray-300 dark:border-gray-500 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                <Upload className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">{t('appScreen.upload')}</span>
                {!isPro && <ProTag />}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!appId || !hasUnsavedChanges || saveStatus === 'loading'}
                title={!hasUnsavedChanges ? t('appScreen.noUnsavedChanges') : undefined}
                className={clsx(
                  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none',
                  saveStatus === 'success'
                    ? 'border border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : saveStatus === 'error'
                      ? 'border border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : 'text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm'
                )}
              >
                <Save className="h-4 w-4 shrink-0" />
                {saveStatus === 'loading'
                  ? t('panel.saving')
                  : saveStatus === 'success'
                    ? (lastUploadJobId ? t('appScreen.jobStarted', { id: lastUploadJobId.slice(0, 8) + '…' }) : t('appScreen.saved'))
                    : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </PageToolbar>

      <div className="flex-1 min-w-0 overflow-x-auto overflow-y-auto p-6">
        {screens.length === 0 && !screensLoading && (
          <EmptyState
            icon={<Image className="h-8 w-8 text-gray-400 dark:text-gray-500" />}
            title={t('onboarding.appScreens.emptyTitle')}
            body={t('onboarding.appScreens.emptyBody')}
          />
        )}
        <div className="flex items-start gap-4 h-full min-h-0 w-max min-w-full">
          {screens.map(screen => {
            const isSelected = selectedScreenId === screen.id;
            const screenVisible = isScreenVisible(screen);
            const bounds = itemBoundsByScreenId[screen.id] ?? [];
            const hitTest = (offsetX: number, offsetY: number) => {
              for (let i = bounds.length - 1; i >= 0; i--) {
                const b = bounds[i];
                if (offsetX >= b.left && offsetX <= b.left + b.width && offsetY >= b.top && offsetY <= b.top + b.height)
                  return b.id;
              }
              return null;
            };
            const selectedItemBound = selectedLayoutItemId && isSelected ? bounds.find(b => b.id === selectedLayoutItemId) : null;
            const selectedItem = selectedLayoutItemId ? screen.layoutItems?.find(l => l.id === selectedLayoutItemId) : null;
            const isSelectedImage = selectedItem?.type === 'image';
            const screenInnerStyle: React.CSSProperties = {
              aspectRatio: `${currentScreenSize.width} / ${currentScreenSize.height}`,
              minHeight: 'min(82vh, 800px)',
              maxHeight: '98vh',
              ...(screen.background?.type === 'color'
                ? { backgroundColor: screen.background.color }
                : {}),
              ...(screen.background?.type === 'image'
                ? {
                    backgroundImage: `url(${resolveStorageUrl(screen.background.imageUrl)})`,
                    backgroundSize: screen.background.imageFit === 'fill' ? '100% 100%' : screen.background.imageFit,
                    backgroundPosition: screen.background.imagePosition,
                  }
                : {}),
            };
            return (
              <React.Fragment key={screen.id}>
                <div
                  ref={el => { screenPreviewRefs.current[screen.id] = el; }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && (setSelectedScreenId(screen.id), setSelectedLayoutItemId(null))}
                  onClick={() => {
                    setSelectedScreenId(screen.id);
                    if (isVideoScreen(screen)) setSelectedLayoutItemId(null);
                  }}
                  className={clsx(
                    'flex-shrink-0 rounded-2xl border-2 transition-colors text-left relative overflow-hidden outline-none flex flex-col items-stretch justify-center',
                    isSelected
                      ? 'border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-800/50'
                      : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-500',
                    !screenVisible && 'opacity-50'
                  )}
                  style={
                    isVideoScreen(screen)
                      ? {
                          aspectRatio: `${currentScreenSize.width} / ${currentScreenSize.height}`,
                          minHeight: 'min(82vh, 800px)',
                          maxHeight: '98vh',
                          backgroundColor: '#0f172a',
                        }
                      : screenInnerStyle
                  }
                >
                  {!screenVisible && (
                    <span className="absolute top-3 right-3 z-20 rounded-md bg-amber-500/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white pointer-events-none flex items-center gap-1">
                      <EyeOff className="h-3 w-3" />
                      {t('appScreen.hidden')}
                    </span>
                  )}
                  {isVideoScreen(screen) ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950 overflow-hidden">
                      <VideoScreenCanvasPreview clips={getScreenVideoClips(screen)} />
                    </div>
                  ) : (
                  <>
                  <canvas
                    ref={el => { canvasRefs.current[screen.id] = el; }}
                    className="block w-full h-full cursor-move"
                    style={{ display: 'block', width: '100%', height: '100%' }}
                    onMouseDown={e => {
                      const x = e.nativeEvent.offsetX;
                      const y = e.nativeEvent.offsetY;
                      const itemId = hitTest(x, y);
                      setSelectedScreenId(screen.id);
                      if (itemId) {
                        handleLayoutItemMouseDown(e as unknown as React.MouseEvent, screen.id, itemId);
                      } else {
                        setSelectedLayoutItemId(null);
                      }
                    }}
                  />
                  <span className="absolute top-3 left-3 z-10 rounded-md bg-black/50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white pointer-events-none">
                    {t('appScreen.screenTypeImage')}
                  </span>
                  {(() => {
                    const size = previewSizeByScreenId[screen.id];
                    if (!size || size.width <= 0 || size.height <= 0) return null;
                    return (bounds as ItemBound[])
                      .filter(b => {
                        const it = screen.layoutItems?.find(l => l.id === b.id);
                        return it?.type === 'text' && isHtmlContent(getTextForLocale(it, editingLocale));
                      })
                      .map(b => {
                        const item = screen.layoutItems?.find(l => l.id === b.id) as Extract<LayoutItem, { type: 'text' }> | undefined;
                        if (!item || item.type !== 'text') return null;
                        const displayText = getTextForLocale(item, editingLocale);
                        const p = getTextPaddingPercents(item);
                        // Canvas/export ile aynı: padding ekran w/h oranına göre (kutu boyutuna değil).
                        const padT = (size.height * p.top) / 100;
                        const padR = (size.width * p.right) / 100;
                        const padB = (size.height * p.bottom) / 100;
                        const padL = (size.width * p.left) / 100;
                        const fontFamily = item.font === 'system' ? 'sans-serif' : item.font;
                        const baseFontSize = getTextSizePxForHeight(item, size.height);
                        let fontSize = baseFontSize;
                        // Canvas ile aynı bütçe: kutu komşusunun üstüne binmesin diye küçült.
                        const measureCtx = document.createElement('canvas').getContext('2d');
                        if (measureCtx) {
                          const items = ensureLayoutItemIndices(screen.layoutItems ?? []);
                          const designRects = buildDesignRects(
                            measureCtx,
                            size.width,
                            size.height,
                            items,
                            editingLocale,
                            localization
                          );
                          const measured = measureTextItem(measureCtx, size.width, size.height, item, displayText, baseFontSize);
                          const limitY = textItemVerticalLimit(size.height, item, measured, designRects);
                          if (measured.boxTop + measured.boxH > limitY) {
                            fontSize = fitTextItemToHeight(
                              measureCtx,
                              size.width,
                              size.height,
                              item,
                              displayText,
                              baseFontSize,
                              limitY - measured.boxTop
                            );
                          }
                        }
                        const isThisSelected = selectedLayoutItemId === item.id;
                        const fullWidth = isTextFullWidth(item);
                        return (
                          <div
                            key={b.id}
                            className="absolute pointer-events-none overflow-visible box-border"
                            style={{
                              left: b.left,
                              top: b.top,
                              minWidth: b.width,
                              width: fullWidth ? b.width : 'max-content',
                              height: b.height,
                              zIndex: layoutItemIndex(item),
                            }}
                          >
                            <div
                              className="app-screen-html-overlay h-full box-border"
                              style={{
                                width: fullWidth ? '100%' : 'max-content',
                                maxWidth: fullWidth ? '100%' : size.width - padL - padR,
                                paddingTop: padT,
                                paddingRight: padR,
                                paddingBottom: padB,
                                paddingLeft: padL,
                                fontSize,
                                fontFamily,
                                color: item.textColor || '#000000',
                                // Arka plan + radius canvas'ta çizilir; burada sadece metin inset.
                                backgroundColor: 'transparent',
                                lineHeight: 1.2,
                                textAlign: fullWidth || getMinWidthPercent(item) > 0 ? 'center' : 'left',
                                // Content-fit: kelime kırıp alta atma — yana uzasın (ekran kenarına kadar).
                                overflowWrap: fullWidth ? 'break-word' : 'normal',
                                wordBreak: fullWidth ? 'break-word' : 'normal',
                                whiteSpace: 'pre-line',
                              }}
                              dangerouslySetInnerHTML={{ __html: displayText || '' }}
                              aria-hidden
                            />
                            {isThisSelected && (
                              <>
                                <div
                                  className="absolute inset-0 border-4 border-dashed border-red-500 pointer-events-none rounded z-0"
                                  aria-hidden
                                />
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); e.preventDefault(); removeLayoutItem(screen.id, item.id); setSelectedLayoutItemId(null); }}
                                  className="absolute z-10 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600 shadow border border-white dark:border-gray-800 pointer-events-auto"
                                  style={{ right: -12, top: -4 }}
                                  title={t('appScreen.remove')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      });
                  })()}
                  {selectedItemBound && (() => {
                    const selectedIsHtmlText = selectedItem?.type === 'text' && isHtmlContent(getTextForLocale(selectedItem, editingLocale));
                    if (selectedIsHtmlText) return null;
                    return (
                    <>
                      <div
                        className="absolute border-4 border-dashed border-red-500 pointer-events-none rounded z-0"
                        style={{
                          left: selectedItemBound.left,
                          top: selectedItemBound.top,
                          width: selectedItemBound.width,
                          height: selectedItemBound.height,
                          boxSizing: 'border-box',
                        }}
                        aria-hidden
                      />
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); e.preventDefault(); removeLayoutItem(screen.id, selectedLayoutItemId!); setSelectedLayoutItemId(null); }}
                        className="absolute z-10 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600 shadow border border-white dark:border-gray-800"
                        style={{
                          left: selectedItemBound.left + selectedItemBound.width - 12,
                          top: selectedItemBound.top - 4,
                        }}
                        title={t('appScreen.remove')}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                      {isSelectedImage && selectedItem && 'widthPercent' in selectedItem && (
                        <span
                          role="button"
                          tabIndex={0}
                          className="absolute w-4 h-4 cursor-se-resize border-4 border-dashed border-red-500 bg-red-500/20 rounded-tl pointer-events-auto z-10"
                          style={{
                            left: selectedItemBound.left + selectedItemBound.width - 16,
                            top: selectedItemBound.top + selectedItemBound.height - 16,
                          }}
                          title={t('appScreen.resizeImage')}
                          onMouseDown={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            pushScreensHistory();
                            const left = (selectedItem.positionLeftPercent ?? 50);
                            const top = (selectedItem.positionTopPercent ?? 50);
                            const wPct = (selectedItem as Extract<LayoutItem, { type: 'image' }>).widthPercent ?? 40;
                            const hPct = (selectedItem as Extract<LayoutItem, { type: 'image' }>).heightPercent ?? 30;
                            setResizeState({
                              screenId: screen.id,
                              itemId: selectedItem.id,
                              anchorLeftPercent: left - wPct / 2,
                              anchorTopPercent: top - hPct / 2,
                            });
                          }}
                        />
                      )}
                    </>
                    );
                  })()}
                  </>
                  )}
                </div>
                {isSelected && (
                  <div
                      className="flex-shrink-0 w-96 min-w-[22rem] rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 flex flex-col shadow-lg overflow-hidden self-start"
                      style={{
                        height: previewSizeByScreenId[screen.id]?.height
                          ? `${previewSizeByScreenId[screen.id].height}px`
                          : undefined,
                        maxHeight: previewSizeByScreenId[screen.id]?.height
                          ? `${previewSizeByScreenId[screen.id].height}px`
                          : 'min(82vh, 800px)',
                      }}
                    >
                    <div className="flex-shrink-0 px-2 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
                      <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={closePanel}
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-400 dark:hover:bg-gray-700"
                        title={t('common.close')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={duplicateScreen}
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-400 dark:hover:bg-gray-700"
                        title={t('appScreen.duplicate')}
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      </div>
                      <div className="mx-1 flex items-center rounded-lg p-0.5 bg-primary-50 dark:bg-primary-950/50 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70">
                      <button
                        type="button"
                        onClick={() => openAiRedesignModal(screen.id)}
                        disabled={isVideoScreen(screen)}
                        className={clsx(
                          'p-1.5 rounded-md',
                          isVideoScreen(screen)
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : !isPro
                              ? 'text-gray-400 dark:text-gray-500 hover:bg-white/70 dark:hover:bg-gray-800/60'
                              : 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400'
                        )}
                        title={
                          isVideoScreen(screen)
                            ? t('appScreen.videoScreenNoLayout')
                            : isPro
                              ? t('appScreen.aiRedesignTitle')
                              : t('appScreen.aiRedesignProOnly')
                        }
                      >
                        <Sparkles className="h-4 w-4" />
                        {!isPro && <span className="sr-only">{t('appScreen.aiRedesign')}</span>}
                      </button>
                      </div>
                      <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => updateScreen(screen.id, { visible: !screenVisible })}
                        className={clsx(
                          'p-2 rounded-lg',
                          screenVisible
                            ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-400 dark:hover:bg-gray-700'
                            : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/20'
                        )}
                        title={screenVisible ? t('appScreen.hide') : t('appScreen.show')}
                      >
                        {screenVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={takeScreenshot}
                        disabled={isVideoScreen(screen)}
                        title={isVideoScreen(screen) ? t('appScreen.videoScreenNoLayout') : t('appScreen.downloadScreenshot')}
                        className={clsx(
                          'p-2 rounded-lg',
                          isVideoScreen(screen)
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-400 dark:hover:bg-gray-700'
                        )}
                      >
                        <Camera className="h-4 w-4" />
                      </button>
                      </div>
                      <button
                        type="button"
                        onClick={deleteScreen}
                        className="ml-auto p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
                      {isVideoScreen(screen) ? (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                          <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 flex items-center gap-2">
                            <Video className="h-4 w-4" />
                            {t('appScreen.screenTypeVideo')}
                          </div>
                          <div className="p-3 space-y-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('appScreen.videoHint')}</p>
                            {(() => {
                              const clips = getScreenVideoClips(screen);
                              const trimBudgetUsed = (exceptIndex: number) =>
                                clips.reduce((sum, c, i) => {
                                  if (i === exceptIndex) return sum;
                                  return sum + clipTrimDuration(c);
                                }, 0);
                              return (
                                <>
                                  {clips.length > 0 ? (
                                    <ul className="space-y-3">
                                      {clips.map((clip, index) => {
                                        const resolved = resolveStorageUrl(clip.url);
                                        const start = Math.max(0, clip.startSeconds ?? 0);
                                        const end = typeof clip.endSeconds === 'number' && clip.endSeconds > start
                                          ? clip.endSeconds
                                          : undefined;
                                        const otherUsed = trimBudgetUsed(index);
                                        const maxSelectable = Math.max(
                                          MIN_SCREEN_VIDEO_TRIM_SECONDS,
                                          MAX_SCREEN_VIDEO_SECONDS - otherUsed
                                        );
                                        return (
                                          <li
                                            key={`${clip.url}-${index}`}
                                            className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-900"
                                          >
                                            <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-600">
                                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                {t('appScreen.videoSourceLabel', { number: index + 1 })}
                                              </span>
                                              <div className="flex items-center gap-0.5">
                                                <button
                                                  type="button"
                                                  disabled={index === 0}
                                                  onClick={() => moveScreenVideo(screen.id, index, index - 1)}
                                                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"
                                                  title={t('common.moveUp')}
                                                >
                                                  <ArrowUp className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                  type="button"
                                                  disabled={index === clips.length - 1}
                                                  onClick={() => moveScreenVideo(screen.id, index, index + 1)}
                                                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"
                                                  title={t('common.moveDown')}
                                                >
                                                  <ArrowDown className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => removeScreenVideoAt(screen.id, index)}
                                                  className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                  title={t('common.delete')}
                                                >
                                                  <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                              </div>
                                            </div>
                                            {resolved ? (
                                              <div className="bg-black aspect-video">
                                                <TrimmedClipPreview
                                                  src={resolved}
                                                  startSeconds={start}
                                                  endSeconds={end}
                                                />
                                              </div>
                                            ) : null}
                                            {resolved ? (
                                              <div className="p-2.5 border-t border-gray-200 dark:border-gray-700">
                                                <VideoClipTrimmer
                                                  src={resolved}
                                                  startSeconds={start}
                                                  endSeconds={end ?? 0}
                                                  maxSelectableSeconds={maxSelectable}
                                                  onChange={({ startSeconds, endSeconds }) =>
                                                    updateScreenVideoTrim(screen.id, index, startSeconds, endSeconds)
                                                  }
                                                />
                                              </div>
                                            ) : null}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setScreenVideoUploadTarget(screen.id);
                                      screenVideoInputRef.current?.click();
                                    }}
                                    disabled={imageUploading || clips.length >= MAX_SCREEN_VIDEO_SOURCES}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {imageUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    {clips.length === 0
                                      ? t('appScreen.uploadVideo')
                                      : t('appScreen.uploadAnotherVideo', { remaining: MAX_SCREEN_VIDEO_SOURCES - clips.length })}
                                  </button>
                                </>
                              );
                            })()}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.videoDeviceType')}</label>
                              <select
                                value={screen.deviceType || previewDeviceTypeFromScreenSizeId(selectedScreenSizeId)}
                                onChange={e => updateScreen(screen.id, { deviceType: e.target.value })}
                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                              >
                                {VIDEO_DEVICE_TYPE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ) : (
                      <>
                      <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600">
                          {t('appScreen.layout')}
                        </div>
                        <div className="p-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => addLayoutItem(screen.id, 'text')}
                            className="flex-1 min-w-[4.5rem] flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            <Type className="h-4 w-4" />
                            {t('appScreen.text')}
                          </button>
                          <button
                            type="button"
                            onClick={() => addLayoutItem(screen.id, 'image')}
                            className="flex-1 min-w-[4.5rem] flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            <Image className="h-4 w-4" />
                            {t('appScreen.image')}
                          </button>
                          <button
                            type="button"
                            onClick={() => addLayoutItem(screen.id, 'device')}
                            className="flex-1 min-w-[4.5rem] flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            <Smartphone className="h-4 w-4" />
                            {t('appScreen.device')}
                          </button>
                        </div>
                        {(screen.layoutItems?.length ?? 0) > 0 && (
                          <ul className="mt-3 space-y-3">
                            {screen.layoutItems?.map((item, index) => {
                              const isExpanded = expandedLayoutItemId === item.id;
                              const isItemSelectedInPanel = selectedLayoutItemId === item.id;
                              const isDragging = layoutListDrag?.screenId === screen.id && layoutListDrag?.itemId === item.id;
                              const itemVisible = isLayoutItemVisible(item);
                              return (
                              <li
                                key={item.id}
                                ref={el => { layoutItemPanelRefs.current[item.id] = el; }}
                                data-layout-index={index}
                                draggable={false}
                                onDragOver={e => {
                                  if (!layoutListDrag || layoutListDrag.screenId !== screen.id) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={e => {
                                  e.preventDefault();
                                  if (!layoutListDrag || layoutListDrag.screenId !== screen.id) return;
                                  const fromIndex = screen.layoutItems?.findIndex(l => l.id === layoutListDrag.itemId) ?? -1;
                                  if (fromIndex === -1) return;
                                  const toIndex = index;
                                  if (fromIndex !== toIndex) reorderLayoutItems(screen.id, fromIndex, toIndex);
                                  setLayoutListDrag(null);
                                }}
                                className={clsx(
                                  'rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 overflow-hidden',
                                  isExpanded && 'max-h-[min(55vh,480px)] flex flex-col',
                                  isItemSelectedInPanel && 'ring-2 ring-primary-500 border-primary-400',
                                  (isDragging || !itemVisible) && 'opacity-50'
                                )}
                              >
                                <div className="flex items-stretch flex-shrink-0">
                                  <span
                                    draggable
                                    onDragStart={e => {
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('application/json', JSON.stringify({ screenId: screen.id, itemId: item.id }));
                                      setLayoutListDrag({ screenId: screen.id, itemId: item.id });
                                    }}
                                    onDragEnd={() => setLayoutListDrag(null)}
                                    className="flex items-center px-1.5 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0 touch-none"
                                    title={t('appScreen.dragToReorder')}
                                  >
                                    <GripVertical className="h-4 w-4" />
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = expandedLayoutItemId === item.id ? null : item.id;
                                      setExpandedLayoutItemId(next);
                                      setSelectedLayoutItemId(next);
                                    }}
                                    className="flex-1 flex items-center gap-2 py-2 px-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors min-w-0"
                                  >
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize flex items-center gap-1.5 min-w-0">
                                      {isExpanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                                      {item.type === 'text' ? (
                                        <><Type className="h-3.5 w-3.5 shrink-0" />{t('appScreen.text')}</>
                                      ) : item.type === 'image' ? (
                                        <><Image className="h-3.5 w-3.5 shrink-0" />{t('appScreen.image')}</>
                                      ) : (
                                        <><Smartphone className="h-3.5 w-3.5 shrink-0" />{t('appScreen.device')}</>
                                      )}
                                    </span>
                                  </button>
                                  <div className="flex items-center gap-0.5 pr-1.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        updateLayoutItem(screen.id, item.id, { visible: !itemVisible });
                                      }}
                                      className={clsx(
                                        'p-1 rounded shrink-0',
                                        itemVisible
                                          ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700/50'
                                          : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/20'
                                      )}
                                      title={itemVisible ? t('appScreen.hide') : t('appScreen.show')}
                                    >
                                      {itemVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        duplicateLayoutItem(screen.id, item.id);
                                      }}
                                      className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700/50 shrink-0"
                                      title={t('appScreen.duplicate')}
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        removeLayoutItem(screen.id, item.id);
                                        setExpandedLayoutItemId(prev => (prev === item.id ? null : prev));
                                        setSelectedLayoutItemId(prev => (prev === item.id ? null : prev));
                                      }}
                                      className="p-1 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 shrink-0"
                                      title={t('appScreen.remove')}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                                {isExpanded && item.type === 'text' && (
                                  <div className="p-3 space-y-2 min-h-0 overflow-y-auto flex-1 border-t border-gray-200 dark:border-gray-600">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.textContent')}</label>
                                      <RichTextEditor
                                        value={getTextForLocale(item, editingLocale)}
                                        onChange={v => updateTextForLocale(screen.id, item.id, editingLocale, v)}
                                        onFocus={pushHistoryBeforeTextEdit}
                                        placeholder={t('appScreen.textContent')}
                                        textColor={item.textColor}
                                        onTextColorChange={v => updateLayoutItem(screen.id, item.id, { textColor: v })}
                                        backgroundColor={item.backgroundColor}
                                        onBackgroundColorChange={v => updateLayoutItem(screen.id, item.id, { backgroundColor: v })}
                                        className="[&_.ql-toolbar]:min-h-0 [&_.ql-toolbar_.ql-picker]:text-xs [&_.ql-toolbar_button]:!w-6 [&_.ql-container]:min-h-[80px] [&_.ql-editor]:min-h-[80px] [&_.ql-editor]:text-sm"
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="w-20">
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.sizePt')}</label>
                                        <input
                                          type="number"
                                          min={6}
                                          max={200}
                                          step={1}
                                          value={textSizeDraft?.itemId === item.id ? textSizeDraft.value : String(getTextSize(item))}
                                          onFocus={() => setTextSizeDraft({ itemId: item.id, value: String(getTextSize(item)) })}
                                          onChange={e => {
                                            const raw = e.target.value;
                                            setTextSizeDraft({ itemId: item.id, value: raw });
                                            if (raw.trim() === '') return;
                                            const n = Number(raw);
                                            if (!Number.isFinite(n)) return;
                                            const v = Math.round(n);
                                            if (v < 6 || v > 200) return;
                                            updateLayoutItem(screen.id, item.id, { textSize: v });
                                          }}
                                          onBlur={e => {
                                            const raw = e.target.value.trim();
                                            const n = Number(raw);
                                            const v =
                                              Number.isFinite(n) && raw !== ''
                                                ? Math.max(6, Math.min(200, Math.round(n)))
                                                : getTextSize(item);
                                            updateLayoutItem(screen.id, item.id, { textSize: v });
                                            setTextSizeDraft(null);
                                          }}
                                          onKeyDown={e => {
                                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                          }}
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          title={t('appScreen.sizePtTitle')}
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('appScreen.backgroundPaddingPercent')}</span>
                                      <div className="grid grid-cols-4 gap-1">
                                        <div>
                                          <label className="sr-only">{t('appScreen.paddingTop')}</label>
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.1}
                                            placeholder={t('appScreen.paddingTop')}
                                            value={getTextPaddingPercents(item).top}
                                            onChange={e => updateLayoutItem(screen.id, item.id, { textPaddingTopPercent: roundRatio(Math.max(0, Math.min(1, (Number(e.target.value) || 0) / 100)), 6) })}
                                            className="w-full px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            title={t('appScreen.paddingTop') + ' %'}
                                          />
                                        </div>
                                        <div>
                                          <label className="sr-only">{t('appScreen.paddingRight')}</label>
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.1}
                                            placeholder={t('appScreen.paddingRight')}
                                            value={getTextPaddingPercents(item).right}
                                            onChange={e => updateLayoutItem(screen.id, item.id, { textPaddingRightPercent: roundRatio(Math.max(0, Math.min(1, (Number(e.target.value) || 0) / 100)), 6) })}
                                            className="w-full px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            title={t('appScreen.paddingRight') + ' %'}
                                          />
                                        </div>
                                        <div>
                                          <label className="sr-only">{t('appScreen.paddingBottom')}</label>
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.1}
                                            placeholder={t('appScreen.paddingBottom')}
                                            value={getTextPaddingPercents(item).bottom}
                                            onChange={e => updateLayoutItem(screen.id, item.id, { textPaddingBottomPercent: roundRatio(Math.max(0, Math.min(1, (Number(e.target.value) || 0) / 100)), 6) })}
                                            className="w-full px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            title={t('appScreen.paddingBottom') + ' %'}
                                          />
                                        </div>
                                        <div>
                                          <label className="sr-only">{t('appScreen.paddingLeft')}</label>
                                          <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.1}
                                            placeholder={t('appScreen.paddingLeft')}
                                            value={getTextPaddingPercents(item).left}
                                            onChange={e => updateLayoutItem(screen.id, item.id, { textPaddingLeftPercent: roundRatio(Math.max(0, Math.min(1, (Number(e.target.value) || 0) / 100)), 6) })}
                                            className="w-full px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            title={t('appScreen.paddingLeft') + ' %'}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.backgroundRadiusPercent')}</label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={50}
                                        step={1}
                                        value={getBackgroundRadiusPercent(item)}
                                        onChange={e =>
                                          updateLayoutItem(screen.id, item.id, {
                                            backgroundRadiusPercent: roundRatio(
                                              Math.max(0, Math.min(50, Number(e.target.value) || 0)) / 100,
                                              6
                                            ),
                                          })
                                        }
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        title={t('appScreen.backgroundRadiusPercentTitle')}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.font')}</label>
                                      <select
                                        value={item.font}
                                        onChange={e => updateLayoutItem(screen.id, item.id, { font: e.target.value })}
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                      >
                                        {FONT_OPTIONS.map(f => (
                                          <option key={f.value} value={f.value}>{f.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <label className="flex items-start gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isTextFullWidth(item)}
                                        onChange={e =>
                                          updateLayoutItem(screen.id, item.id, {
                                            widthPercent: e.target.checked ? 100 : TEXT_WIDTH_CONTENT_FIT,
                                          })
                                        }
                                        className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                                      />
                                      <span className="min-w-0">
                                        <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">{t('appScreen.textFullWidth')}</span>
                                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{t('appScreen.textFullWidthHint')}</span>
                                      </span>
                                    </label>
                                    {!isTextFullWidth(item) && (
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.minWidthPercent')}</label>
                                        <input
                                          type="number"
                                          min={0}
                                          max={100}
                                          step={1}
                                          value={getMinWidthPercent(item)}
                                          onChange={e =>
                                            updateLayoutItem(screen.id, item.id, {
                                              minWidthPercent: roundRatio(
                                                Math.max(0, Math.min(100, Number(e.target.value) || 0)) / 100,
                                                6
                                              ),
                                            })
                                          }
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          title={t('appScreen.minWidthPercentTitle')}
                                        />
                                      </div>
                                    )}
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.maxHeightPercent')}</label>
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={item.maxHeightPercent ?? 0}
                                        onChange={e =>
                                          updateLayoutItem(screen.id, item.id, {
                                            maxHeightPercent: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                                          })
                                        }
                                        className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        title={t('appScreen.maxHeightPercentTitle')}
                                      />
                                      <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{t('appScreen.maxHeightPercentHint')}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionTopPercent')}</label>
                                        <input
                                          type="number"
                                          value={item.positionTopPercent ?? 50}
                                          onChange={e => {
                                            const n = parsePositionPercentInput(e.target.value);
                                            if (n !== null) updateLayoutItem(screen.id, item.id, { positionTopPercent: n });
                                          }}
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionLeftPercent')}</label>
                                        <input
                                          type="number"
                                          value={item.positionLeftPercent ?? 50}
                                          onChange={e => {
                                            const n = parsePositionPercentInput(e.target.value);
                                            if (n !== null) updateLayoutItem(screen.id, item.id, { positionLeftPercent: n });
                                          }}
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {isExpanded && item.type === 'image' && (() => {
                                  const img = item;
                                  const imageUrl = img.imageUrl ?? '';
                                  const imageFit = img.imageFit ?? 'contain';
                                  const imageShape = img.imageShape ?? 'square';
                                  const frameThickness = img.frameThickness ?? 0;
                                  const frameColor = img.frameColor ?? '#000000';
                                  const flipHorizontal = img.flipHorizontal ?? false;
                                  const positionTop = img.positionTopPercent ?? 50;
                                  const positionLeft = img.positionLeftPercent ?? 50;
                                  const widthPercent = img.widthPercent ?? 40;
                                  const heightPercent = img.heightPercent ?? 30;
                                  return (
                                    <div className="p-3 space-y-3 min-h-0 overflow-y-auto flex-1 border-t border-gray-200 dark:border-gray-600">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('appScreen.presetImages')}</label>
                                        <div className="relative mb-2">
                                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                          <input
                                            type="text"
                                            value={presetImageSearch}
                                            onChange={e => setPresetImageSearch(e.target.value)}
                                            placeholder={t('appScreen.search')}
                                            className="w-full pl-8 pr-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          />
                                        </div>
                                        <div className="grid grid-cols-3 gap-1.5 max-h-32 overflow-y-auto">
                                          {filteredPresetImages.map(preset => (
                                            <button
                                              key={preset.id}
                                              type="button"
                                              onClick={() => updateLayoutItem(screen.id, item.id, { imageUrl: preset.url })}
                                              className={clsx(
                                                'aspect-square rounded border-2 overflow-hidden bg-gray-100 dark:bg-gray-700',
                                                imageUrl === preset.url
                                                  ? 'border-primary-500 ring-1 ring-primary-500'
                                                  : 'border-transparent hover:border-gray-400 dark:hover:border-gray-500'
                                              )}
                                            >
                                              <img src={preset.url} alt={preset.label} className="w-full h-full object-cover" />
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('appScreen.customImage')}</label>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setLayoutImageUploadTarget({ screenId: screen.id, itemId: item.id });
                                            layoutImageInputRef.current?.click();
                                          }}
                                          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium"
                                        >
                                          <Upload className="h-4 w-4" />
                                          {t('appScreen.uploadImage')}
                                        </button>
                                        <input
                                          ref={layoutImageInputRef}
                                          type="file"
                                          accept="image/jpeg,image/png,image/webp"
                                          className="hidden"
                                          disabled={imageUploading}
                                          onChange={handleLayoutImageUpload}
                                        />
                                      </div>
                                      {imageUrl ? (
                                        <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-video">
                                          <img
                                            src={resolveStorageUrl(imageUrl)}
                                            alt=""
                                            className={clsx('w-full h-full object-contain', flipHorizontal && '-scale-x-100')}
                                          />
                                        </div>
                                      ) : null}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.imageFit')}</label>
                                        <select
                                          value={imageFit}
                                          onChange={e => updateLayoutItem(screen.id, item.id, { imageFit: e.target.value as 'cover' | 'contain' | 'fill' })}
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        >
                                          {IMAGE_FIT_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.imageShape')}</label>
                                        <select
                                          value={imageShape}
                                          onChange={e =>
                                            updateLayoutItem(screen.id, item.id, {
                                              imageShape: e.target.value as Extract<LayoutItem, { type: 'image' }>['imageShape'],
                                            })
                                          }
                                          className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                        >
                                          {IMAGE_SHAPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.flipHorizontal')}</label>
                                        <button
                                          type="button"
                                          onClick={() => updateLayoutItem(screen.id, item.id, { flipHorizontal: !flipHorizontal })}
                                          className={clsx(
                                            'w-full flex items-center justify-center gap-2 py-1.5 rounded border text-xs font-medium transition-colors',
                                            flipHorizontal
                                              ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-500'
                                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                          )}
                                          aria-pressed={flipHorizontal}
                                        >
                                          <FlipHorizontal className="h-3.5 w-3.5" />
                                          {flipHorizontal ? t('appScreen.flipHorizontalOn') : t('appScreen.flipHorizontalOff')}
                                        </button>
                                      </div>
                                      <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-2.5 bg-gray-50/50 dark:bg-gray-800/50 space-y-2">
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('appScreen.frame')}</label>
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1">
                                            <label className="sr-only">{t('appScreen.frameThickness')}</label>
                                            <input
                                              type="number"
                                              min={0}
                                              max={20}
                                              value={frameThickness}
                                              onChange={e =>
                                                updateLayoutItem(screen.id, item.id, {
                                                  frameThickness: Math.max(0, Math.min(20, Number(e.target.value) || 0)),
                                                })
                                              }
                                              className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                              placeholder="0"
                                            />
                                          </div>
                                          <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
                                        </div>
                                        {frameThickness > 0 && (
                                          <ColorSwatchPicker
                                            label={t('appScreen.frameColor')}
                                            value={frameColor}
                                            onChange={v => updateLayoutItem(screen.id, item.id, { frameColor: v })}
                                          />
                                        )}
                                      </div>
                                      <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-2.5 bg-gray-50/50 dark:bg-gray-800/50 space-y-2">
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('appScreen.sizePercentOfScreen')}</label>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">{t('appScreen.widthPercent')}</label>
                                            <input
                                              type="number"
                                              min={5}
                                              max={100}
                                              value={widthPercent}
                                              onChange={e => updateLayoutItem(screen.id, item.id, { widthPercent: Math.max(5, Math.min(100, Number(e.target.value) || 0)) })}
                                              className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">{t('appScreen.heightPercent')}</label>
                                            <input
                                              type="number"
                                              min={5}
                                              max={100}
                                              value={heightPercent}
                                              onChange={e => updateLayoutItem(screen.id, item.id, { heightPercent: Math.max(5, Math.min(100, Number(e.target.value) || 0)) })}
                                              className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionTopPercent')}</label>
                                          <input
                                            type="number"
                                            value={positionTop}
                                            onChange={e => {
                                              const n = parsePositionPercentInput(e.target.value);
                                              if (n !== null) updateLayoutItem(screen.id, item.id, { positionTopPercent: n });
                                            }}
                                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionLeftPercent')}</label>
                                          <input
                                            type="number"
                                            value={positionLeft}
                                            onChange={e => {
                                              const n = parsePositionPercentInput(e.target.value);
                                              if (n !== null) updateLayoutItem(screen.id, item.id, { positionLeftPercent: n });
                                            }}
                                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {isExpanded && item.type === 'device' && (() => {
                                  const dev = item;
                                  const positionTop = dev.positionTopPercent ?? 50;
                                  const positionLeft = dev.positionLeftPercent ?? 50;
                                  const rotationDeg = dev.rotationDegrees ?? 0;
                                    return (
                                    <div className="p-3 space-y-3 min-h-0 overflow-y-auto flex-1 border-t border-gray-200 dark:border-gray-600">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionTopPercent')}</label>
                                          <input
                                            type="number"
                                            value={positionTop}
                                            onChange={e => {
                                              const n = parsePositionPercentInput(e.target.value);
                                              if (n !== null) updateLayoutItem(screen.id, item.id, { positionTopPercent: n });
                                            }}
                                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.positionLeftPercent')}</label>
                                          <input
                                            type="number"
                                            value={positionLeft}
                                            onChange={e => {
                                              const n = parsePositionPercentInput(e.target.value);
                                              if (n !== null) updateLayoutItem(screen.id, item.id, { positionLeftPercent: n });
                                            }}
                                            className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                          {t('appScreen.rotationDegrees')} <span className="font-normal text-gray-500 dark:text-gray-400">{rotationDeg}°</span>
                                        </label>
                                        <div className="relative pt-1">
                                          {/* Koyu çubuk (track) */}
                                          <div className="absolute left-0 right-0 top-1 h-2 rounded-full bg-slate-600 dark:bg-slate-500" aria-hidden />
                                          {/* Sürgü: şeffaf track, beyaz thumb */}
                                          <input
                                            type="range"
                                            min={-180}
                                            max={180}
                                            step={1}
                                            value={rotationDeg}
                                            onChange={e => updateLayoutItem(screen.id, item.id, { rotationDegrees: Number(e.target.value) })}
                                            className="rotation-slider relative z-10 w-full h-4 appearance-none bg-transparent cursor-pointer"
                                          />
                                          {/* Preset açılar: eşit aralıklı, rahat tıklanabilir */}
                                          <div className="flex justify-between gap-1 mt-2 px-0.5">
                                            {[-45, -30, -15, 0, 15, 30, 45].map(angle => (
                                              <button
                                                key={angle}
                                                type="button"
                                                onClick={() => updateLayoutItem(screen.id, item.id, { rotationDegrees: angle })}
                                                className={clsx(
                                                  'min-w-[2.25rem] py-1.5 px-1 rounded-md text-xs font-medium tabular-nums transition-colors flex-shrink-0',
                                                  rotationDeg === angle
                                                    ? 'bg-primary-500 text-white dark:bg-primary-600 shadow-sm'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                )}
                                                title={`${angle}°`}
                                              >
                                                {angle === 0 ? '0' : angle > 0 ? `+${angle}` : angle}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{t('appScreen.rotationHint')}</p>
                                      </div>
                                      <ColorSwatchPicker
                                        label={t('appScreen.frameColor')}
                                        value={dev.frameColor ?? DEFAULT_DEVICE_FRAME_COLOR}
                                        onChange={frameColor => updateLayoutItem(screen.id, item.id, { frameColor })}
                                      />
                                      <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">{t('appScreen.deviceContent')}</label>
                                        <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-100 dark:bg-gray-700">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateLayoutItem(screen.id, item.id, {
                                                background: { type: 'color', color: (dev.background?.type === 'color' && dev.background.color) || '#ffffff' },
                                              })
                                            }
                                            className={clsx(
                                              'flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-colors',
                                              dev.background?.type !== 'image'
                                                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                            )}
                                          >
                                            <span className="w-4 h-4 rounded border border-gray-300 dark:border-gray-500" style={{ backgroundColor: dev.background?.type === 'color' ? dev.background.color : '#ffffff' }} />
                                            {t('appScreen.color')}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => { setDeviceInnerBackgroundTarget({ screenId: screen.id, itemId: item.id }); deviceInnerBackgroundInputRef.current?.click(); }}
                                            className={clsx(
                                              'flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-colors',
                                              dev.background?.type === 'image'
                                                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow'
                                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                            )}
                                          >
                                            <Image className="h-4 w-4" />
                                            {t('appScreen.uploadImage')}
                                          </button>
                                        </div>
                                        <input
                                          ref={deviceInnerBackgroundInputRef}
                                          type="file"
                                          accept="image/jpeg,image/png,image/webp"
                                          className="hidden"
                                          disabled={imageUploading}
                                          onChange={handleDeviceInnerImageUpload}
                                        />
                                        {dev.background?.type === 'color' && (
                                          <div className="mt-2">
                                            <ColorSwatchPicker
                                              label={t('appScreen.contentColor')}
                                              value={dev.background.color}
                                              onChange={color => updateLayoutItem(screen.id, item.id, { background: { type: 'color', color } })}
                                            />
                                          </div>
                                        )}
                                        {dev.background?.type === 'image' && (
                                          <div className="mt-2 space-y-2">
                                            <div className="relative rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-video">
                                              <img src={resolveStorageUrl(dev.background.imageUrl)} alt="" className="w-full h-full object-contain" />
                                              <button
                                                type="button"
                                                onClick={() => updateLayoutItem(screen.id, item.id, { background: undefined })}
                                                className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white hover:bg-black/70"
                                                title={t('appScreen.removeImage')}
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.imageFit')}</label>
                                              <select
                                                value={dev.background.imageFit}
                                                onChange={e =>
                                                  updateLayoutItem(screen.id, item.id, {
                                                    background: dev.background?.type === 'image'
                                                      ? { ...dev.background, imageFit: e.target.value as 'cover' | 'contain' | 'fill' }
                                                      : dev.background,
                                                  })
                                                }
                                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                              >
                                                {IMAGE_FIT_OPTIONS.map(opt => (
                                                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">{t('appScreen.position')}</label>
                                              <select
                                                value={dev.background.imagePosition}
                                                onChange={e =>
                                                  updateLayoutItem(screen.id, item.id, {
                                                    background: dev.background?.type === 'image'
                                                      ? { ...dev.background, imagePosition: e.target.value }
                                                      : dev.background,
                                                  })
                                                }
                                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                                              >
                                                {IMAGE_POSITION_OPTIONS.map(pos => (
                                                  <option key={pos} value={pos}>{pos}</option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </li>
                            );
                            })}
                          </ul>
                        )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setBackgroundOpen(prev => !prev)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-medium text-gray-900 dark:text-white"
                        >
                          <span>{t('appScreen.background')}</span>
                          {backgroundOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                        </button>
                        {backgroundOpen && (
                        <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-100 dark:bg-gray-700">
                          <button
                            type="button"
                            onClick={() =>
                              updateScreenBackground(screen.id, {
                                type: 'color',
                                color: (screen.background?.type === 'color' && screen.background.color) || DEFAULT_BG_COLOR,
                              })
                            }
                            className={clsx(
                              'flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-colors',
                              screen.background?.type !== 'image'
                                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            )}
                          >
                            <span className="w-4 h-4 rounded border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700" style={screen.background?.type === 'color' ? { backgroundColor: screen.background.color } : {}} />
                            {t('appScreen.color')}
                          </button>
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className={clsx(
                              'flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-colors',
                              screen.background?.type === 'image'
                                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                            )}
                          >
                            <Image className="h-4 w-4" />
                            {t('appScreen.image')}
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={imageUploading}
                            onChange={e => handleImageUpload(e, screen.id)}
                          />
                        </div>

                        {screen.background?.type === 'color' && (
                          <div className="mt-3">
                            <ColorSwatchPicker
                              label={t('appScreen.backgroundColor')}
                              value={screen.background.color}
                              onChange={color =>
                                updateScreenBackground(screen.id, { type: 'color', color })
                              }
                            />
                          </div>
                        )}

                        {screen.background?.type === 'image' && (
                          <div className="mt-3 space-y-3">
                            <div className="relative rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-video">
                              <img
                                src={resolveStorageUrl(screen.background.imageUrl)}
                                alt={t('panel.pageHostingPreviewLink')}
                                className="w-full h-full object-contain"
                              />
                              <button
                                type="button"
                                onClick={() => updateScreenBackground(screen.id, undefined)}
                                className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white hover:bg-black/70"
                                title={t('appScreen.removeImage')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('appScreen.imageFit')}
                              </label>
                              <select
                                value={screen.background.imageFit}
                                onChange={e =>
                                  screen.background?.type === 'image' &&
                                  updateScreenBackground(screen.id, {
                                    ...screen.background,
                                    imageFit: e.target.value as 'cover' | 'contain' | 'fill',
                                  })
                                }
                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                              >
                                {IMAGE_FIT_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {t(opt.labelKey)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('appScreen.position')}
                              </label>
                              <select
                                value={screen.background.imagePosition}
                                onChange={e =>
                                  screen.background?.type === 'image' &&
                                  updateScreenBackground(screen.id, {
                                    ...screen.background,
                                    imagePosition: e.target.value,
                                  })
                                }
                                className="w-full px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                              >
                                {IMAGE_POSITION_OPTIONS.map(pos => (
                                  <option key={pos} value={pos}>
                                    {pos}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {!screen.background && (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            {t('appScreen.chooseColorOrImage')}
                          </p>
                        )}
                        </div>
                        )}
                      </div>
                      </>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          <div
            data-tour="screens-add"
            className="flex-shrink-0 flex flex-col gap-3"
            style={{
              aspectRatio: `${currentScreenSize.width} / ${currentScreenSize.height}`,
              minHeight: 'min(82vh, 800px)',
              maxHeight: '98vh',
            }}
          >
            <button
              type="button"
              onClick={() => addScreen('image')}
              className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors p-4"
            >
              <Image className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 text-center px-2">{t('appScreen.addImageScreen')}</span>
            </button>
            <button
              type="button"
              onClick={() => addScreen('video')}
              className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors p-4"
            >
              <Video className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 text-center px-2">{t('appScreen.addVideoScreen')}</span>
            </button>
          </div>
          <input
            ref={screenVideoInputRef}
            type="file"
            accept="video/mp4,video/quicktime,.mp4,.mov,.m4v"
            className="hidden"
            disabled={imageUploading}
            onChange={handleScreenVideoUpload}
          />
        </div>
      </div>

      {screenOrderModalOpen && (
        <ModalLayout
          open={true}
          onClose={() => {
            setScreenOrderModalOpen(false);
            setScreenOrderDragIndex(null);
          }}
          title={t('appScreen.reorderScreensModalTitle')}
          icon={<ListOrdered className="h-5 w-5 text-primary-500" />}
          description={t('appScreen.reorderScreensDescription')}
          primaryLabel={t('appScreen.applyOrder')}
          onPrimary={applyScreenOrder}
          maxWidth="md"
        >
          <ol className="space-y-2">
            {screenOrderDraft.map((screen, index) => {
              const firstText = screen.layoutItems?.find(
                (item): item is Extract<LayoutItem, { type: 'text' }> => item.type === 'text'
              );
              const screenLabel = isVideoScreen(screen)
                ? t('appScreen.screenTypeVideo')
                : firstText
                  ? stripHtmlForMeasure(getTextForLocale(firstText, editingLocale))
                  : t('appScreen.screenNumber', { number: index + 1 });
              return (
                <li
                  key={screen.id}
                  draggable
                  onDragStart={event => {
                    setScreenOrderDragIndex(index);
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', screen.id);
                  }}
                  onDragOver={event => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (screenOrderDragIndex !== null && screenOrderDragIndex !== index) {
                      moveScreenOrderDraft(screenOrderDragIndex, index);
                      setScreenOrderDragIndex(index);
                    }
                  }}
                  onDragEnd={() => setScreenOrderDragIndex(null)}
                  className={clsx(
                    'flex items-center gap-3 rounded-xl border p-3 transition-colors',
                    screenOrderDragIndex === index
                      ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20 opacity-70'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
                    !isScreenVisible(screen) && 'opacity-60'
                  )}
                >
                  <GripVertical
                    className="h-5 w-5 flex-shrink-0 cursor-grab text-gray-400 active:cursor-grabbing"
                    aria-hidden="true"
                  />
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                    {screenLabel}
                  </span>
                  {!isScreenVisible(screen) && (
                    <span className="flex-shrink-0 text-amber-600 dark:text-amber-400" title={t('appScreen.hidden')}>
                      <EyeOff className="h-4 w-4" />
                    </span>
                  )}
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveScreenOrderDraft(index, index - 1)}
                      disabled={index === 0}
                      aria-label={t('appScreen.moveScreenUp', { number: index + 1 })}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveScreenOrderDraft(index, index + 1)}
                      disabled={index === screenOrderDraft.length - 1}
                      aria-label={t('appScreen.moveScreenDown', { number: index + 1 })}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        </ModalLayout>
      )}

      <ModalLayout
        open={aiRedesignModalOpen}
        onClose={() => { setAiRedesignModalOpen(false); setAiRedesignTargetId(null); }}
        title={t('appScreen.aiRedesignTitle')}
        icon={<Sparkles className="h-5 w-5 text-primary-500" />}
        description={<>{t('appScreen.aiRedesignDescription')}<br /><span className="text-xs">{t('appScreen.aiTemplatePrimaryLocaleNote', { locale: `${getLocaleFlag(editingLocale)} ${APP_STORE_LOCALIZATIONS.find(l => l.value === editingLocale)?.label ?? editingLocale}` })}</span></>}
        primaryLabel={aiRedesignLoading ? t('appScreen.aiRedesignGenerating') : t('appScreen.aiRedesignGenerate')}
        onPrimary={handleAiRedesignConfirm}
        primaryLoading={aiRedesignLoading}
        closeDisabled={aiRedesignLoading}
        maxWidth="md"
      >
        <div>
          <label htmlFor="ai-redesign-optional" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('appScreen.aiRedesignOptionalLabel')}
          </label>
          <textarea
            id="ai-redesign-optional"
            value={aiRedesignOptionalPrompt}
            onChange={e => setAiRedesignOptionalPrompt(e.target.value)}
            rows={4}
            disabled={aiRedesignLoading}
            placeholder={t('appScreen.aiRedesignOptionalPlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('appScreen.aiRedesignOptionalHint')}</p>
        </div>
      </ModalLayout>

      <ModalLayout
        open={aiTemplateModalOpen}
        onClose={() => setAiTemplateModalOpen(false)}
        title={t('appScreen.aiTemplateTitle')}
        icon={<Sparkles className="h-5 w-5 text-primary-500" />}
        description={<>{t('appScreen.aiTemplateModalDescription')}<br /><span className="text-xs">{t('appScreen.aiTemplatePrimaryLocaleNote', { locale: `${getLocaleFlag(editingLocale)} ${APP_STORE_LOCALIZATIONS.find(l => l.value === editingLocale)?.label ?? editingLocale}` })}</span></>}
        primaryLabel={aiTemplateLoading ? t('appScreen.aiTemplateGenerating') : t('appScreen.aiTemplateGenerate')}
        onPrimary={handleAiTemplateGenerate}
        primaryLoading={aiTemplateLoading}
        closeDisabled={aiTemplateLoading}
        maxWidth="lg"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="ai-template-context" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('appScreen.aiTemplateContextLabel')}
            </label>
            <textarea
              id="ai-template-context"
              value={aiTemplateContext}
              onChange={e => setAiTemplateContext(e.target.value)}
              rows={8}
              disabled={aiTemplateLoading}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
              placeholder={t('appScreen.aiTemplateContextHint')}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('appScreen.aiTemplateContextHint')}</p>
          </div>
          <div>
            <label htmlFor="ai-template-user" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('appScreen.aiTemplateUserPromptLabel')}
            </label>
            <textarea
              id="ai-template-user"
              value={aiTemplateUserPrompt}
              onChange={e => setAiTemplateUserPrompt(e.target.value)}
              rows={3}
              disabled={aiTemplateLoading}
              placeholder={t('appScreen.aiTemplateUserPromptPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <div>
            <label htmlFor="ai-template-refs" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('appScreen.aiTemplateReferenceUrlsLabel')}
            </label>
            <textarea
              id="ai-template-refs"
              value={aiTemplateReferenceUrls}
              onChange={e => setAiTemplateReferenceUrls(e.target.value)}
              rows={3}
              disabled={aiTemplateLoading}
              placeholder={t('appScreen.aiTemplateReferenceUrlsPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('appScreen.aiTemplateReferenceUrlsHint')}</p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={aiTemplateReplace}
              onChange={e => setAiTemplateReplace(e.target.checked)}
              disabled={aiTemplateLoading}
              className="mt-1 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-900 dark:text-white">{t('appScreen.aiTemplateReplaceLabel')}</span>
          </label>
        </div>
      </ModalLayout>

      {uploadModalOpen && appId && (
        <ModalLayout
          open={true}
          onClose={() => setUploadModalOpen(false)}
          closeDisabled={uploading}
          title={t('appScreen.uploadModalTitle')}
          icon={<Upload className="h-5 w-5 text-primary-500" />}
          description={t('appScreen.uploadModalDescription')}
          primaryLabel={uploading ? t('appScreen.uploadStarting') : t('appScreen.upload')}
          onPrimary={handleUploadConfirm}
          primaryDisabled={uploadPlatforms.length === 0 || uploading || uploadNeedsAppStoreVersion}
          primaryLoading={uploading}
          maxWidth="md"
        >
          <>
            <PlatformSelectCards
              options={[
                {
                  value: 'app_store',
                  label: t('panel.appStore'),
                  disabled: !appStoreReady,
                  helperText: !appStoreReady && (
                    <>
                      {t('appScreen.toEnable')}{' '}
                      <Link
                        to={panelPath(`/applications/${appId}`)}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                        onClick={() => setUploadModalOpen(false)}
                      >
                        {t('appScreen.fromAppSettings')}
                      </Link>
                      {' '}{t('appScreen.enterAppStoreCredentials')}
                    </>
                  ),
                },
                {
                  value: 'play_store',
                  label: t('panel.playStore'),
                  disabled: !playStoreReady,
                  helperText: !playStoreReady && (
                    <>
                      {t('appScreen.toEnable')}{' '}
                      <Link
                        to={panelPath(`/applications/${appId}`)}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                        onClick={() => setUploadModalOpen(false)}
                      >
                        {t('appScreen.fromAppSettings')}
                      </Link>
                      {' '}{t('appScreen.enterPlayStoreCredentials')}
                    </>
                  ),
                },
              ]}
              selected={uploadPlatforms}
              onToggle={toggleUploadPlatform as (value: string) => void}
            />
            {uploadPlatforms.includes('app_store') && (
              <div className="mt-3 space-y-3">
                <div>
                  <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {t('appScreen.uploadAppStoreTargetLabel')}
                  </p>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="upload-app-store-target"
                        checked={!uploadAsABTest}
                        onChange={() => setUploadAsABTest(false)}
                        disabled={uploading}
                        className="mt-0.5"
                      />
                      <span>
                        {t('appScreen.uploadTargetListing')}
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('appScreen.uploadTargetListingHint')}
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="radio"
                        name="upload-app-store-target"
                        checked={uploadAsABTest}
                        onChange={() => setUploadAsABTest(true)}
                        disabled={uploading}
                        className="mt-0.5"
                      />
                      <span>
                        {t('appScreen.uploadTargetABTest')}
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('appScreen.uploadTargetABTestHint', {
                            name: `app-screen-${currentScreenSet?.name?.trim() || 'default'}`,
                          })}
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
                {!uploadAsABTest && (
                  <div>
                    <label htmlFor="upload-app-store-version" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      {t('appScreen.uploadAppStoreVersionLabel')}
                    </label>
                    <select
                      id="upload-app-store-version"
                      value={uploadAppStoreVersion}
                      onChange={e => setUploadAppStoreVersion(e.target.value)}
                      disabled={uploading || versionsLoading || versions.length === 0}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {versionsLoading ? (
                        <option value="">{t('appScreen.uploadAppStoreVersionLoading')}</option>
                      ) : versions.length === 0 ? (
                        <option value="">{t('appScreen.uploadAppStoreVersionEmpty')}</option>
                      ) : (
                        versions.map(v => (
                          <option key={v.id} value={v.version}>
                            {v.version}
                          </option>
                        ))
                      )}
                    </select>
                    {versions.length === 0 && !versionsLoading && (
                      <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                        {t('appScreen.uploadAppStoreVersionNoVersions')}{' '}
                        <Link
                          to={panelPath(`/applications/${appId}`)}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                          onClick={() => setUploadModalOpen(false)}
                        >
                          {t('appScreen.fromAppSettings')}
                        </Link>
                        .
                      </p>
                    )}
                  </div>
                )}
                {screens.some(isVideoScreen) && (
                  <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uploadAppPreviewPrimaryOnly}
                      onChange={e => setUploadAppPreviewPrimaryOnly(e.target.checked)}
                      disabled={uploading}
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
                    />
                    <span>
                      {t('appScreen.uploadAppPreviewPrimaryOnly')}
                      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('appScreen.uploadAppPreviewPrimaryOnlyHint')}
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}
          </>
        </ModalLayout>
      )}

      {aiTranslationModalOpen && (
        <ModalLayout
          open={true}
          onClose={() => {
            if (!targetLocalesSaving) setAiTranslationModalOpen(false);
          }}
          closeDisabled={targetLocalesSaving}
          title={t('appScreen.aiTranslationModalTitle')}
          icon={<Sparkles className="h-5 w-5 text-primary-500" />}
          description={t('appScreen.aiTranslationModalDescription')}
          primaryLabel={targetLocalesSaving ? t('panel.saving') : t('common.save')}
          onPrimary={() => void saveTargetLocales()}
          primaryDisabled={targetLocalesSaving}
          primaryLoading={targetLocalesSaving}
          cancelLabel={t('common.cancel')}
          maxWidth="md"
        >
          <SourceTargetLocalePicker
            sourceLocale={sourceLocaleDraft}
            onSourceChange={setSourceLocaleDraft}
            targetLocales={targetLocalesDraft}
            onTargetLocalesChange={setTargetLocalesDraft}
            sourceSelectId="ai-translation-source-locale"
            disabled={targetLocalesSaving}
          />
        </ModalLayout>
      )}
      {newVersionModalOpen && (
        <ModalLayout
          open={true}
          onClose={() => {
            if (!newVersionSubmitting) setNewVersionModalOpen(false);
          }}
          title={t('appScreen.newVersionTitle')}
          icon={<Plus className="h-5 w-5 text-primary-500" />}
          description={t('appScreen.newVersionDescription')}
          primaryLabel={t('appScreen.newVersionCreate')}
          onPrimary={() => void handleCreateScreensVersion()}
          primaryDisabled={newVersionSubmitting || !newVersionNumber.trim()}
          primaryLoading={newVersionSubmitting}
          closeDisabled={newVersionSubmitting}
          cancelLabel={t('common.cancel')}
          maxWidth="md"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="app-screens-new-version" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('appScreen.newVersionNumber')}
              </label>
              <input
                id="app-screens-new-version"
                type="text"
                value={newVersionNumber}
                onChange={e => setNewVersionNumber(e.target.value)}
                disabled={newVersionSubmitting}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="v1.0"
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={newVersionCopyScreens}
                onChange={e => setNewVersionCopyScreens(e.target.checked)}
                disabled={newVersionSubmitting || !screensId}
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                {t('appScreen.newVersionCopyScreens')}
                {currentScreenSet ? ` (${currentScreenSet.name})` : ''}
              </span>
            </label>
          </div>
        </ModalLayout>
      )}
      <ConfirmDialog
        open={Boolean(deleteScreensConfirm)}
        onClose={() => {
          if (!deletingScreensId) setDeleteScreensConfirm(null);
        }}
        title={t('appScreen.deleteVersionTitle')}
        message={t('appScreen.deleteVersionConfirm', { name: deleteScreensConfirm?.name ?? '' })}
        confirmLabel={deletingScreensId ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDeleteScreensSet()}
        confirmLoading={Boolean(deletingScreensId)}
        closeDisabled={Boolean(deletingScreensId)}
        variant="danger"
      />
      <ConfirmDialog
        open={Boolean(pendingVersionSwitchId)}
        onClose={() => setPendingVersionSwitchId(null)}
        title={t('appScreen.unsavedSwitchTitle')}
        message={t('appScreen.unsavedSwitchVersion')}
        confirmLabel={t('appScreen.switchAnyway')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmVersionSwitch}
        variant="warning"
      />
    </>
      )}
    </div>
      )}
      {appId && screensId && (
        <AppScreensAIChatPanel
          open={aiChatOpen}
          onClose={() => setAiChatOpen(false)}
          appId={appId}
          screensId={screensId}
          screens={screens}
          primaryLocale={localization}
          targetLocales={targetLocales}
        />
      )}
    </AppPageLayout>
  );
};

export default AppScreensPage;
